/**
 * ExcelApp Component
 * Main UI for the Excel add-in - chat interface for AI-powered Excel modifications
 */

import * as React from "react";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Spinner, Button } from "@finster/ui";
import { SimpleMarkdown } from "../shared/components/SimpleMarkdown";
import { OutputFeedback, useOutputFeedbackHydration } from "../shared/components/OutputFeedback";
import { SupportFeedbackDialog } from "../shared/components/SupportFeedbackDialog";
import { Login } from "../shared/components/Login";
import { closeCitationDialog, openCitationDialog, type Citation } from "../shared/citations";
import { useAuth } from "../shared/hooks/useAuth";
import { useStickToBottom } from "../shared/hooks/useStickToBottom";
import { JumpToLatestButton } from "../shared/components/JumpToLatestButton";
import {
  hasCompletedOnboarding,
  markOnboardingCompleted,
} from "../shared/services/onboardingService";
import {
  clearExcelChatTabs,
  loadExcelChatTabs,
  saveExcelChatTabs,
  type ExcelChatScope,
} from "./services/excelChatHistoryService";
import { shouldAttemptFactSetAutoLogin } from "../shared/auth/factsetAutoLogin";
import { createChatSessionId } from "../shared/utils/sessionId";
import { trackQueryRun, trackQuerySubmitted } from "../shared/analytics/product";
import {
  ExcelChatMessage,
  ExcelChatMessageAction,
  ExcelScopedRetryPayload,
  ExcelStepStatus,
  ExcelTab,
  ExcelTabsState,
  ExecutorRunResult,
  ExecutorUnifiedRunResult,
  OpenWorkbookInfo,
  PlanStep,
  WorkbookContent,
  WorkbookModification,
  WorkbookNamedObject,
  generateId,
} from "./types";
import {
  applyWorkbookModifications,
  gatherProbeContext,
  getActiveSheetName,
  readRangeContent,
  workbookContentFromProbe,
  WorkbookUnavailableError,
  type ApplyWorkbookModificationsOptions,
} from "./excelService";
import { collectFdsBoundFormulaSheets } from "./formulaWriteDetection";
import {
  shouldUseExcelIntentBridge,
  getActiveExcelContext,
  activateWorksheet,
  getTemplatesList,
  getUserAvailableTemplates,
  getWorkbookNamedObjects,
  getOpenWorkbooks,
  applicationCalculate,
} from "./excelHostOps";
import {
  excelColumnIndex,
  excelColumnName,
} from "./columnSizingPolicy";
import {
  harmonizedRefreshAllWorksheetsViaIntents,
  harmonizedRefreshWorksheetsSequentiallyViaIntents,
} from "./services/factset/spreadsheetIntents";
import { foregroundCoordinator } from "./foregroundCoordinator";
import { supportsRangeMutationWorkbookName } from "./excelTargetingConfig";
import {
  prewarmTemplateEmbeddings,
  rankTemplateCandidates,
  TemplateCatalogRow,
} from "./excelAIService";
import { prewarmWorksheetTopicEmbeddings } from "./worksheetColumnIntent";
import { type ToolCallContext } from "./toolRegistry";
import { timePhase, recordPhase } from "./aiDiag";
import {
  waitForHostReady,
  waitForHostReadyPatient,
  PATIENT_HOST_READY_SCHEDULE,
  hostReadyRetryCopy,
  type HostReadyResult,
  type StuckHostReadyReason,
} from "./services/hostReady";
import { openTemplateInExcel } from "./services/openTemplate";
import { isTransientFetchError, isTimeoutFetchError } from "../shared/utils/http";
import { isAbortError } from "../shared/utils/abortError";
import {
  getAvailableFinsterQueryDataSources,
  type FinsterQueryDataSource,
} from "../shared/api/finster/constants";
import { fetchSavedContext } from "../shared/api/finster/savedContext";
import {
  applyExcelResearchSelectionToActiveTab,
  captureExcelSubmittedResearchSelection,
  resolveExcelLiveResearchSelectionMode,
  resolveExcelSessionDataSources,
  selectExcelDataSources,
  selectExcelUserFiles,
  shouldDeferExcelRunAfterWorkbookSwitch,
  type ExcelSubmittedResearchSelection,
} from "./researchSelection";
import type { ExecutorRunEvent } from "./executorRunTypes";
import { runNativeReactExecutor } from "./executorNativeReactRunner";
import {
  applyUserSkillRunContextToToolCallContext,
  buildUserSkillPreludeMessages,
  clearSkillOperations,
  createSkillContinuationId,
  createSkillResolveSingleFlight,
  ExcelSkillsBlockedError,
  getSkillOperationsGeneration,
  markSkillContinuationRetained,
  openSkillOperation,
  readSkillOperation,
  rebindSkillOperationWorkbook,
  releaseSkillContinuation,
  retargetSkillOperationsWorkbook,
  sameSkillSelection,
  sealSkillOperationPolicy,
  settleExcelUserSkillTurn,
  validateExcelOperationClaim,
  type ExcelOperationOwner,
  withSkillSlashTokens,
  type UserSkillRunContext,
} from "./userSkills";
import {
  createSkillSubmissionId,
  snapshotSkillSubmissionOptions,
  type SkillSubmissionInput,
  type SkillTurnBootstrapResult,
} from "../shared/skills/runtime/skillTurnBootstrap";
import {
  messagesBeforeLastUser,
  replaceSkillRun,
} from "../shared/skills/runtime/skillDecision";
import { useMatchingSkillBanner } from "../shared/skills/hooks/useMatchingSkillBanner";
import type { SelectedSkillReference } from "../shared/skills";
import { skillLoadingStatus } from "../shared/skills/runtime/skillLoadingStatus";
import { buildSkillsManagementUrl } from "../shared/skills/managementUrl";
import { buildQuerySkillsAnalytics } from "../shared/analytics/product";
import {
  createExcelSubmissionHandoff,
  type ExcelSubmissionHandoff,
  type ExcelSubmissionOutcome,
} from "./submissionHandoff";
import { ExecutionPlan } from "./types";
import { ExcelOnboarding } from "./ExcelOnboarding";
import { ExcelConsolePanel } from "./components/ExcelConsolePanel";
import { ExcelEmptyState } from "./components/ExcelEmptyState";
import { ExcelHeader } from "./components/ExcelHeader";
import { ExcelQueryComposer, type ExcelQueryComposerHandle } from "./components/ExcelQueryComposer";
import { ExcelUserQuestionCard } from "./components/ExcelUserQuestionCard";
import type { UserQuestion, UserQuestionAnswer } from "./userQuestion";
import type { SuggestedSkillsSelectionState } from "../shared/components/GettingStartedSkills";
import { ExcelBrandDefaultsToggle } from "./components/ExcelBrandDefaultsToggle";
import { useExcelActiveBrand } from "./hooks/useExcelActiveBrand";
import type { ExcelBrandId } from "./branding/excelBrandDefaults";
import {
  ExcelCitationsView,
  ExcelRetrievedSources,
  resolveExcelInlineCitationSource,
} from "./components/ExcelRetrievedSources";
import { ExcelThoughtProcess } from "./components/ExcelThoughtProcess";
import { isDiagEnabled } from "./aiDiag";
import { snapshotChatHistoryForRun } from "./chatHistoryContext";
import { getClientBuildVersion } from "../shared/clientUpdate/buildVersion";
import { fetchCurrentVersion } from "../shared/clientUpdate/fetchVersion";
import {
  createClientUpdateController,
  type ClientUpdateController,
} from "../shared/clientUpdate/updateController";
import { subscribeToUpgradeRequired } from "../shared/clientUpdate/upgradeRequired";
import { isExcelClientUpdateBusy } from "./clientUpdateBusy";
import { getFactSetSpreadsheetNoteCapability } from "../shared/factset/hostContextService";
import {
  createExcelLangfuseRunContext,
  postExcelLangfuseObservation,
  postExcelLangfuseOutcome,
  startExcelLangfuseRun,
  summarizeWorkbookForLangfuse,
  toLangfuseRunSkills,
  type ExcelLangfuseRunContext,
} from "./langfuseTrace";
import {
  capExcelAgentTimeline,
  emptyExcelAgentTimeline,
  finalizeFailedExcelAgentTimeline,
  withActiveUserSkills,
  extractSourcesFromCitations,
  type ExcelAgentSource,
  mergeExcelAgentSources,
  reduceExcelAgentEvent,
  withoutDuplicateTerminalAssistantText,
} from "./agentTimeline";
import { createExcelExecutorReasoningNarrator } from "./executorReasoningNarrator";
import {
  classifyHardErrorsInFormulaValues,
  classifyHardErrorsInValues,
  summarizeHardErrors,
  type ExcelHardErrorCell,
} from "./excelHardErrors";
import { extractCitedValuesFromText, mergeCitedValuesByUuid } from "./citationNotes";
import { registerChartCreatorTemplateProbe } from "./diagnostics/chartCreatorTemplateProbe";

// Console-only Chart Creator template dump, used to author the layout
// fingerprints in `chartCreatorTemplateMap.ts`. Self-gates on the diagnostics
// flag and is never registered as an agent tool.
registerChartCreatorTemplateProbe();

const WORKBOOK_KEY_PROBE_TIMEOUT_MS = 1_500;
/**
 * Maximum time to await the host acknowledgement for a worksheet FactSet
 * refresh. Large models can legitimately refresh for several minutes. The
 * bridge cannot cancel work already dispatched to the host, so a short client
 * timeout creates a false failure while the refresh continues in Excel.
 */
const FACTSET_WORKSHEET_REFRESH_TIMEOUT_MS = 10 * 60_000;
const MAX_CONSOLE_LOGS = 500;
/**
 * Shown when a submission cannot be given a frozen operation record: the
 * account changed while it was resolving, or the registry is holding its
 * ceiling of still-reachable operations. Running anyway would produce exactly
 * the unbound run the registry exists to prevent, so the request is refused
 * before anything starts and the composer keeps what the user typed.
 */
const EXCEL_OPERATION_ADMISSION_REFUSAL =
  "This request could not be started. Send it again to continue.";
const EXCEL_WORKBOOK_EXTENSION_RE = /\.(xlsx|xlsm|xlsb|xls|xltx|xltm|xlam|xla|xlw)$/i;
const nodeEnv = process.env.NODE_ENV?.toLowerCase();
const isLocalEnv = !nodeEnv || nodeEnv === "development";

function getMessageCitedValues(message: ExcelChatMessage): Record<string, string> | undefined {
  const userFacingValues = extractCitedValuesFromText(message.content);
  const mergedValues = mergeCitedValuesByUuid(message.retrievedCitedValuesByUuid, userFacingValues);
  return Object.keys(mergedValues).length > 0 ? mergedValues : undefined;
}

/**
 * Polling cadence for the workbook-key resolution effect (see usage
 * further down). 1s keeps `GetActiveExcelContext` lightweight while
 * still making workbook switches feel responsive in the chat scope,
 * and still
 * leaving the `withTimeout`-wrapped probe (1.5s budget) enough room
 * to complete before the next tick — the `inFlight` guard in the
 * effect itself absorbs the rare slow probe.
 */
const WORKBOOK_KEY_POLL_MS = 1_000;

/**
 * How many consecutive degraded polls may keep holding the last verified
 * workbook identity for Save As classification.
 *
 * A save hides the inventory for a moment at most, while an Excel with
 * everything closed can report nothing for minutes. Without a bound, that
 * stale identity is still sitting there to misread the next file the user
 * opens as its rename — moving one workbook's chat into another.
 */
const MAX_WORKBOOK_INVENTORY_HOLD_POLLS = 5;

interface HandleQueryOptions {
  /**
   * Skill selection captured at submit. Native clarification replies omit it
   * so the run inherits the context resolved for the original prompt.
   */
  skills?: SkillSubmissionInput;
  /** Effective file scope captured by the composer in the submit event. */
  userFileIds?: readonly string[];
}

function workbookFileNameFromKey(key: string | null): string {
  if (!key) return "";
  const trimmed = key.trim();
  if (!trimmed) return "";
  const lastBackslash = trimmed.lastIndexOf("\\");
  const lastSlash = trimmed.lastIndexOf("/");
  const cut = Math.max(lastBackslash, lastSlash);
  return cut >= 0 ? trimmed.slice(cut + 1) : trimmed;
}

/**
 * Reduce a workbook key (which may be a full path like
 * `C:\Users\foo\Book1.xlsx` or `/Users/foo/Book1.xlsx`) to the bare
 * filename suitable for the header label. Path separators on both
 * Windows (`\`) and POSIX (`/`) are handled. Empty/null input yields
 * `""` so the caller can suppress the slot entirely.
 */
function workbookDisplayNameFromKey(key: string | null): string {
  const fileName = workbookFileNameFromKey(key);
  const withoutExtension = fileName.replace(EXCEL_WORKBOOK_EXTENSION_RE, "");
  return withoutExtension || fileName;
}

interface WorkbookIdentitySnapshot {
  activeIdentity: string;
  activeSaved?: boolean;
  openKeys: string[];
  resolvedKey: string;
}

interface WorkbookHistoryTransfer {
  sourceKey: string;
  targetKey: string;
}

function redirectedWorkbookHistoryKey(
  key: string,
  redirects: ReadonlyMap<string, string>,
  liveKey?: string | null
): string {
  // A reused unsaved identity such as Book1 is a different workbook. Following
  // the old Save As redirect would write its prompts into the saved file while
  // the visible pane stays on the empty live slot.
  if (
    liveKey &&
    normalizedWorkbookIdentity(key) === normalizedWorkbookIdentity(liveKey)
  ) {
    return key;
  }
  let resolved = key;
  const visited = new Set<string>();
  while (!visited.has(resolved)) {
    visited.add(resolved);
    const next = redirects.get(resolved);
    if (!next) break;
    resolved = next;
  }
  return resolved;
}

function dropLiveWorkbookHistoryRedirect(
  redirects: Map<string, string>,
  liveKey: string | null | undefined
): void {
  if (!liveKey) return;
  const liveIdentity = normalizedWorkbookIdentity(liveKey);
  if (!liveIdentity) return;
  redirects.forEach((_targetKey, sourceKey) => {
    if (normalizedWorkbookIdentity(sourceKey) === liveIdentity) {
      redirects.delete(sourceKey);
    }
  });
}

function normalizedWorkbookIdentity(key: string | null | undefined): string {
  return key?.trim().toLowerCase() ?? "";
}

function workbookIdentity(workbook: OpenWorkbookInfo): string {
  return normalizedWorkbookIdentity(workbook.fullName || workbook.name);
}

/**
 * A Save As redirect is only valid while the unsaved source identity is gone.
 * Excel reuses names such as Book1 for the next new workbook, so once that
 * identity is open again it is a different file and must not inherit the
 * previous destination's chat slot.
 */
function pruneWorkbookHistoryRedirects(
  redirects: Map<string, string>,
  openKeys: readonly string[]
): void {
  if (openKeys.length === 0) return;
  const openIdentities = new Set(openKeys);
  for (const [sourceKey, targetKey] of [...redirects.entries()]) {
    const sourceIdentity = normalizedWorkbookIdentity(sourceKey);
    if (!sourceIdentity || !openIdentities.has(sourceIdentity)) continue;
    if (sourceIdentity === normalizedWorkbookIdentity(targetKey)) continue;
    redirects.delete(sourceKey);
  }
}

/**
 * Detect the host identity replacement produced by Save / Save As.
 *
 * An ordinary workbook switch leaves the set of open workbook identities
 * unchanged, so the evidence for a rename is the active workbook's identity
 * being *replaced in place*: it left the inventory and the new active identity
 * arrived in the same observation.
 *
 * `saved` cannot carry that decision, only veto it. It is Excel's
 * unsaved-edits bit rather than a save signal, so it reads `true` throughout a
 * Save As of a workbook with no pending edits (a rename of an already-saved
 * file, a read-only conversation, or any Ctrl+S before the rename), and hosts
 * may omit it entirely. A host still reporting unsaved edits on the new
 * identity is the one answer that contradicts a completed save.
 *
 * Callers must additionally establish that the two observations are
 * consecutive — see `workbookObservationGapRef`.
 */
function isWorkbookSaveAs(
  previous: WorkbookIdentitySnapshot | null,
  nextActiveIdentity: string,
  nextActiveSaved: boolean | undefined,
  nextOpenKeys: string[]
): boolean {
  if (
    !previous ||
    !nextActiveIdentity ||
    previous.activeIdentity === nextActiveIdentity ||
    nextOpenKeys.length === 0
  ) {
    return false;
  }

  const previousOpenKeys = new Set(previous.openKeys);
  const nextOpenKeySet = new Set(nextOpenKeys);
  const removed = [...previousOpenKeys].filter((key) => !nextOpenKeySet.has(key));
  const added = [...nextOpenKeySet].filter((key) => !previousOpenKeys.has(key));
  if (
    removed.length !== 1 ||
    added.length !== 1 ||
    removed[0] !== previous.activeIdentity ||
    added[0] !== nextActiveIdentity
  ) {
    return false;
  }

  return nextActiveSaved !== false;
}

/**
 * Settle on one chat-history key per workbook, whichever spelling the host
 * happened to answer with.
 *
 * `GetOpenWorkbooks` and `GetActiveExcelContext` name the same workbook with a
 * full path on one probe and a bare name on the next, depending on which of them
 * resolved the active workbook and whether either timed out. A scope keyed on
 * the raw string would read those as two workbooks: the conversation the user is
 * watching empties into a fresh slot, and the run behind it keeps reporting into
 * the slot it started in. So the first spelling seen for a workbook wins for as
 * long as that workbook stays open.
 *
 * `aliases` is the caller's memory of that choice, keyed by display name, and is
 * both pruned and extended here. Workbooks that merely share a display name are
 * different files, so nothing is collapsed across them — telling those apart is
 * what `readWorkbookKey`'s preference for `fullName` is for.
 */
export function canonicalWorkbookKey(
  probedKey: string | null,
  openWorkbooks: OpenWorkbookInfo[],
  aliases: Map<string, string>
): string | null {
  const displayNameOf = (workbook: OpenWorkbookInfo): string =>
    workbookDisplayNameFromKey(workbook.fullName || workbook.name || null).toLowerCase();
  if (openWorkbooks.length > 0) {
    // A remembered spelling holds only while its workbook is open, so a closed
    // workbook's key cannot capture a different file that opens later under the
    // same name. An empty list means the probe could not read the host at all,
    // which is no evidence that anything closed.
    const openDisplayNames = new Set(openWorkbooks.map(displayNameOf));
    aliases.forEach((_spelling, displayName) => {
      if (!openDisplayNames.has(displayName)) aliases.delete(displayName);
    });
  }
  if (!probedKey?.trim()) return probedKey;
  const displayName = workbookDisplayNameFromKey(probedKey).toLowerCase();
  if (!displayName) return probedKey;
  if (openWorkbooks.filter((workbook) => displayNameOf(workbook) === displayName).length > 1) {
    aliases.delete(displayName);
    return probedKey;
  }
  const remembered = aliases.get(displayName);
  if (remembered) return remembered;
  aliases.set(displayName, probedKey);
  return probedKey;
}

/**
 * Claim a workbook's chat-history key for a run that just created it. The
 * workbook poll resolves the same workbook on its own, and without this it can
 * settle on a different spelling and strand the conversation the run relocated.
 */
export function rememberWorkbookKey(aliases: Map<string, string>, key: string): void {
  const displayName = workbookDisplayNameFromKey(key).toLowerCase();
  if (displayName) aliases.set(displayName, key.trim());
}

const withTimeout = <T,>(p: Promise<T>, timeoutMs = WORKBOOK_KEY_PROBE_TIMEOUT_MS): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("workbook identity probe timeout")), timeoutMs);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });

const TEMPLATE_IDENTIFIER_NAME = "Identifier";

/**
 * Inline-button actions for the Identifier-conflict prompt.
 *
 * The `reply` strings are plain-language clarification replies. They are
 * appended to chat history and passed to the unified agent alongside the
 * original request; there is no local keyword router for these choices.
 */
function buildTemplateIdentifierConflictActions(): ExcelChatMessageAction[] {
  return [
    {
      id: "new-workbook",
      label: "Open in new workbook",
      reply: "Open in a new workbook",
      variant: "default",
    },
    {
      id: "replace-identifier",
      label: `Replace ${TEMPLATE_IDENTIFIER_NAME}`,
      reply: `Replace the existing ${TEMPLATE_IDENTIFIER_NAME}`,
      variant: "outline",
    },
  ];
}

function namedObjectName(namedObject: WorkbookNamedObject): string | undefined {
  const name = namedObject.name ?? namedObject.Name;
  return typeof name === "string" ? name : undefined;
}

function namedObjectsIncludeIdentifier(namedObjects?: WorkbookNamedObject[]): boolean {
  return (namedObjects ?? []).some(
    (namedObject) =>
      namedObjectName(namedObject)?.toLowerCase() === TEMPLATE_IDENTIFIER_NAME.toLowerCase()
  );
}

/** First non-empty trimmed sheet name among the candidates, or `""`. */
function nonEmptySheetName(...candidates: (string | undefined)[]): string {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return "";
}

/**
 * Pin the live active sheet to the target a `WorkbookModification` is about
 * to write to, immediately before `applyWorkbookModifications`. Fixes the
 * "user clicked onto another sheet during the LLM call between steps /
 * verifier passes, and bridge cell / range writes redirected onto the live
 * active sheet" bug (see the warning at `excelService.ts:3140-3152` —
 * `setCellContents`, `fillRangeContents`, and most range actions ignore
 * `WorksheetModification.sheetName` and default to the live active sheet).
 *
 * Target resolution prefers the modifications' own first non-empty
 * `worksheets[].sheetName` (so the verifier writing to a different sheet
 * than the step's `targetSheet` still gets pinned correctly) and falls
 * back to the caller-supplied fallback (`step.targetSheet ||
 * sessionLockSheet` for step / step-verifier applies; `sessionLockSheet`
 * for global verifier applies), then to the live active sheet name when
 * both are empty (scoped-retry payloads and legacy plans that omit
 * `targetSheet`).
 *
 * `activateWorksheet` is bridge-only; failures are warn-swallowed so
 * (a) Office.js mode is a harmless no-op (Office.js writes target sheets
 * by reference, not active sheet, so the bug doesn't manifest there),
 * (b) a target sheet that doesn't exist yet (the agent creates it via
 * `SheetModification.action = "create"` in the same payload) doesn't
 * abort the apply — the create path leaves the new sheet active and
 * cell writes land correctly. Idempotent: activating an already-active
 * sheet is a host no-op.
 *
 * Known limitation: a single `WorkbookModification` whose worksheets
 * span multiple sheets gets only the first sheet activated; cross-sheet
 * cell / range writes in that payload still drift. Mitigation = soft-lock
 * follow-up (thread `worksheetName` through every bridge wrapper).
 */
async function reactivateSheetBeforeApply(
  modifications: WorkbookModification,
  fallback: string | undefined,
  contextLabel: string,
  pinnedWorkbookName?: string
): Promise<void> {
  // Background targeting: the bridge apply path routes every write to the
  // pinned workbook by name (external-ref `destinationCell` +
  // `RangeReferenceAddress.workbookName`) and runs its own focus-free liveness
  // probe, so fronting the pin here would only yank the user's Excel view for
  // no benefit. Skip the pre-apply activation entirely; the writes self-route.
  // (Kill-switch off / no-pin paths keep the legacy pre-apply activation, which
  // the un-pinned mutation intents still rely on to resolve the active sheet.)
  if (pinnedWorkbookName && supportsRangeMutationWorkbookName()) {
    return;
  }
  const fromMods = nonEmptySheetName(
    modifications.worksheets?.find((ws) => ws.sheetName)?.sheetName
  );
  let target = fromMods || nonEmptySheetName(fallback);
  if (!target) {
    try {
      target = nonEmptySheetName(await getActiveSheetName());
    } catch (e) {
      console.warn(`[Excel] Could not resolve active sheet before ${contextLabel}:`, e);
    }
  }
  if (!target) return;
  try {
    // When a workbook is pinned, pass it through so the host flips
    // focus to the right workbook before activating the sheet —
    // otherwise a user-initiated workbook switch mid-plan would
    // leave the next write targeting whichever workbook is now
    // active. `ActivateWorksheet` accepts `workbookName` for
    // exactly this scenario.
    await activateWorksheet(
      target,
      pinnedWorkbookName ? { workbookName: pinnedWorkbookName } : undefined
    );
  } catch (e) {
    console.warn(
      `[Excel] activateWorksheet("${target}") failed before ${contextLabel}; continuing:`,
      e
    );
  }
}

// Focus custody for the active-workbook-only trailing intents
// (`HarmonizedRefreshWorksheets`) is now owned by the foreground coordinator
// (`foregroundCoordinator.runInForeground`), which serialises the pin
// activation across concurrent runs and snapshots/restores the user's real
// focus. The old `reactivatePinnedWorkbookForRefresh` (front-the-pin, no
// restore) and `withPreservedActiveSheet` (local snapshot/restore, no
// cross-run serialisation) helpers were retired in favour of it.

function isStuckHostReadyReason(reason: unknown): reason is StuckHostReadyReason {
  return reason === "stuck_edit" || reason === "stuck_busy";
}

function shouldPublishHostReadyWaitProgress(attempt: number, maxAttempts: number): boolean {
  return attempt === 1 || attempt === maxAttempts || attempt % 10 === 0;
}

function hostReadyWaitStatus(
  reason: StuckHostReadyReason,
  attempt: number,
  maxAttempts: number
): string {
  const suffix = `Waiting up to 5 minutes (${attempt}/${maxAttempts})...`;
  if (reason === "stuck_edit") {
    return (
      "Excel is in edit mode. Press Enter to finish editing or Esc to cancel editing, " +
      `then I'll continue automatically. ${suffix}`
    );
  }
  return `Excel is busy. I'll continue automatically once Excel is ready. ${suffix}`;
}

function hostReadyClearedStatus(reason: StuckHostReadyReason): string {
  return reason === "stuck_edit"
    ? "Excel exited edit mode. Continuing..."
    : "Excel is ready. Continuing...";
}

type HostReadyStatusPublisher = (status: string) => void;

function hostReadyWaitCallbacks(
  publishStatus: HostReadyStatusPublisher
): Pick<ApplyWorkbookModificationsOptions, "onHostReadyWaitProgress" | "onHostReadyWaitCleared"> {
  return {
    onHostReadyWaitProgress: (info) => {
      if (
        isStuckHostReadyReason(info.initialReason) &&
        shouldPublishHostReadyWaitProgress(info.attempt, info.maxAttempts)
      ) {
        publishStatus(hostReadyWaitStatus(info.initialReason, info.attempt, info.maxAttempts));
      }
    },
    onHostReadyWaitCleared: (info) => {
      if (isStuckHostReadyReason(info.initialReason)) {
        publishStatus(hostReadyClearedStatus(info.initialReason));
      }
    },
  };
}

async function escalateToPatientWait(
  gate: HostReadyResult,
  signal: AbortSignal,
  publishStatus?: HostReadyStatusPublisher
): Promise<HostReadyResult> {
  if (gate.reason !== "stuck_edit" && gate.reason !== "stuck_busy") {
    return gate;
  }

  const patientGate = await waitForHostReadyPatient({
    initialResult: gate,
    signal,
    onProgress: publishStatus
      ? hostReadyWaitCallbacks(publishStatus).onHostReadyWaitProgress
      : undefined,
  });
  if (publishStatus && patientGate.ready && isStuckHostReadyReason(patientGate.initialReason)) {
    publishStatus(hostReadyClearedStatus(patientGate.initialReason));
  }
  return patientGate;
}

type FinalizeStatus = "refreshed" | "recalculated" | "skipped" | "failed" | "deferred-host-busy";

interface FinalizeRefreshAndRecalcOptions {
  /**
   * Sheets that received FDS-bound formulas during the run. The
   * trailing `HarmonizedRefreshWorksheets` targets these directly.
   * Empty set = no FDS refresh needed.
   */
  fdsRefreshSheets: ReadonlySet<string>;
  /**
   * True iff a workbook operation requested `refreshFactSetData`
   * workbook op during the run (deferred by the apply path onto
   * `ApplyModificationsResult.deferredRefreshAll`). The workbook-op
   * schema has no sheet payload, so this means "refresh all" and
   * supersedes `fdsRefreshSheets`.
   */
  deferredRefreshAllRequested: boolean;
  /**
   * Mode explicitly requested via a `recalculate` workbook
   * op (deferred by the apply path). When `null`, trailing recalc
   * fires only as a side effect of a refresh.
   */
  deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null;
  /**
   * Pinned workbook for the run — handed to the foreground coordinator so the
   * trailing refresh brings the plan's origin workbook forward (and restores
   * the user's focus afterwards) even if the user clicked into a different one.
   */
  pinnedWorkbookName: string | undefined;
  /**
   * Sheet to bring forward with the pin when the refresh target list is empty.
   * Typically the session-lock sheet or the active sheet of the pinned
   * workbook.
   */
  fallbackSheet: string;
  signal: AbortSignal;
  /** Optional console-line logger (`log(...)` in `runQuery`). */
  log?: (line: string) => void;
  /** Optional loading-status updater for the chat header. */
  setLoadingStatus?: (status: string) => void;
  /** Context label propagated to `reactivatePinnedWorkbookForRefresh`. */
  contextLabel: string;
}

interface FinalizeResult {
  refreshStatus: FinalizeStatus;
  recalcStatus: FinalizeStatus;
  refreshedSheetNames?: string[];
  refreshScopeConfirmed?: boolean;
  /**
   * Set when the patient gate stuck on `stuck_edit` / `stuck_busy` —
   * used by the caller to pick the right "host busy / in edit mode"
   * footer line. `probe_failed` is treated as best-effort proceed and
   * NEVER surfaces this field.
   */
  gateReason?: StuckHostReadyReason;
}

/**
 * Single end-of-run trailing pair: `HarmonizedRefreshWorksheets` +
 * `ApplicationCalculate`. Routes refresh / recalc requests from both
 * the run's FDS-write auto-detection (`fdsRefreshSheets`) and the
 * deferred `refreshFactSetData` / `recalculate` workbook ops
 * through one patient-gated call instead of near-duplicate blocks in the
 * main and retry code paths.
 *
 * Patient gate posture (matches the apply-path posture):
 *  - `ready` / `cleared` → proceed.
 *  - `probe_failed` → proceed best-effort. The downstream refresh /
 *    recalc wrappers have their own try / catch and chat-side
 *    fallback footer; skipping a transient probe failure would just
 *    deny a working refresh.
 *  - `stuck_edit` / `stuck_busy` → skip both calls and surface the
 *    appropriate edit / busy footer via `gateReason`.
 *
 * Trailing recalc topology: fires when refresh is needed OR the
 * a workbook operation explicitly asked. Ordinary formula writes are kept fresh by
 * the per-burst recalc inside the apply path's manual-calc bracket —
 * adding them to this gate would double-fire `ApplicationCalculate`
 * for every formula-writing step and violate the "at most once
 * end-of-run" invariant.
 *
 * Best-effort except user cancellation — the apply burst already landed,
 * so ordinary trailing failures never abort the run. Failures surface as
 * `failed` status with a polished chat-side footer ("click the Refresh
 * button in your FactSet add-in"). If the user cancels during the patient
 * wait, `AbortError` propagates to the outer run-cancel path.
 */
async function finalizeRefreshAndRecalc(
  opts: FinalizeRefreshAndRecalcOptions
): Promise<FinalizeResult> {
  const {
    fdsRefreshSheets,
    deferredRefreshAllRequested,
    deferredRecalcMode,
    pinnedWorkbookName,
    fallbackSheet,
    signal,
    log,
    setLoadingStatus,
    contextLabel,
  } = opts;

  const refreshNeeded = deferredRefreshAllRequested || fdsRefreshSheets.size > 0;
  const recalcNeeded = refreshNeeded || deferredRecalcMode !== null;
  if (!recalcNeeded || signal.aborted) {
    return { refreshStatus: "skipped", recalcStatus: "skipped" };
  }

  let gate = await waitForHostReady(PATIENT_HOST_READY_SCHEDULE);
  if (signal.aborted) {
    return { refreshStatus: "skipped", recalcStatus: "skipped" };
  }
  gate = await escalateToPatientWait(gate, signal, setLoadingStatus);
  if (signal.aborted) {
    return { refreshStatus: "skipped", recalcStatus: "skipped" };
  }
  if (gate.reason === "stuck_edit" || gate.reason === "stuck_busy") {
    console.warn(
      `[Excel] Trailing finalize gate stuck (${gate.reason}) after ${gate.attempts} probes; deferring refresh/recalc.`
    );
    const copy = hostReadyRetryCopy(gate.reason);
    log?.(`⏸️ Skipped post-plan refresh — Excel was ${copy.queueShortLabel}.`);
    return {
      refreshStatus: "deferred-host-busy",
      recalcStatus: "deferred-host-busy",
      gateReason: gate.reason,
    };
  }
  // `ready` / `cleared` / `probe_failed` all proceed below.
  // `probe_failed` mirrors the apply-path posture — proceed
  // best-effort and let the wrapped refresh / recalc catch a real
  // host failure.

  if (signal.aborted) {
    return { refreshStatus: "skipped", recalcStatus: "skipped" };
  }

  let refreshStatus: FinalizeStatus = "skipped";
  let refreshedSheetNames: string[] = [];
  let refreshScopeConfirmed = false;
  if (refreshNeeded) {
    const sheetList = Array.from(fdsRefreshSheets);
    // Prefer a sheet from the refresh-list when we have one; fall back to the
    // caller-provided sheet otherwise (the workbook-op channel doesn't carry a
    // sheet binding, so a deferred refresh-all run still needs a hint).
    const refreshSheet =
      fdsRefreshSheets.size > 0 ? (sheetList[0] ?? fallbackSheet) : fallbackSheet;
    const refreshLabel = deferredRefreshAllRequested
      ? "Refreshing FactSet data (all sheets)"
      : `Refreshing FactSet data on ${sheetList.length} sheet(s)`;
    setLoadingStatus?.(`${refreshLabel}…`);
    log?.(`🔄 ${refreshLabel} (host runtime; no cloud quota used)`);
    try {
      // `HarmonizedRefreshWorksheets` is active-workbook-only (f2_docs) and
      // takes no `workbookName`, so the pin must be the active workbook for the
      // refresh to hit the run's origin. Route through the foreground
      // coordinator: it snapshots the user's real focus, brings the pin (+
      // refreshSheet) forward, runs the refresh, then restores the user's focus
      // — serialised so concurrent multi-workbook finalizes never ping-pong.
      // No-pin / Office.js paths run inline (coordinator no-op), matching the
      // old behaviour where the pinned-workbook reactivation was a no-op.
      const refreshResult = await foregroundCoordinator.runInForeground(
        { workbookName: pinnedWorkbookName, sheet: refreshSheet },
        async () => {
          if (deferredRefreshAllRequested) {
            return harmonizedRefreshAllWorksheetsViaIntents(
              pinnedWorkbookName,
              undefined,
              FACTSET_WORKSHEET_REFRESH_TIMEOUT_MS,
            );
          }
          return harmonizedRefreshWorksheetsSequentiallyViaIntents(
            sheetList,
            undefined,
            FACTSET_WORKSHEET_REFRESH_TIMEOUT_MS,
          );
        },
        { contextLabel, log }
      );
      refreshedSheetNames = refreshResult.refreshedSheetNames;
      refreshScopeConfirmed = refreshResult.scopeConfirmed;
      const failedSheetNames = refreshResult.failedSheetNames ?? [];
      refreshStatus = failedSheetNames.length > 0 ? "failed" : "refreshed";
      if (failedSheetNames.length > 0) {
        log?.(
          `⚠️ FactSet auto-refresh completed partially; failed sheets: ${failedSheetNames.join(", ")}`
        );
      } else {
        log?.(
          refreshScopeConfirmed
            ? `✅ FactSet data refreshed sequentially`
            : `✅ FactSet data refreshed (worksheet scope not confirmed)`
        );
      }
    } catch (refreshErr) {
      refreshStatus = "failed";
      console.warn(`[Excel] Trailing HarmonizedRefresh failed (${contextLabel}):`, refreshErr);
      log?.(`⚠️ FactSet auto-refresh could not complete`);
    }
  }

  if (signal.aborted) {
    return {
      refreshStatus,
      recalcStatus: "skipped",
      refreshedSheetNames,
      refreshScopeConfirmed,
    };
  }

  let recalcStatus: FinalizeStatus = "skipped";
  try {
    // Refresh-paired recalcs use "full" so any cell pinned in manual
    // calc mode re-evaluates against the freshly fetched FDS data.
    // Explicit deferred recalcs honour the requested mode.
    //
    // Runs OUTSIDE the foreground coordinator window (after focus is restored):
    // `ApplicationCalculate` is application-level per f2_docs ("not workbook
    // specific"), so it recalculates every open workbook regardless of which is
    // active — no pin activation or focus custody required.
    const mode = deferredRecalcMode ?? "full";
    await applicationCalculate(mode);
    recalcStatus = "recalculated";
  } catch (calcErr) {
    recalcStatus = "failed";
    console.warn(
      `[Excel] Trailing ApplicationCalculate failed (${contextLabel}); cells may stay stale until the user triggers a manual recalc:`,
      calcErr
    );
  }

  return {
    refreshStatus,
    recalcStatus,
    refreshedSheetNames,
    refreshScopeConfirmed,
  };
}

/**
 * Build the chat-side footer line(s) for a `finalizeRefreshAndRecalc`
 * result. Returns the empty string when nothing surfaced (no refresh
 * needed AND no failure to communicate).
 */
function buildFinalizeFooter(result: FinalizeResult): string {
  const lines: string[] = [];
  if (result.refreshStatus === "refreshed") {
    lines.push("📊 FactSet data refreshed");
  } else if (result.refreshStatus === "failed") {
    lines.push(
      "⚠️ Could not refresh FactSet data automatically — please click the Refresh button in your FactSet add-in."
    );
  } else if (result.refreshStatus === "deferred-host-busy") {
    const blocker = result.gateReason
      ? hostReadyRetryCopy(result.gateReason).blocker
      : "Excel is unavailable.";
    const recovery =
      result.gateReason === "stuck_edit"
        ? "Press Enter to finish the edit or Esc to cancel it, then click Refresh on the FactSet add-in."
        : "Wait for Excel to finish, then click Refresh on the FactSet add-in.";
    lines.push(`⏸️ Skipped post-plan refresh because ${blocker} ${recovery}`);
  }

  if (result.recalcStatus === "failed") {
    lines.push(
      "⚠️ Could not recalculate the workbook automatically — press F9 or use Excel's Calculate Now command."
    );
  } else if (result.recalcStatus === "recalculated" && result.refreshStatus === "skipped") {
    lines.push("🧮 Workbook recalculated");
  }

  return lines.length > 0 ? `\n\n${lines.join("\n")}` : "";
}

async function scanPostFinalizeHardErrors(opts: {
  targets: Iterable<PostFinalizeScanTarget>;
  pinnedWorkbookName?: string;
  signal: AbortSignal;
  log?: (message: string) => void;
}): Promise<PostFinalizeHardErrorScanResult> {
  const errors: ExcelHardErrorCell[] = [];
  const truncatedTargets: PostFinalizeScanTarget[] = [];
  const failedTargets: PostFinalizeScanTarget[] = [];
  const scanTargets = coalescePostFinalizeScanTargets(opts.targets);
  for (const target of scanTargets) {
    if (opts.signal.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      const { range, truncated } = await readRangeContent(
        target.sheetName,
        target.address,
        { includeFormulas: true, includeFormats: false },
        opts.pinnedWorkbookName
      );
      if (!range) {
        failedTargets.push(target);
        opts.log?.(
          `⚠️ Could not scan ${target.sheetName}!${target.address} for formula errors after refresh because Excel returned no range data.`
        );
        continue;
      }
      if (truncated) {
        truncatedTargets.push(target);
        opts.log?.(
          `⚠️ Could not fully scan ${target.sheetName}!${target.address} for formula errors after refresh because the range is too large.`
        );
        continue;
      }
      const scanOptions = {
        sheetName: target.sheetName,
        address: range.address,
        startRow: range.startRow,
        startColumn: range.startColumn,
      };
      const hasFormulaMetadata =
        Array.isArray(range.formulas) &&
        range.formulas.some((row) => Array.isArray(row) && row.length > 0);
      if (!hasFormulaMetadata) {
        const scan = classifyHardErrorsInValues(range.values, scanOptions);
        errors.push(...scan.errors);
        continue;
      }
      const scan = classifyHardErrorsInFormulaValues(range.values, range.formulas, scanOptions);
      errors.push(...scan.errors);
    } catch (err) {
      if (opts.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) {
        throw err;
      }
      failedTargets.push(target);
      opts.log?.(
        `⚠️ Could not scan ${target.sheetName}!${target.address} for formula errors after refresh: ` +
          `${err instanceof Error ? err.message : String(err)}`
      );
    }
  }
  return { errors, truncatedTargets, failedTargets };
}

function shouldRunPostFinalizeFormulaScan(runResult: ExecutorRunResult): boolean {
  if (
    runResult.fdsRefreshSheets.size > 0 ||
    runResult.deferredRefreshAllRequested ||
    runResult.deferredRecalcMode !== null ||
    (runResult.formulaWriteTargets?.length ?? 0) > 0
  ) {
    return true;
  }

  for (const worksheet of runResult.lastModifications?.worksheets ?? []) {
    for (const cell of worksheet.cells ?? []) {
      if (cell.action === "set" && (isFormulaWrite(cell.formula) || isFormulaWrite(cell.value))) {
        return true;
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        range.action === "fill" &&
        (matrixHasFormulaWrite(range.formulas) || matrixHasFormulaWrite(range.values))
      ) {
        return true;
      }
    }
  }

  return false;
}

function isFormulaWrite(value: unknown): boolean {
  return typeof value === "string" && value.trimStart().startsWith("=");
}

function matrixHasFormulaWrite(values: unknown): boolean {
  return (
    Array.isArray(values) && values.some((row) => Array.isArray(row) && row.some(isFormulaWrite))
  );
}

interface PostFinalizeScanTarget {
  sheetName: string;
  address: string;
}

interface PostFinalizeHardErrorScanResult {
  errors: ExcelHardErrorCell[];
  truncatedTargets: PostFinalizeScanTarget[];
  failedTargets: PostFinalizeScanTarget[];
}

function collectPostFinalizeScanTargets(runResult: ExecutorRunResult): PostFinalizeScanTarget[] {
  const targets: PostFinalizeScanTarget[] = [];
  for (const target of runResult.formulaWriteTargets ?? []) {
    targets.push({ sheetName: target.sheetName, address: target.address });
  }

  for (const worksheet of runResult.lastModifications?.worksheets ?? []) {
    if (!worksheet.sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      if (cell.action === "set" && (isFormulaWrite(cell.formula) || isFormulaWrite(cell.value))) {
        targets.push({ sheetName: worksheet.sheetName, address: cell.address });
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        range.action === "fill" &&
        (matrixHasFormulaWrite(range.formulas) || matrixHasFormulaWrite(range.values))
      ) {
        targets.push({ sheetName: worksheet.sheetName, address: range.address });
      }
    }
  }

  return dedupePostFinalizeScanTargets(targets);
}

function dedupePostFinalizeScanTargets(
  targets: Iterable<PostFinalizeScanTarget>
): PostFinalizeScanTarget[] {
  const unique = new Map<string, PostFinalizeScanTarget>();
  for (const target of Array.from(targets)) {
    const sheetName = typeof target.sheetName === "string" ? target.sheetName.trim() : "";
    const address = typeof target.address === "string" ? target.address.trim() : "";
    if (!sheetName || !address) continue;
    unique.set(`${sheetName.toLowerCase()}!${address.toUpperCase()}`, { sheetName, address });
  }
  return Array.from(unique.values());
}

interface A1ScanRegion {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

function parseA1ScanRegion(address: string): A1ScanRegion | undefined {
  const unqualifiedAddress = address
    .slice(address.lastIndexOf("!") + 1)
    .replace(/\$/g, "")
    .trim();
  const match = /^([A-Z]{1,3})(\d+)(?::([A-Z]{1,3})(\d+))?$/i.exec(unqualifiedAddress);
  if (!match) return undefined;
  const startColumn = excelColumnIndex(match[1]);
  const endColumn = excelColumnIndex(match[3] ?? match[1]);
  const startRow = Number(match[2]);
  const endRow = Number(match[4] ?? match[2]);
  if (
    startColumn === null ||
    endColumn === null ||
    !Number.isSafeInteger(startRow) ||
    !Number.isSafeInteger(endRow) ||
    startRow < 1 ||
    endRow < 1
  ) {
    return undefined;
  }
  return {
    startRow: Math.min(startRow, endRow),
    endRow: Math.max(startRow, endRow),
    startColumn: Math.min(startColumn, endColumn),
    endColumn: Math.max(startColumn, endColumn),
  };
}

function scanRegionArea(region: A1ScanRegion): number {
  return (region.endRow - region.startRow + 1) * (region.endColumn - region.startColumn + 1);
}

function combineRectangularScanRegions(
  left: A1ScanRegion,
  right: A1ScanRegion
): A1ScanRegion | undefined {
  const combined = {
    startRow: Math.min(left.startRow, right.startRow),
    endRow: Math.max(left.endRow, right.endRow),
    startColumn: Math.min(left.startColumn, right.startColumn),
    endColumn: Math.max(left.endColumn, right.endColumn),
  };
  return scanRegionArea(combined) <= scanRegionArea(left) + scanRegionArea(right)
    ? combined
    : undefined;
}

function formatA1ScanRegion(region: A1ScanRegion): string {
  const start = `${excelColumnName(region.startColumn)}${region.startRow}`;
  const end = `${excelColumnName(region.endColumn)}${region.endRow}`;
  return start === end ? start : `${start}:${end}`;
}

function coalescePostFinalizeScanTargets(
  targets: Iterable<PostFinalizeScanTarget>
): PostFinalizeScanTarget[] {
  const coalesced: Array<PostFinalizeScanTarget & { region?: A1ScanRegion }> = [];
  for (const target of dedupePostFinalizeScanTargets(targets)) {
    let region = parseA1ScanRegion(target.address);
    if (!region) {
      coalesced.push(target);
      continue;
    }

    for (let index = coalesced.length - 1; index >= 0; index -= 1) {
      const existing = coalesced[index];
      if (existing.sheetName.toLowerCase() !== target.sheetName.toLowerCase() || !existing.region) {
        continue;
      }
      const combined = combineRectangularScanRegions(existing.region, region);
      if (!combined) continue;
      region = combined;
      coalesced.splice(index, 1);
    }
    coalesced.push({
      sheetName: target.sheetName,
      address: formatA1ScanRegion(region),
      region,
    });
  }
  return coalesced.map(({ sheetName, address }) => ({ sheetName, address }));
}

function summarizePostFinalizeScanTargets(
  targets: readonly PostFinalizeScanTarget[],
  maxItems = 5
): string {
  const sample = targets
    .slice(0, maxItems)
    .map((target) => `${target.sheetName}!${target.address}`)
    .join(", ");
  const suffix = targets.length > maxItems ? ` and ${targets.length - maxItems} more` : "";
  return `${sample}${suffix}`;
}

function replaceStepResultWithPostFinalizeWarning(
  stepResults: string[],
  label: string | number,
  reasons: readonly string[]
): void {
  const prefix = `${label}.`;
  const warningLine = `${prefix} needs attention: ${reasons.join("; ")}`;
  const existingIndex = stepResults.findIndex((result) => result.trimStart().startsWith(prefix));
  if (existingIndex === -1) {
    stepResults.push(warningLine);
    return;
  }
  // Replace the step's line in place so array order — and the
  // `stepResultIndexById` mapping derived from it — stays stable. Any duplicate
  // lines that share this step's prefix are dropped (defensive; normally none),
  // which also keeps a repeated call idempotent.
  stepResults[existingIndex] = warningLine;
  for (let i = stepResults.length - 1; i > existingIndex; i -= 1) {
    if (stepResults[i].trimStart().startsWith(prefix)) {
      stepResults.splice(i, 1);
    }
  }
}

/**
 * Pretty-prints a diagnostic payload for the debug log. Strings that
 * parse as JSON are re-stringified with indentation; everything else is
 * stringified as-is. Pure helper hoisted to module scope so it is not
 * re-created on every run.
 */
function formatDiagnosticPayload(payload: unknown): string {
  if (typeof payload === "string") {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  }
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

/**
 * Parses a diagnostic payload into structured data when it is a JSON
 * string, otherwise returns it unchanged. Pure helper hoisted to module
 * scope (shares intent with `formatDiagnosticPayload`).
 */
function parseDiagnosticPayload(payload: unknown): unknown {
  if (typeof payload !== "string") return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
}

/**
 * Moves one in-flight chat tab between workbook slots. The target's pristine
 * hydration placeholder is replaced; real target conversations are preserved.
 * Exported for focused handoff tests.
 */
export function relocateExcelRunTab(
  slots: Record<string, ExcelTabsState>,
  sourceWorkbookKey: string,
  targetWorkbookKey: string,
  runTabId: string
): Record<string, ExcelTabsState> {
  if (!targetWorkbookKey || sourceWorkbookKey === targetWorkbookKey) return slots;
  const source = slots[sourceWorkbookKey];
  const target = slots[targetWorkbookKey];
  const runTab = source?.tabs.find((tab) => tab.id === runTabId);
  if (!source || !runTab) {
    if (target?.tabs.some((tab) => tab.id === runTabId)) {
      return {
        ...slots,
        [targetWorkbookKey]: { ...target, activeTabId: runTabId },
      };
    }
    return slots;
  }

  const sourceTabs = source.tabs.filter((tab) => tab.id !== runTabId);
  const nextSource =
    sourceTabs.length > 0
      ? {
          ...source,
          tabs: sourceTabs,
          activeTabId: sourceTabs.some((tab) => tab.id === source.activeTabId)
            ? source.activeTabId
            : sourceTabs[0].id,
        }
      : createInitialTabsState();
  const targetIsPristinePlaceholder =
    target?.tabs.length === 1 &&
    target.tabs[0].session.messages.length === 0 &&
    !target.tabs[0].isLoading &&
    target.tabs[0].currentPlan === null;
  const targetTabs = targetIsPristinePlaceholder
    ? []
    : (target?.tabs ?? []).filter((tab) => tab.id !== runTabId);

  return {
    ...slots,
    [sourceWorkbookKey]: nextSource,
    [targetWorkbookKey]: {
      ...(target ?? { tabs: [], activeTabId: runTabId }),
      tabs: [...targetTabs, runTab],
      activeTabId: runTabId,
    },
  };
}

/**
 * Carry a same-tick optimistic submit from the empty-key sentinel onto the
 * resolved workbook. `materializeTabsStateForRun` prefers persisted/in-memory
 * history (`loaded ?? provisional`), so a first prompt on `""` would otherwise
 * lose its user bubble and `isLoading` once identity resolves. Continue actions
 * stay on the copied messages because the run is not accepted yet.
 */
export function carryInFlightSubmitToResolvedSlot(
  slots: Record<string, ExcelTabsState>,
  sourceWorkbookKey: string,
  targetWorkbookKey: string,
  sourceTabId: string,
  userMessageId: string
): Record<string, ExcelTabsState> {
  if (!targetWorkbookKey || sourceWorkbookKey === targetWorkbookKey) return slots;
  const source = slots[sourceWorkbookKey];
  const sourceTab = source?.tabs.find((tab) => tab.id === sourceTabId);
  const message = sourceTab?.session.messages.find((msg) => msg.id === userMessageId);
  if (!source || !sourceTab || !message) return slots;

  const target = slots[targetWorkbookKey] ?? createInitialTabsState();
  const targetTab = target.tabs.find((tab) => tab.id === target.activeTabId);
  if (!targetTab) return slots;

  const alreadyHasMessage = targetTab.session.messages.some((msg) => msg.id === userMessageId);
  const nextTargetTab: ExcelTab = {
    ...targetTab,
    isLoading: true,
    loadingStatus: sourceTab.loadingStatus,
    session: alreadyHasMessage
      ? targetTab.session
      : {
          ...targetTab.session,
          messages: [...targetTab.session.messages, message],
          updatedAt: Date.now(),
        },
  };

  return {
    ...slots,
    [sourceWorkbookKey]: {
      ...source,
      tabs: source.tabs.map((tab) =>
        tab.id === sourceTabId ? { ...tab, isLoading: false, loadingStatus: "" } : tab
      ),
    },
    [targetWorkbookKey]: {
      ...target,
      tabs: target.tabs.map((tab) =>
        tab.id === target.activeTabId ? nextTargetTab : tab
      ),
    },
  };
}

const ExcelApp: React.FC = () => {
  const [supportDialogOpen, setSupportDialogOpen] = useState<boolean>(false);
  const [chatViewByScope, setChatViewByScope] = useState<Record<string, "chat" | "citations">>({});
  const queryComposerRef = useRef<ExcelQueryComposerHandle>(null);
  const [userQuestionsByWorkbook, setUserQuestionsByWorkbook] = useState<
    Record<
      string,
      {
        owner: string;
        question: UserQuestion;
        resolve: (answer: UserQuestionAnswer) => void;
      }
    >
  >({});
  const userQuestionsByWorkbookRef = useRef(userQuestionsByWorkbook);
  userQuestionsByWorkbookRef.current = userQuestionsByWorkbook;
  const clientUpdateControllerRef = useRef<ClientUpdateController | null>(null);
  const [clientUpdatePending, setClientUpdatePending] = useState(false);
  const [suggestedSkillsState, setSuggestedSkillsState] = useState<SuggestedSkillsSelectionState>({
    availableSkillSlugs: [],
    selectedSkillSlugs: [],
    selectionAtLimit: false,
  });
  const activeBrand = useExcelActiveBrand();
  const activeBrandId = activeBrand?.id ?? null;
  const [brandStyle, setBrandStyle] = useState<ExcelBrandId | null>(null);

  // Brand styling is an explicit per-session opt-in. Feature-flag state is
  // reset during logout/account changes, so clear the local selection when
  // that availability disappears rather than reviving a prior user's choice
  // if the same brand is served again after sign-in.
  useEffect(() => {
    if (activeBrandId === null) {
      setBrandStyle(null);
    }
  }, [activeBrandId]);

  /**
   * Per-workbook chat state. Each key is a stable workbook identifier
   * (the same one `excelChatHistoryService` uses for persistence —
   * full path when available, falling back to filename); the value
   * is that workbook's `ExcelTabsState`. The empty-string key
   * `""` is reserved for the "no workbook yet known" state (bridge
   * resolving / Office.js mode / pre-auth) so the UI always renders
   * a tabs object even before `currentWorkbookKey` resolves.
   *
   * Workbook-scoped chats: switching workbooks shows that workbook's
   * tabs; a plan running in workbook A keeps writing into its slot
   * even when the user has clicked into workbook B. Both visible
   * (foreground) and background tabs survive workbook switches.
   */
  const [tabsByWorkbook, setTabsByWorkbook] = useState<Record<string, ExcelTabsState>>(() => ({
    "": createInitialTabsState(),
  }));
  const tabsByWorkbookRef = useRef(tabsByWorkbook);
  useEffect(() => {
    tabsByWorkbookRef.current = tabsByWorkbook;
  }, [tabsByWorkbook]);

  /** Which workbook keys have already been hydrated from storage. */
  const hydratedKeysRef = useRef<Set<string>>(new Set([""]));

  /**
   * Stable per-workbook identifier for chat-history scoping. It prefers
   * `fullName` (full path) over `name` so two workbooks with the same
   * filename in different folders don't share history, with active-context
   * fallback when `getOpenWorkbooks()` is ambiguous. `null` while the
   * bridge is resolving or in Office.js mode where workbook identity isn't
   * surfaced - `excelChatHistoryService` no-ops on incomplete scopes so
   * the UI stays in-memory until both this and `accountScopeKey` resolve.
   */
  const [currentWorkbookKey, setCurrentWorkbookKey] = useState<string | null>(null);
  const currentWorkbookKeyFallbackRef = useRef(currentWorkbookKey);
  const workbookIdentitySnapshotRef = useRef<WorkbookIdentitySnapshot | null>(null);
  /**
   * Set while the workbook poll is paused, so the next observation is not
   * treated as consecutive with the last one.
   *
   * `isWorkbookSaveAs` reads a rename from the active workbook's identity
   * being replaced between two observations. Across a pause — a hidden pane,
   * or a run's workbook handoff — the user can close one workbook and open an
   * unrelated one unseen, which arrives looking exactly like that
   * replacement. Classifying it would move their chat into the wrong file and
   * drop it from the right one, so the first poll after a pause only
   * re-establishes the snapshot.
   */
  const workbookObservationGapRef = useRef(false);
  /** Consecutive degraded polls currently holding the identity snapshot. */
  const workbookInventoryHoldPollsRef = useRef(0);
  const pendingWorkbookHistoryTransferRef = useRef<WorkbookHistoryTransfer | null>(null);
  const workbookHistoryKeyRedirectsRef = useRef<Map<string, string>>(new Map());
  /**
   * Number of runs currently moving their conversation into a workbook they
   * are creating. Excel activates a new workbook the instant it exists, but
   * the run can only relocate its chat tab once the workbook is populated —
   * several seconds later for a Chart Creator import. Following Excel in
   * between would blank the conversation the user is watching and then bring
   * it back, so the workbook poll below stands still while this is non-zero.
   */
  const workbookHandoffHoldsRef = useRef(0);
  /**
   * The spelling of each open workbook's key that this pane has committed to,
   * by display name. See `canonicalWorkbookKey`: the host names one workbook
   * several ways, and the chat scope has to pick one and stay on it.
   */
  const workbookKeyAliasesRef = useRef(new Map<string, string>());
  /**
   * Workbook identity reader for chat-history scoping and run pinning.
   * This is intentionally metadata-only: `GetOpenWorkbooks` and
   * `GetActiveExcelContext` identify the active workbook but do not read
   * cell values, formulas, ranges, charts, or tables. Workbook content
   * reads stay behind an explicit prompt or write/retry action.
   *
   * Every host probe is covered by one timeout so a wedged bridge cannot
   * block query submission or workbook-chat switching indefinitely.
   */
  const readWorkbookKey = useCallback(async (): Promise<string | null> => {
    try {
      return await withTimeout(
        (async () => {
          let workbooks: OpenWorkbookInfo[] = [];
          let workbookInventoryAvailable = true;
          try {
            workbooks = await getOpenWorkbooks();
          } catch {
            workbookInventoryAvailable = false;
            workbooks = [];
          }

          const probeKey = async (): Promise<string | null> => {
            const activeFlagged = workbooks.filter((w) => w.isActive === true);
            if (activeFlagged.length === 1) {
              return activeFlagged[0]?.fullName || activeFlagged[0]?.name || null;
            }

            try {
              const ctx = await getActiveExcelContext();
              const workbookName = ctx.workbookName?.trim();
              const matchedByName = workbookName
                ? workbooks.find((w) => w.fullName === workbookName || w.name === workbookName)
                : undefined;
              return matchedByName?.fullName || matchedByName?.name || workbookName || null;
            } catch {
              // Fall through to the single-workbook fallback below.
            }

            if (workbooks.length === 1) {
              return workbooks[0]?.fullName || workbooks[0]?.name || null;
            }
            return null;
          };

          const probedKey = await probeKey();
          const previousIdentity = workbookIdentitySnapshotRef.current;
          const inventoryUnusable =
            !workbookInventoryAvailable || workbooks.length === 0;
          if (inventoryUnusable && previousIdentity) {
            // A missing inventory cannot tell a Save As from a switch.
            // Hold the last verified scope until a healthy poll can classify it.
            // A successful empty list is not a close: GetActiveExcelContext can
            // still name the workbook, and treating that as a switch would drop
            // the snapshot that later Save As detection needs.
            //
            // Naming no workbook at all is different. Nothing is open, so the
            // held identity describes a workbook the user has closed, and
            // whatever they open next would otherwise read as that workbook
            // being renamed — carrying its chat into an unrelated file.
            workbookInventoryHoldPollsRef.current += 1;
            if (
              !probedKey?.trim() ||
              workbookInventoryHoldPollsRef.current > MAX_WORKBOOK_INVENTORY_HOLD_POLLS
            ) {
              workbookObservationGapRef.current = true;
            }
            return (
              pendingWorkbookHistoryTransferRef.current?.sourceKey ??
              previousIdentity.resolvedKey
            );
          }
          if (workbooks.length === 0) {
            workbookIdentitySnapshotRef.current = null;
          }
          const activeIdentity = normalizedWorkbookIdentity(probedKey);
          const activeWorkbook =
            workbooks.find((workbook) => workbookIdentity(workbook) === activeIdentity) ??
            workbooks.find((workbook) => workbook.isActive === true);
          const openKeys = workbooks.map(workbookIdentity).filter(Boolean);
          if (workbookInventoryAvailable && workbooks.length > 0) {
            pruneWorkbookHistoryRedirects(
              workbookHistoryKeyRedirectsRef.current,
              openKeys
            );
          }
          const saveAsDetected =
            !workbookObservationGapRef.current &&
            isWorkbookSaveAs(
              previousIdentity,
              activeIdentity,
              activeWorkbook?.saved,
              openKeys
            );
          const resolvedKey = saveAsDetected
            ? probedKey?.trim() || null
            : canonicalWorkbookKey(probedKey, workbooks, workbookKeyAliasesRef.current);

          if (saveAsDetected && previousIdentity && resolvedKey) {
            pendingWorkbookHistoryTransferRef.current = {
              sourceKey: previousIdentity.resolvedKey,
              targetKey: resolvedKey,
            };
            rememberWorkbookKey(workbookKeyAliasesRef.current, resolvedKey);
            // Trigger the persistence effect even if an active run keeps the
            // visible scope pinned to the source key until it finishes.
            setTabsByWorkbook((prev) => ({ ...prev }));
          }

          if (probedKey?.trim() && resolvedKey && workbooks.length > 0) {
            const snapshotUnchanged =
              previousIdentity?.activeIdentity === activeIdentity &&
              previousIdentity.activeSaved === activeWorkbook?.saved &&
              previousIdentity.resolvedKey === resolvedKey &&
              previousIdentity.openKeys.length === openKeys.length &&
              previousIdentity.openKeys.every((key, index) => key === openKeys[index]);
            if (!snapshotUnchanged) {
              workbookIdentitySnapshotRef.current = {
                activeIdentity,
                activeSaved: activeWorkbook?.saved,
                openKeys,
                resolvedKey,
              };
            }
            workbookObservationGapRef.current = false;
            workbookInventoryHoldPollsRef.current = 0;
          }

          // Which branch above answers depends on what the host could tell us this
          // second, and they spell the same workbook differently. The chat scope
          // and a run's own handoff have to agree on one key, so the spelling is
          // settled once per workbook rather than per probe.
          const queuedTransfer = pendingWorkbookHistoryTransferRef.current;
          const sourceRunIsActive = queuedTransfer
            ? tabsByWorkbookRef.current[queuedTransfer.sourceKey]?.tabs.some(
                (tab) => tab.isLoading
              )
            : false;
          const liveKey =
            sourceRunIsActive && queuedTransfer
              ? queuedTransfer.sourceKey
              : resolvedKey;
          dropLiveWorkbookHistoryRedirect(
            workbookHistoryKeyRedirectsRef.current,
            liveKey
          );
          return liveKey;
        })()
      );
    } catch {
      return null;
    }
  }, []);
  const resolveWorkbookKey = useCallback(async (): Promise<string | null> => {
    const nextKey = await readWorkbookKey();
    const pendingTransfer = pendingWorkbookHistoryTransferRef.current;
    const keepSourceScope =
      pendingTransfer?.sourceKey === currentWorkbookKey &&
      pendingTransfer.targetKey === nextKey;
    const resolvedKey = keepSourceScope ? currentWorkbookKey : nextKey;
    if (resolvedKey !== null) {
      currentWorkbookKeyFallbackRef.current = resolvedKey;
      dropLiveWorkbookHistoryRedirect(
        workbookHistoryKeyRedirectsRef.current,
        resolvedKey
      );
      setCurrentWorkbookKey((prev) =>
        prev === resolvedKey ? prev : resolvedKey
      );
    }
    return resolvedKey;
  }, [currentWorkbookKey, readWorkbookKey]);
  /**
   * Set once we've finished a hydration pass for the current scope.
   * Prevents the initial empty `tabsState` from overwriting saved
   * history before `loadExcelChatTabs` has had a chance to run.
   */
  const [chatHistoryHydrated, setChatHistoryHydrated] = useState<boolean>(false);

  // Run progress fields live on `ExcelTab` so background runs update their own
  // workbook instead of the visible workbook's UI.

  const [logs, setLogs] = useState<string[]>([]);
  const [showLogs, setShowLogs] = useState<boolean>(false);

  const log = useCallback((message: string) => {
    console.log(`[ExcelApp] ${message}`);
    const nextLine = `${new Date().toLocaleTimeString()} | ${message}`;
    setLogs((prev) => [...prev, nextLine].slice(-MAX_CONSOLE_LOGS));
  }, []);

  const { authState, authChecked, accountScopeKey, handleLogin } = useAuth({
    log,
  });
  /**
   * Account scope whose chat state has completed the reset effect below.
   *
   * This ref intentionally changes only in that effect. During the render that
   * first observes a new account, it still names the previous owner, allowing
   * the visible state derivation to hide that owner's transcript before React
   * commits it under the new account.
   */
  const settledAccountScopeKeyRef = useRef(accountScopeKey);
  const storageUserId = authState.user?.id || authState.user?.email || null;
  const manageSkillsUrl = buildSkillsManagementUrl(authState.features?.finsterWebUrl);
  const availableDataSources = useMemo(
    () => getAvailableFinsterQueryDataSources(authState.features?.featureFlags, !authChecked),
    [authState.features?.featureFlags, authChecked]
  );
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const shouldShowOnboarding = Boolean(
    authState.isAuthenticated &&
    storageUserId &&
    !onboardingDismissed &&
    !hasCompletedOnboarding("excel", storageUserId)
  );

  useEffect(() => {
    setOnboardingDismissed(false);
  }, [storageUserId]);

  // Workbook-key resolution for per-workbook chat scoping. This effect
  // polls `readWorkbookKey` on a tight cadence so a workbook switch in
  // Excel is reflected in the chat scope almost immediately, without
  // the user having to hover, click, or focus the pane first. The
  // privacy boundary is preserved: only identity metadata intents
  // (`GetOpenWorkbooks`, `GetActiveExcelContext`) fire — never content
  // reads such as `GetRangeInfo`, `GetRangeAsCsv`, or
  // `extractAllSheetsContent`.
  //
  // Why polling (and not the event-driven `mouseenter` / `focus` /
  // `pointerdown` / `visibilitychange` listeners we used briefly):
  // those events only fire when the user interacts with the add-in
  // pane, so if the user switches to a different workbook in Excel
  // without touching the pane the chat scope stays stuck on the prior
  // workbook until they hover back. Polling closes that gap — the
  // user sees the right chat the moment Excel surfaces the new
  // workbook.
  //
  // Why 1s: reduces `GetActiveExcelContext` probe volume while still
  // keeping workbook switches responsive in the UI, and remains clear of the host probe's
  // 1.5s `withTimeout` so the `inFlight` guard absorbs an occasional
  // slow probe without queuing up backlog.
  //
  // `inFlight` prevents probe stacking — if a previous tick is still
  // resolving (slow bridge, queued host call), the new tick is a
  // no-op rather than queuing a second concurrent
  // `GetOpenWorkbooks` / `GetActiveExcelContext` pair.
  //
  // `runQuery` also calls `resolveWorkbookKey()` at submit time,
  // so the key resyncs the moment the user hits Send even if the
  // poll happens to be mid-flight.
  //
  // Transient `null` probe results are skipped so a single failed
  // read can't drop the UI into the unkeyed `""` slot.
  //
  // `workbookHandoffHoldsRef` pauses the probe while a run is moving its
  // conversation into a workbook it just created: that switch belongs to the
  // run, which relocates the chat tab itself once the workbook is ready.
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    // Visibility gate. The host probe (`readWorkbookKey`) is suppressed
    // while `document.hidden` is true so a perpetually-hidden taskpane
    // never drips `GetActiveExcelContext` / `GetOpenWorkbooks` calls
    // into the bridge. The 1Hz interval keeps ticking (the wakeup
    // itself is free) — only the network/host call is gated. When the
    // pane becomes visible, the `visibilitychange` listener fires an
    // immediate `refresh()` so chat scope resyncs at once rather than
    // waiting up to 1s for the next interval tick.
    const refresh = async () => {
      // A skipped probe leaves the host unobserved, so the next reading cannot
      // be compared against the last one to classify a Save As.
      if (document.hidden) {
        workbookObservationGapRef.current = true;
        return;
      }
      if (workbookHandoffHoldsRef.current > 0) {
        workbookObservationGapRef.current = true;
        return;
      }
      // A probe already in flight will report; nothing goes unobserved.
      if (inFlight) return;
      inFlight = true;
      try {
        const nextKey = await readWorkbookKey();
        if (cancelled || nextKey === null) return;
        setCurrentWorkbookKey((prev) => (prev === nextKey ? prev : nextKey));
      } finally {
        inFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (!document.hidden) refresh();
    };

    refresh();
    const interval = window.setInterval(refresh, WORKBOOK_KEY_POLL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [storageUserId, readWorkbookKey]);

  const handleCompleteOnboarding = useCallback(() => {
    if (storageUserId) {
      markOnboardingCompleted("excel", storageUserId);
    }
    setOnboardingDismissed(true);
  }, [storageUserId]);

  /**
   * Stable per-render lookup of the *focused* workbook's tabs.
   * Falls back to the unkeyed `""` slot until the bridge resolves
   * `currentWorkbookKey`, which always exists in `tabsByWorkbook`
   * because the state initialiser seeds it. Updating this object
   * always goes through `setTabsByWorkbook` so background-run
   * writes targeting a non-focused workbook key don't disturb the
   * visible slot.
   */
  const visibleWorkbookKey = currentWorkbookKey ?? "";
  const tabsState = tabsByWorkbook[visibleWorkbookKey] ?? createInitialTabsState();
  // Passive effects run after a render has been committed. Hide account-owned
  // state on the render where the account key changes, so the previous key's
  // transcript, Continue actions, and file selection are not committed as the
  // new key's UI. The first resolution from signed-out to signed-in is safe:
  // no prior account owned the initial in-memory slot.
  const accountScopeTransition =
    settledAccountScopeKeyRef.current !== null &&
    settledAccountScopeKeyRef.current !== accountScopeKey;
  const activeTab = accountScopeTransition
    ? undefined
    : tabsState.tabs.find((t) => t.id === tabsState.activeTabId);
  const matchingSkillBanner = useMatchingSkillBanner({
    isLoading: accountScopeTransition || Boolean(activeTab?.isLoading),
  });
  const noteSkillSubmissionRef = React.useRef(matchingSkillBanner.noteSubmission);
  noteSkillSubmissionRef.current = matchingSkillBanner.noteSubmission;
  const setSkillDecisionRef = React.useRef(matchingSkillBanner.setDecision);
  setSkillDecisionRef.current = matchingSkillBanner.setDecision;
  const dismissSkillDecisionRef = React.useRef(matchingSkillBanner.dismiss);
  dismissSkillDecisionRef.current = matchingSkillBanner.dismiss;
  // Skill selection is prompt-scoped. The reset key includes the account,
  // workbook, and tab, so a Skill chosen for one of those does not stay
  // selected for another.
  const skillsResetKey = `${accountScopeKey ?? "anonymous"}::${visibleWorkbookKey}::${activeTab?.id ?? ""}`;
  useOutputFeedbackHydration({ sessionId: activeTab?.session.id });
  const selectedDataSources = useMemo(() => {
    const storedSources = activeTab?.session.selectedDataSources;
    return resolveExcelSessionDataSources(
      availableDataSources,
      Array.isArray(storedSources) ? storedSources : undefined
    );
  }, [activeTab?.session.selectedDataSources, availableDataSources]);

  const activeCitationData = useMemo(() => {
    let merged: ExcelAgentSource[] = [];
    const citations: unknown[] = [];
    const citedValuesByUuid: Record<string, string> = {};
    for (const message of activeTab?.session.messages ?? []) {
      if (message.role !== "assistant") continue;
      if (message.retrievedSources?.length) {
        merged = mergeExcelAgentSources(merged, message.retrievedSources);
      }
      if (message.retrievedCitations?.length) {
        citations.push(...message.retrievedCitations);
      }
      for (const [id, value] of Object.entries(getMessageCitedValues(message) ?? {})) {
        if (!(id in citedValuesByUuid)) citedValuesByUuid[id] = value;
      }
    }
    if (activeTab?.isLoading && activeTab.retrievedSources?.length) {
      merged = mergeExcelAgentSources(merged, activeTab.retrievedSources);
    }
    if (activeTab?.isLoading && activeTab.retrievedCitations?.length) {
      citations.push(...activeTab.retrievedCitations);
    }
    for (const [id, value] of Object.entries(activeTab?.retrievedCitedValuesByUuid ?? {})) {
      if (!(id in citedValuesByUuid)) citedValuesByUuid[id] = value;
    }
    return { sources: merged, citations, citedValuesByUuid };
  }, [
    activeTab?.session.messages,
    activeTab?.isLoading,
    activeTab?.retrievedSources,
    activeTab?.retrievedCitations,
    activeTab?.retrievedCitedValuesByUuid,
  ]);
  const [citationPopupError, setCitationPopupError] = useState<{
    message: string;
    directHref?: string;
  } | null>(null);
  const openInlineCitation = useCallback(
    (
      rawCitations: unknown[] | undefined,
      sources: ExcelAgentSource[] | undefined,
      citedValuesByUuid: Record<string, string> | undefined,
      id: string
    ) => {
      const citations = (rawCitations ?? []).filter((citation): citation is Citation =>
        Boolean(citation && typeof citation === "object" && !Array.isArray(citation))
      );
      const sourceItem = resolveExcelInlineCitationSource(
        { sources, citations, citedValuesByUuid },
        id
      );
      setCitationPopupError(null);
      if (citations.length === 0 && (sources?.length ?? 0) > 1 && !sourceItem) {
        setCitationPopupError({
          message:
            "This older citation cannot be matched safely. Open its source from Retrieved sources.",
        });
        return;
      }
      void openCitationDialog({
        citationId: id,
        sourceItem,
        citations,
        allowIframeSameOrigin: false,
      }).then((result) => {
        if (!result.launched) {
          setCitationPopupError({
            message:
              result.reason ?? "The source popup could not be opened. Allow popups and retry.",
            directHref: result.directHref,
          });
        }
      });
    },
    []
  );
  useEffect(() => {
    setCitationPopupError(null);
    closeCitationDialog();
    return () => closeCitationDialog();
  }, [visibleWorkbookKey, activeTab?.id]);
  const showChatCitationsToggle = Boolean(activeTab && activeTab.session.messages.length > 0);
  const chatViewScope = `${visibleWorkbookKey}:${activeTab?.id ?? ""}`;
  const chatView = showChatCitationsToggle ? (chatViewByScope[chatViewScope] ?? "chat") : "chat";
  const handleChatViewChange = useCallback(
    (nextView: "chat" | "citations") => {
      setChatViewByScope((prev) =>
        prev[chatViewScope] === nextView ? prev : { ...prev, [chatViewScope]: nextView }
      );
    },
    [chatViewScope]
  );

  /**
   * Per-workbook in-flight run registry. Each entry's
   * `abortController` cancels the run for the workbook it was
   * started in (only). Workbook switches do NOT abort background
   * runs — `runQuery` started in workbook A keeps streaming
   * progress into A's chat tab even when the user has clicked into
   * workbook B. The Stop button cancels whichever run is bound to
   * the currently focused workbook; the unmount cleanup aborts all
   * of them.
   *
   * Plain `Map` (not React state) because run registration / removal
   * never needs to trigger a re-render — UI surfaces that depend on
   * "is a run in flight" (loading spinner, banner, Stop button) read
   * from `tabsByWorkbook[key].tabs.find(...).isLoading`, which IS
   * React state.
   *
   * **Declaration order matters.** `backgroundRuns` immediately
   * below reads `inFlightRunsRef.current` inside its `useMemo`
   * factory — and the factory runs synchronously during render.
   * If this declaration moves below that memo it lands in the
   * `const` temporal dead zone for the duration of the memo
   * factory, and a non-visible workbook with a loading tab (the
   * exact background-execution scenario the registry exists for)
   * trips a `ReferenceError` on first render. Keep this ref
   * declared BEFORE `backgroundRuns`.
   */
  /**
   * Coarse lifecycle phase for a background run, surfaced in the
   * "running in another workbook" banner so the user knows whether an
   * off-screen run is still working (`running`) or in its brief
   * foreground-only tail (`finalizing` — the `HarmonizedRefreshWorksheets`
   * step that must own Excel focus; see `foregroundCoordinator`). Left
   * `undefined` until the run reaches a phase worth announcing.
   */
  type RunPhase = "running" | "finalizing";
  type RunHandle = {
    abortController: AbortController;
    runWorkbookKey: string;
    /**
     * Workbook the run is pinned to. `pinnedWorkbookName` is the routing
     * key threaded onto every intent (`RangeReferenceAddress.workbookName`
     * / external-ref `destinationCell`); Excel routes by *name*, so
     * `pinnedWorkbookFullName` (full path) is carried alongside purely to
     * disambiguate in logs/UI and to detect the ambiguous case below.
     */
    pinnedWorkbookName?: string;
    pinnedWorkbookFullName?: string;
    /**
     * True when more than one open workbook shares `pinnedWorkbookName`.
     * Name-based host routing can't tell them apart (foreground activation is
     * name-based too), so we surface a one-time chat warning at pin time and a
     * banner hint asking the user to disambiguate.
     */
    pinAmbiguous?: boolean;
    phase?: RunPhase;
  };
  const inFlightRunsRef = useRef<Map<string, RunHandle>>(new Map());

  /**
   * Workbook slot holding a submit that has been drawn but not yet committed
   * to a workbook. Held back from the debounced save until the submit-time
   * probe says which workbook the run belongs to. Null whenever no submit is
   * in that window.
   */
  const optimisticSubmitSlotRef = useRef<string | null>(null);

  /**
   * The account every run and every frozen operation belongs to.
   *
   * Assigned during render so a run compares against the account key that owns
   * the pane now. The key includes company, because a user id alone would let
   * two companies share runs and sealed Skill policies.
   */
  const accountScopeKeyRef = useRef(accountScopeKey);
  accountScopeKeyRef.current = accountScopeKey;
  /**
   * Bumped once per account change. A submission captures it before its first
   * await and compares afterwards, which is what stops work begun under the
   * previous account from registering a run, committing chat state, or sealing
   * a Skill policy once the account underneath it has changed. Aborting the
   * runs below only reaches work that already registered itself.
   */
  const accountGenerationRef = useRef(0);

  useEffect(() => {
    const previousAccountScopeKey = settledAccountScopeKeyRef.current;
    if (previousAccountScopeKey === accountScopeKey) return;
    settledAccountScopeKeyRef.current = accountScopeKey;
    accountGenerationRef.current += 1;
    log("🔐 Account changed — stopping runs and clearing Skill operations.");
    inFlightRunsRef.current.forEach((handle) => handle.abortController.abort());
    inFlightRunsRef.current.clear();
    queryAbortRef.current = null;
    clearSkillOperations();
    setUserQuestionsByWorkbook({});
    // Resolving authentication for the first time in this pane is not a change
    // of owner: nothing on screen belongs to anyone else yet.
    if (previousAccountScopeKey === null) return;
    pendingWorkbookHistoryTransferRef.current = null;
    workbookIdentitySnapshotRef.current = null;
    workbookHistoryKeyRedirectsRef.current.clear();
    // Everything else in the pane is scoped to the account that produced it —
    // the transcript, pending questions, and the file selection the composer
    // reads back. Dropping every slot is what keeps them from
    // outliving their owner, and it is the only reset that reaches the
    // Office.js case where there is no workbook key to re-hydrate against.
    // The old account's history stays in its own storage scope, so signing
    // back in restores it.
    hydratedKeysRef.current = new Set([""]);
    setTabsByWorkbook({ "": createInitialTabsState() });
    setChatHistoryHydrated(false);
  }, [accountScopeKey, log]);

  useEffect(() => {
    const controller = createClientUpdateController({
      currentBuildVersion: getClientBuildVersion(),
      fetchVersion: fetchCurrentVersion,
      isBusy: () =>
        isExcelClientUpdateBusy({
          inFlightRunCount: inFlightRunsRef.current.size,
          tabsByWorkbook: tabsByWorkbookRef.current,
          userQuestionCount: Object.keys(userQuestionsByWorkbookRef.current).length,
        }),
      beforeReload: () => {
        const currentAccountScopeKey = accountScopeKeyRef.current;
        if (!currentAccountScopeKey) return;
        for (const [workbookKey, slot] of Object.entries(tabsByWorkbookRef.current)) {
          if (!workbookKey || !hydratedKeysRef.current.has(workbookKey)) continue;
          saveExcelChatTabs(
            { accountScopeKey: currentAccountScopeKey, workbookKey },
            slot
          );
        }
        const pendingTransfer = pendingWorkbookHistoryTransferRef.current;
        if (!pendingTransfer) return;
        const sourceState =
          tabsByWorkbookRef.current[pendingTransfer.sourceKey] ?? null;
        const pendingState =
          currentWorkbookKeyFallbackRef.current === pendingTransfer.targetKey
            ? tabsByWorkbookRef.current[pendingTransfer.targetKey] ?? sourceState
            : sourceState;
        if (!pendingState) return;
        saveExcelChatTabs(
          {
            accountScopeKey: currentAccountScopeKey,
            workbookKey: pendingTransfer.targetKey,
          },
          pendingState
        );
      },
      reload: (version) => {
        const url = new URL(window.location.href);
        url.searchParams.set("_build", version);
        window.location.replace(url.toString());
      },
      onUpdatePending: setClientUpdatePending,
      windowTarget: window,
      documentTarget: document,
    });
    clientUpdateControllerRef.current = controller;
    const unsubscribeUpgradeRequired = subscribeToUpgradeRequired((version) => {
      void controller.handleUpgradeRequired(version);
    });
    controller.start();

    return () => {
      unsubscribeUpgradeRequired();
      controller.stop();
      if (clientUpdateControllerRef.current === controller) {
        clientUpdateControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    clientUpdateControllerRef.current?.notifyStateChanged();
  }, [tabsByWorkbook, userQuestionsByWorkbook]);

  /**
   * List of in-flight runs on workbooks OTHER than the currently
   * focused one. Used by the "a plan is running in <other workbook>"
   * banner above the chat input to let the user know background
   * work is in progress without forcing focus back to that
   * workbook. Derived from the per-workbook `isLoading` flag (which
   * lives in React state so changes trigger re-renders), not from
   * `inFlightRunsRef` itself (which is a plain Map that wouldn't
   * propagate updates). The `pinnedWorkbookName` is read from the
   * matching run handle when available so the banner can show the
   * actual workbook name instead of an opaque key.
   */
  const backgroundRuns = useMemo(() => {
    if (accountScopeTransition) return [];
    const out: Array<{
      workbookKey: string;
      workbookName?: string;
      phase?: RunPhase;
      ambiguous?: boolean;
    }> = [];
    for (const [workbookKey, slot] of Object.entries(tabsByWorkbook)) {
      if (!workbookKey || workbookKey === visibleWorkbookKey) continue;
      const tab = slot.tabs.find((t) => t.id === slot.activeTabId);
      if (!tab?.isLoading) continue;
      const handle = inFlightRunsRef.current.get(workbookKey);
      out.push({
        workbookKey,
        workbookName: handle?.pinnedWorkbookName,
        phase: handle?.phase,
        ambiguous: handle?.pinAmbiguous,
      });
    }
    return out;
  }, [accountScopeTransition, tabsByWorkbook, visibleWorkbookKey]);

  const lastUserMessage = activeTab?.session.messages
    .slice()
    .reverse()
    .find((message) => message.role === "user");
  const lastAssistantMessage = activeTab?.session.messages
    .slice()
    .reverse()
    .find((message) => message.role === "assistant");

  useEffect(() => {
    currentWorkbookKeyFallbackRef.current = currentWorkbookKey;
  }, [currentWorkbookKey]);
  // ----- Chat-history scope: load on mount/scope-change, flush on scope change -----
  //
  // Per-workbook chats: each workbook's slot in `tabsByWorkbook` is
  // hydrated independently from `excelChatHistoryService`, keyed by
  // `(accountScopeKey, workbookKey)`. The first time the user focuses
  // a workbook, we load its tabs; subsequent switches reveal the
  // already-hydrated slot. Background runs writing into a non-
  // focused workbook stay live because their slot survives the
  // switch.
  //
  // `chatHistoryHydrated` gates the debounced save effect so an
  // empty initial slot can't clobber persisted history before the
  // hydration pass completes for the focused workbook.
  useEffect(() => {
    if (!accountScopeKey || !currentWorkbookKey) {
      setChatHistoryHydrated(false);
      return undefined;
    }
    // An account change has already emptied every cached slot by the time
    // this runs — the effect above owns that reset and is declared before
    // this one, so `hydratedKeysRef` can no longer short-circuit the load
    // for a workbook key the *previous* account hydrated. The two run in the
    // same tick, and React batches their setState calls into one re-render,
    // so the previous account's chat never makes it onto the screen.
    const liveRedirectTarget = workbookHistoryKeyRedirectsRef.current.get(
      currentWorkbookKey
    );
    if (liveRedirectTarget && liveRedirectTarget !== currentWorkbookKey) {
      workbookHistoryKeyRedirectsRef.current.delete(currentWorkbookKey);
    }
    const scope: ExcelChatScope = {
      accountScopeKey,
      workbookKey: currentWorkbookKey,
    };
    const queuedTransfer = pendingWorkbookHistoryTransferRef.current;
    const pendingTransfer =
      queuedTransfer?.targetKey === currentWorkbookKey ? queuedTransfer : null;
    const sourceState =
      pendingTransfer
        ? tabsByWorkbookRef.current[pendingTransfer.sourceKey] ??
          loadExcelChatTabs({
            accountScopeKey,
            workbookKey: pendingTransfer.sourceKey,
          })
        : null;
    const transferred = sourceState;
    const transferPersisted = transferred ? saveExcelChatTabs(scope, transferred) : false;
    if (pendingTransfer && transferred) {
      workbookHistoryKeyRedirectsRef.current.set(
        pendingTransfer.sourceKey,
        pendingTransfer.targetKey
      );
      // Submissions resolve through the redirect above from here on, so an
      // pending operation still owned by the old key would refuse its
      // clarification reply as a workbook change. The registry moves in the
      // same step the redirect is installed, never between them.
      retargetSkillOperationsWorkbook(
        pendingTransfer.sourceKey,
        pendingTransfer.targetKey
      );
    }
    if (pendingTransfer && (!transferred || transferPersisted)) {
      pendingWorkbookHistoryTransferRef.current = null;
    }
    const existingTarget =
      !transferred && !tabsByWorkbookRef.current[currentWorkbookKey]
        ? loadExcelChatTabs(scope)
        : null;
    if (!hydratedKeysRef.current.has(currentWorkbookKey) || transferred) {
      const loaded = transferred ?? existingTarget ?? loadExcelChatTabs(scope);
      setTabsByWorkbook((prev) => {
        // Save As changes the host's workbook identity, not the workbook the
        // user is working in. Its live conversation is authoritative even when
        // the destination path has stale history from an older file.
        if (transferred && pendingTransfer) {
          const remaining = { ...prev };
          if (transferPersisted) {
            delete remaining[pendingTransfer.sourceKey];
          }
          return { ...remaining, [currentWorkbookKey]: transferred };
        }
        // Storage had saved history — load it in. Submit-time workbook
        // resolution materializes and marks its target key before starting a
        // run, so that path cannot race this effect and be overwritten here.
        if (loaded) {
          return { ...prev, [currentWorkbookKey]: loaded };
        }
        // No saved history and the slot already exists in memory —
        // a concurrent `runQuery()` claimed it (the user
        // submitted their first prompt while the bridge was still
        // resolving the workbook key, so `setCurrentWorkbookKey()`
        // and the run's slot writes batched into the same render).
        // Preserve the in-progress slot; resetting to a fresh
        // `createInitialTabsState()` here would wipe the user's
        // submission AND leave the run pinned to a slot it can no
        // longer find its own tab in.
        if (prev[currentWorkbookKey]) {
          return prev;
        }
        return { ...prev, [currentWorkbookKey]: createInitialTabsState() };
      });
      hydratedKeysRef.current.add(currentWorkbookKey);
      if (transferred) {
        if (pendingTransfer) {
          if (transferPersisted) {
            hydratedKeysRef.current.delete(pendingTransfer.sourceKey);
            clearExcelChatTabs({
              accountScopeKey,
              workbookKey: pendingTransfer.sourceKey,
            });
          }
          // The chat/citations choice is per (workbook, tab). Save As renames
          // the workbook underneath a reader, so without this the citations
          // view they were on is orphaned and silently falls back to chat.
          const sourcePrefix = `${pendingTransfer.sourceKey}:`;
          setChatViewByScope((prev) => {
            let moved = false;
            const next: Record<string, "chat" | "citations"> = {};
            for (const [viewScope, view] of Object.entries(prev)) {
              if (viewScope.startsWith(sourcePrefix)) {
                next[`${currentWorkbookKey}:${viewScope.slice(sourcePrefix.length)}`] = view;
                moved = true;
              } else {
                next[viewScope] = view;
              }
            }
            return moved ? next : prev;
          });
        }
      }
    }
    setChatHistoryHydrated(true);
    return () => {
      // Same hold-back as the debounced save: a workbook switch is exactly
      // when an uncommitted submit is about to be disowned, so flushing the
      // slot here would log the prompt into the workbook being left behind.
      if (currentWorkbookKey === optimisticSubmitSlotRef.current) return;
      // Snapshot the latest persisted-state slot for the OLD scope
      // before the next setup runs. This guarantees background-run
      // updates that landed in this workbook's tab while focused
      // are flushed to storage right before we switch away.
      const snapshot = tabsByWorkbookRef.current[currentWorkbookKey];
      if (snapshot) {
        saveExcelChatTabs(scope, snapshot);
      }
    };
  }, [accountScopeKey, currentWorkbookKey]);

  // ----- Chat-history scope: debounced save on every tabsByWorkbook change -----
  //
  // 500ms debounce keeps us from pounding sessionStorage during
  // status-update bursts. Saves ALL hydrated workbook slots that
  // have a key — a background plan running in workbook A still
  // persists progress to A's storage slot even while the user is
  // looking at workbook B. Gated on `chatHistoryHydrated` so an
  // empty initial slot can't clobber persisted history before the
  // visible workbook finishes its hydration pass.
  useEffect(() => {
    if (accountScopeTransition) return undefined;
    if (!chatHistoryHydrated) return undefined;
    if (!accountScopeKey) return undefined;
    const handle = window.setTimeout(() => {
      for (const [workbookKey, slot] of Object.entries(tabsByWorkbook)) {
        if (!workbookKey) continue;
        if (!hydratedKeysRef.current.has(workbookKey)) continue;
        // An optimistic bubble is drawn against the workbook the user was
        // looking at when they hit send, which the submit-time probe can still
        // disown. Persisting it now would log the prompt into a workbook that
        // never ran it. The slot saves again once the run commits or unwinds.
        if (workbookKey === optimisticSubmitSlotRef.current) continue;
        saveExcelChatTabs({ accountScopeKey, workbookKey }, slot);
      }
      const pendingTransfer = pendingWorkbookHistoryTransferRef.current;
      let pendingState: ExcelTabsState | null = null;
      if (pendingTransfer) {
        const sourceState =
          tabsByWorkbookRef.current[pendingTransfer.sourceKey] ?? null;
        pendingState =
          currentWorkbookKey === pendingTransfer.targetKey
            ? tabsByWorkbookRef.current[pendingTransfer.targetKey] ?? sourceState
            : sourceState;
      }
      if (pendingTransfer && pendingState) {
        // A running query stays pinned to the old key until it finishes, but
        // Save As has already changed the durable workbook identity. Mirror
        // each progress snapshot to the new key so a taskpane reload can
        // recover before the final in-memory handoff.
        const persisted = saveExcelChatTabs(
          { accountScopeKey, workbookKey: pendingTransfer.targetKey },
          pendingState
        );
        if (
          persisted &&
          currentWorkbookKey === pendingTransfer.targetKey
        ) {
          pendingWorkbookHistoryTransferRef.current = null;
          retargetSkillOperationsWorkbook(
            pendingTransfer.sourceKey,
            pendingTransfer.targetKey
          );
          hydratedKeysRef.current.delete(pendingTransfer.sourceKey);
          clearExcelChatTabs({
            accountScopeKey,
            workbookKey: pendingTransfer.sourceKey,
          });
          setTabsByWorkbook((prev) => {
            const next = { ...prev, [pendingTransfer.targetKey]: pendingState };
            delete next[pendingTransfer.sourceKey];
            return next;
          });
        }
      }
    }, 500);
    return () => window.clearTimeout(handle);
  }, [
    accountScopeTransition,
    tabsByWorkbook,
    accountScopeKey,
    chatHistoryHydrated,
    currentWorkbookKey,
  ]);

  // AbortController for the in-flight runQuery run on the *focused*
  // workbook. A user-clicked Stop button (or component unmount) calls
  // .abort() on this; every fetch in the AI service / step loop
  // carries this signal, so cancellation is immediate. Null when no
  // run is in flight on the focused workbook. **Background runs in
  // other workbooks live in `inFlightRunsRef` (declared above next
  // to `backgroundRuns`) and survive a workbook switch — they are
  // NOT cancelled on switch.**
  const queryAbortRef = useRef<AbortController | null>(null);
  // Skill replacement aborts the run it is about to resubmit. That abort must
  // not paint "Run cancelled" or clear loading once the replacement owns the tab.
  const skillReplacementAbortsRef = useRef(new WeakSet<AbortController>());

  // One Skills resolution per submission id, shared by any code path that
  // needs the result. Deduping here — rather than at each call site — is what
  // guarantees a run can never observe two different Skill contexts.
  const skillResolveSingleFlightRef = useRef(createSkillResolveSingleFlight());

  // Lazy session-scoped cache of the FactSet template catalog exposed to the
  // unified agent so it can retrieve/apply templates from real names without
  // paying the host RPC on every chat.
  // `null` = uninitialised; `[]` = host returned an empty library, or
  // entitlement fetch reduced the catalog to nothing, or the fetch
  // failed best-effort; a non-empty array = ready.
  //
  // **Entitlement filter.** The cached catalog is the INTERSECTION of
  // `getTemplatesList()` (the host's full library) and
  // `getUserAvailableTemplates()` (the keys the current user is
  // actually entitled to instantiate). Doing the filter at cache-fill
  // time means the LLM never sees a template the user can't open,
  // which prevents two confusing failure modes downstream: (a) the
  // LLM picks an unentitled template and the open-template chain
  // errors mid-flight on `DownloadTemplate`, and (b) a chat reply
  // like "what templates do I have?" lists templates the user
  // doesn't actually have access to.
  //
  // Re-fetched only on the next addin reload — both lists change rarely and a
  // stale cache just means an unfindable template falls into the agent's
  // clarification path.
  const templateCatalogRef = useRef<TemplateCatalogRow[] | null>(null);
  // Cached light workbook context from the most recent `runQuery`
  // probe. The unified agent uses this as recent workbook context for
  // follow-up runs without probing Office.js during typing.
  const lastProbedWorkbookContentRef = useRef<WorkbookContent | null>(null);
  // Fetch + cache the template catalog, intersected with the
  // entitlement filter. Best-effort end-to-end:
  //   - Both host calls are fired in parallel (independent intents on
  //     the `OI Remote Iframe` relay; ~400ms savings vs serial).
  //   - If `getTemplatesList` throws, we cache `[]` and the chat
  //     continues without template-open routing.
  //   - If `getUserAvailableTemplates` throws, we fall back to the
  //     UNFILTERED catalog and warn — entitlement is advisory per the
  //     host docs (the host doesn't currently block unentitled
  //     `DownloadTemplate` calls), so showing the user the full library
  //     is preferable to hiding everything when only the entitlement
  //     read failed.
  // Returning the cached value on subsequent calls keeps every chat
  // round-trip fast.
  const ensureTemplateCatalog = useCallback(
    async (log: (msg: string) => void): Promise<TemplateCatalogRow[]> => {
      if (templateCatalogRef.current !== null) {
        return templateCatalogRef.current;
      }
      if (!shouldUseExcelIntentBridge()) {
        templateCatalogRef.current = [];
        return [];
      }

      // Local `Promise.allSettled` polyfill — FactSet Workstation's
      // embedded Chromium predates ES2020 and `Promise.allSettled` is
      // not available there (the chat throws `Promise.allSettled is
      // not a function` on the very first query). Inline it rather
      // than reaching for `core-js` so the bundle stays slim and the
      // shape downstream code already destructures from
      // (`{ status: "fulfilled", value } | { status: "rejected", reason }`)
      // is preserved bit-for-bit.
      type Settled<T> = { status: "fulfilled"; value: T } | { status: "rejected"; reason: unknown };
      const settle = <T,>(p: Promise<T>): Promise<Settled<T>> =>
        p.then(
          (value): Settled<T> => ({ status: "fulfilled", value }),
          (reason): Settled<T> => ({ status: "rejected", reason })
        );
      const [listResult, entitledResult] = await Promise.all([
        settle(getTemplatesList()),
        settle(getUserAvailableTemplates()),
      ]);

      if (listResult.status === "rejected") {
        log(
          `⚠️ getTemplatesList failed; chat will run without template-open routing: ` +
            `${listResult.reason instanceof Error ? listResult.reason.message : String(listResult.reason)}`
        );
        templateCatalogRef.current = [];
        return [];
      }

      // Defensive: drop entries with no usable `key` since `key` is the
      // sole identifier the open-template chain dispatches on. A missing
      // name is fine (the LLM can still match on description); a missing
      // key would let the LLM "succeed" classification only for the
      // chain to error on dispatch.
      const fullCatalog: TemplateCatalogRow[] = (listResult.value.templates ?? [])
        .filter((t) => typeof t.key === "string" && t.key.trim().length > 0)
        .map((t) => ({
          key: t.key,
          name: t.name,
          category: t.category,
          description: t.description,
          extendedDescription: t.extendedDescription,
          language: t.language,
          inputTypes: t.metadata?.inputs?.map((input) => input.inputType),
        }));

      let rows: TemplateCatalogRow[];
      if (entitledResult.status === "fulfilled") {
        const entitledKeys = new Set(
          (entitledResult.value.templateKeys ?? []).filter(
            (k): k is string => typeof k === "string" && k.trim().length > 0
          )
        );
        rows = fullCatalog.filter((t) => entitledKeys.has(t.key));
        const filteredOut = fullCatalog.length - rows.length;
        log(
          `📚 Cached template catalog: ${rows.length} entitled template(s) ` +
            `(filtered ${filteredOut} of ${fullCatalog.length} from the host's full library).`
        );
      } else {
        // Entitlement fetch failed — fall back to the unfiltered catalog
        // rather than hiding every template. The user will see the full
        // library; an unentitled pick will surface as a host error from
        // the open-template chain rather than silently failing here.
        rows = fullCatalog;
        log(
          `⚠️ getUserAvailableTemplates failed; chat will show the unfiltered template library: ` +
            `${entitledResult.reason instanceof Error ? entitledResult.reason.message : String(entitledResult.reason)}`
        );
        log(
          `📚 Cached template catalog: ${rows.length} template(s) (entitlement filter unavailable).`
        );
      }

      templateCatalogRef.current = rows;
      if (rows.length > 0) {
        prewarmTemplateEmbeddings(rows, log);
      }
      prewarmWorksheetTopicEmbeddings(log);
      return rows;
    },
    []
  );

  /**
   * Pre-warm the template catalog as soon as the user is authenticated.
   *
   * Without this, the catalog fetch (`getTemplatesList` +
   * `getUserAvailableTemplates`, ~700ms combined on a cold host)
   * lands on the critical path of the first prompt the user
   * submits. By kicking it off during the idle window between
   * "login complete" and "user typing their first query" we
   * absorb that cost off the user-visible latency budget. The
   * second call (inside `runQuery`) returns the cached value
   * synchronously.
   *
   * Best-effort: failures are already logged inside
   * `ensureTemplateCatalog` and leave `templateCatalogRef.current`
   * as `[]`, which the consumer paths tolerate.
   */
  useEffect(() => {
    if (!authState.isAuthenticated) return undefined;

    // Visibility gate. If the pane is visible (the common case) we
    // prewarm immediately, same as before. If it is hidden at the
    // moment of auth success, we register a single-shot
    // `visibilitychange` listener that fires the prewarm the first
    // time the pane becomes visible — and then removes itself, so
    // hide-show-hide-show cycles can never trigger the prewarm a
    // second time. `ensureTemplateCatalog` is itself idempotent (cache
    // hit on second call) but the early return keeps the effect cheap.
    if (!document.hidden) {
      ensureTemplateCatalog(log).catch(() => {
        // Surfaced inline in ensureTemplateCatalog already.
      });
      return undefined;
    }

    const onVisible = () => {
      if (document.hidden) return;
      document.removeEventListener("visibilitychange", onVisible);
      ensureTemplateCatalog(log).catch(() => {
        // Surfaced inline in ensureTemplateCatalog already.
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [authState.isAuthenticated, ensureTemplateCatalog, log]);

  /**
   * Keep the scroll container at the bottom as the agent timeline streams in,
   * while respecting a manual scroll-up so the user can read back through a run
   * that is still going.
   *
   * `followStreamedContent` is gated on the chat view because the citations view renders
   * inside this same scroll container — without the gate, a streaming run would
   * drag the citations list to the bottom under the user.
   */
  const {
    scrollContainerRef: chatScrollRef,
    scrollToBottom: scrollChatToBottom,
    showReturnToBottom: showChatReturnToBottom,
  } = useStickToBottom<HTMLDivElement>(
    [activeTab?.agentTimeline, activeTab?.loadingStatus, activeTab?.session.messages],
    { followStreamedContent: chatView === "chat" }
  );

  /**
   * Jump to the bottom when a run starts, so the user's own message is always in
   * view. Deliberately keyed on the rising edge of `isLoading` only: run
   * *completion* goes through the hook's near-bottom check instead, which is what
   * lets someone who scrolled up to watch the run stay where they are when the
   * answer lands.
   */
  const wasChatLoadingRef = useRef(false);
  const loadingTabIdRef = useRef<string | null>(null);
  useEffect(() => {
    const isLoading = activeTab?.isLoading ?? false;
    const activeTabId = activeTab?.id ?? null;
    if (activeTabId !== loadingTabIdRef.current) {
      wasChatLoadingRef.current = false;
    }
    if (isLoading && !wasChatLoadingRef.current) scrollChatToBottom();
    wasChatLoadingRef.current = isLoading;
    loadingTabIdRef.current = activeTabId;
  }, [activeTab?.id, activeTab?.isLoading, scrollChatToBottom]);

  /**
   * Chat history hydrates asynchronously once the workbook key resolves, and in
   * FactSet bridge mode the key can change under a single mounted pane. Either
   * way the scroll container keeps its old `scrollTop` and its old follow-the-
   * bottom state while entirely different chat history renders into it, so reset
   * both on every key change.
   */
  useEffect(() => {
    scrollChatToBottom();
  }, [visibleWorkbookKey, scrollChatToBottom]);

  /**
   * Imperatively update a specific workbook's `ExcelTabsState`
   * slot. Used by `runQuery` and the per-run callbacks to route
   * updates into the workbook the run was started in, even when
   * the user has switched focus to a different workbook. The
   * mutator receives the latest slot (or a fresh
   * `createInitialTabsState()` when the slot is missing — e.g.
   * the workbook was hydrated lazily after the run started) and
   * returns the next slot.
   */
  const setTabsStateFor = useCallback(
    (workbookKey: string, mutator: (prev: ExcelTabsState) => ExcelTabsState) => {
      const targetWorkbookKey = redirectedWorkbookHistoryKey(
        workbookKey,
        workbookHistoryKeyRedirectsRef.current,
        currentWorkbookKeyFallbackRef.current
      );
      setTabsByWorkbook((prev) => {
        const slot = prev[targetWorkbookKey] ?? createInitialTabsState();
        return { ...prev, [targetWorkbookKey]: mutator(slot) };
      });
    },
    []
  );

  /**
   * Synchronously claims a workbook slot for a new run. This closes the gap
   * between submit-time workbook resolution and the scope-hydration effect:
   * every run receives one stable active-tab ID before any async work starts.
   */
  const materializeTabsStateForRun = useCallback(
    (workbookKey: string, provisionalWorkbookKey?: string): ExcelTabsState => {
      const targetWorkbookKey = redirectedWorkbookHistoryKey(
        workbookKey,
        workbookHistoryKeyRedirectsRef.current,
        currentWorkbookKeyFallbackRef.current
      );
      const existing = tabsByWorkbookRef.current[targetWorkbookKey];
      if (existing) return existing;

      const loaded =
        accountScopeKey && targetWorkbookKey
          ? loadExcelChatTabs({
              accountScopeKey,
              workbookKey: targetWorkbookKey,
            })
          : null;
      const provisional =
        provisionalWorkbookKey !== undefined
          ? tabsByWorkbookRef.current[provisionalWorkbookKey]
          : undefined;
      const slot = loaded ?? provisional ?? createInitialTabsState();

      tabsByWorkbookRef.current = {
        ...tabsByWorkbookRef.current,
        [targetWorkbookKey]: slot,
      };
      if (accountScopeKey && targetWorkbookKey) {
        hydratedKeysRef.current.add(targetWorkbookKey);
      }
      setTabsByWorkbook((prev) =>
        prev[targetWorkbookKey]
          ? prev
          : { ...prev, [targetWorkbookKey]: slot }
      );
      return slot;
    },
    [accountScopeKey]
  );

  /**
   * Backwards-compatible drop-in for `setTabsState(prev => ...)`
   * usage from UI-driven call sites that always target the
   * *focused* workbook. Plan-loop call sites should use
   * `setTabsStateFor(runWorkbookKey, ...)` directly so background
   * progress lands in the right workbook's slot.
   */
  const setTabsState = useCallback(
    (updater: React.SetStateAction<ExcelTabsState>) => {
      setTabsStateFor(visibleWorkbookKey, (prev) =>
        typeof updater === "function"
          ? (updater as (p: ExcelTabsState) => ExcelTabsState)(prev)
          : updater
      );
    },
    [setTabsStateFor, visibleWorkbookKey]
  );

  /**
   * Update a specific workbook's active tab. Used by `runQuery`
   * to route per-step status / message updates into the workbook
   * the run was started in, even when the user has switched focus
   * to a different workbook mid-plan. `workbookKey` should be the
   * value captured at `runQuery` start (the run's
   * `pinnedWorkbookKey`).
   */
  const updateTab = useCallback(
    (workbookKey: string, updates: Partial<ExcelTab>) => {
      setTabsStateFor(workbookKey, (slot) => ({
        ...slot,
        tabs: slot.tabs.map((t) => (t.id === slot.activeTabId ? { ...t, ...updates } : t)),
      }));
    },
    [setTabsStateFor]
  );

  /**
   * Convenience: update the active tab of the *focused* workbook
   * (whichever workbook the user is currently looking at). Used by
   * UI-driven actions (toolbar buttons, clear / undo, chart insert)
   * that always target the visible chat. Plan-loop updates should
   * use `updateTab(runWorkbookKey, ...)` instead so background
   * progress lands in the right workbook's slot.
   */
  const updateActiveTab = useCallback(
    (updates: Partial<ExcelTab>) => {
      updateTab(visibleWorkbookKey, updates);
    },
    [updateTab, visibleWorkbookKey]
  );

  const handleUserFileIdsChange = useCallback(
    (fileIds: string[]) => {
      const selection = selectExcelUserFiles(selectedDataSources, fileIds);

      setTabsState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId
            ? {
                ...tab,
                session: {
                  ...tab.session,
                  selectedDataSources: selection.dataSources,
                  selectedUserFileIds: selection.userFileIds,
                  researchSelectionMode: resolveExcelLiveResearchSelectionMode(
                    selection,
                    tab.session.researchSelectionMode
                  ),
                  updatedAt: Date.now(),
                },
              }
            : tab
        ),
      }));
    },
    [selectedDataSources, setTabsState]
  );

  const handleDataSourcesChange = useCallback(
    (dataSources: FinsterQueryDataSource[]) => {
      setTabsState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) => {
          if (tab.id !== prev.activeTabId) return tab;
          const selection = selectExcelDataSources(
            tab.session.selectedUserFileIds ?? [],
            dataSources
          );
          const isUserFilesOnlySelection =
            selection.userFileIds.length > 0 && selection.dataSources.length === 0;
          return {
            ...tab,
            session: {
              ...tab.session,
              selectedDataSources: selection.dataSources,
              selectedUserFileIds: selection.userFileIds,
              researchSelectionMode: isUserFilesOnlySelection ? "user_files" : "filtered",
              updatedAt: Date.now(),
            },
          };
        }),
      }));
    },
    [setTabsState]
  );

  /**
   * Run-progress UI setters. These live on each tab (per workbook)
   * rather than as component-level state so background runs in
   * non-focused workbooks update only THEIR workbook's chat —
   * never the visible workbook's step counter, plan banner, or completion
   * toast.
   *
   * The component-scope wrappers below target the *focused*
   * workbook (used by user-driven handlers — chart inserts, template actions,
   * retry buttons). Inside `runQuery` and `handleMessageAction` these names
   * are intentionally shadowed so the same call sites route to that run's
   * workbook instead of the currently-visible one.
   */
  const setCurrentIteration = useCallback(
    (next: number) => updateActiveTab({ currentIteration: next }),
    [updateActiveTab]
  );
  const setCurrentPlan = useCallback(
    (next: ExecutionPlan | null) => updateActiveTab({ currentPlan: next }),
    [updateActiveTab]
  );
  const setResult = useCallback(
    (next: { success: boolean; message: string } | null) => updateActiveTab({ result: next }),
    [updateActiveTab]
  );

  // Stop button: aborts the run on the currently FOCUSED workbook
  // (not any background run on a different workbook). The signal
  // flows into every fetchWithRetry call via callProviderForJSON,
  // so any currently-pending AI request is cancelled immediately;
  // the outer catch in runQuery surfaces the abort with partial
  // step results preserved.
  const handleCancelQuery = useCallback(() => {
    const focused = inFlightRunsRef.current.get(visibleWorkbookKey);
    if (focused) {
      log(
        `🛑 User requested cancel for workbook "${focused.pinnedWorkbookName ?? visibleWorkbookKey}"`
      );
      focused.abortController.abort();
    }
    dismissSkillDecisionRef.current();
  }, [log, visibleWorkbookKey]);

  // Best-effort cleanup: if the component unmounts mid-run (tab
  // close, navigation), abort every pending run across every
  // workbook so we don't leak network calls.
  useEffect(() => {
    const runs = inFlightRunsRef.current;
    return () => {
      runs.forEach((handle) => {
        try {
          handle.abortController.abort();
        } catch {
          /* best-effort abort; ignore */
        }
      });
      runs.clear();
      // The registry is module-level, so resolved guidance would otherwise
      // outlive the pane that resolved it and greet the next mount.
      clearSkillOperations();
    };
  }, []);

  const runQuery = useCallback(
    async (query: string, options: HandleQueryOptions, submission: ExcelSubmissionHandoff) => {
      const runBrandId: ExcelBrandId | null =
        activeBrandId !== null && brandStyle === activeBrandId ? activeBrandId : null;
      const submittedWorkbookKey = visibleWorkbookKey;
      // Ownership is frozen here, ahead of every await, alongside the research
      // selection captured below. Reading the account after the workbook probe
      // would attribute this request to whatever account happened to be signed
      // in by the time the probe returned.
      const submissionAccountScopeKey = accountScopeKeyRef.current ?? null;
      const submissionAccountGeneration = accountGenerationRef.current;
      const submissionRegistryGeneration = getSkillOperationsGeneration();
      const ownsAccount = () => accountGenerationRef.current === submissionAccountGeneration;
      const submittedTabsState = tabsByWorkbookRef.current[submittedWorkbookKey];
      const submittedTab = submittedTabsState?.tabs.find(
        (tab) => tab.id === submittedTabsState.activeTabId
      );
      if (!submittedTab) {
        submission.refuse("no_active_chat");
        return;
      }
      const submittedResearchSelection =
        captureExcelSubmittedResearchSelection(
          availableDataSources,
          options.userFileIds === undefined
            ? submittedTab.session
            : {
                ...submittedTab.session,
                selectedUserFileIds: options.userFileIds,
              }
        );

      // ── IMMEDIATE THINKING (synchronous, before any await) ───────────────────
      // Mirror PowerPoint's useQueryHandler: commit the user bubble + loading
      // state BEFORE `resolveWorkbookKey()`. Without this, QueryBar clears the
      // input and the chat shows nothing for up to WORKBOOK_KEY_PROBE_TIMEOUT_MS
      // (1.5s). Write both the React state and `tabsByWorkbookRef` so
      // `materializeTabsStateForRun` below sees the optimistic slot on the same
      // tick (the ref is otherwise only synced in an effect).
      //
      // Hoisted so the auto-retarget relocation path can reference the same
      // ExcelChatMessage instance by id.
      let userMessage: ExcelChatMessage | null = null;
      let optimisticRunHandle: RunHandle | null = null;
      // True only while we're still in the pre-probe / early-exit window.
      // Cleared once the run is committed to `runWorkbookKey` so a late
      // Cancel goes through the normal abort path instead of double-unwinding.
      let optimisticPhaseActive = false;

      const writeWorkbookSlot = (workbookKey: string, nextSlot: ExcelTabsState) => {
        tabsByWorkbookRef.current = {
          ...tabsByWorkbookRef.current,
          [workbookKey]: nextSlot,
        };
        setTabsByWorkbook((prev) => ({
          ...prev,
          [workbookKey]: nextSlot,
        }));
      };

      const patchSubmittedTab = (patch: (tab: ExcelTab) => ExcelTab) => {
        if (!submittedTab) return;
        // Never restore the submit-time snapshot after account/workbook state
        // has removed its slot. That snapshot belongs to the scope that
        // accepted the submit, not necessarily the scope currently rendered.
        const currentSlot = tabsByWorkbookRef.current[submittedWorkbookKey];
        if (!currentSlot) return;
        writeWorkbookSlot(submittedWorkbookKey, {
          ...currentSlot,
          tabs: currentSlot.tabs.map((tab) =>
            tab.id === submittedTab.id ? patch(tab) : tab
          ),
        });
      };

      // Let the held-back slot persist again. The re-render re-arms the
      // debounced save, which would otherwise not fire until the run's next
      // state write.
      const releaseOptimisticSubmitSlot = () => {
        optimisticPhaseActive = false;
        if (optimisticSubmitSlotRef.current !== submittedWorkbookKey) return;
        optimisticSubmitSlotRef.current = null;
        setTabsByWorkbook((prev) => ({ ...prev }));
      };

      const unwindOptimisticSubmit = () => {
        if (!userMessage || !submittedTab || !submittedTabsState) return;
        releaseOptimisticSubmitSlot();
        const messageId = userMessage.id;
        patchSubmittedTab((tab) => ({
          ...tab,
          isLoading: false,
          loadingStatus: "",
          session: {
            ...tab.session,
            messages: tab.session.messages.filter((msg) => msg.id !== messageId),
            updatedAt: Date.now(),
          },
        }));
        if (
          optimisticRunHandle &&
          inFlightRunsRef.current.get(optimisticRunHandle.runWorkbookKey) ===
            optimisticRunHandle
        ) {
          inFlightRunsRef.current.delete(optimisticRunHandle.runWorkbookKey);
        }
        if (
          optimisticRunHandle &&
          queryAbortRef.current === optimisticRunHandle.abortController
        ) {
          queryAbortRef.current = null;
        }
      };

      if (submittedTabsState) {
        // Abort any prior handle on this slot BEFORE writing the new bubble so
        // an optimistic abort listener cannot unwind the commit we are about to
        // make.
        inFlightRunsRef.current.get(submittedWorkbookKey)?.abortController.abort();

        userMessage = {
          id: generateId(),
          role: "user",
          content: query,
          timestamp: Date.now(),
        };
        const appendedUserMessage = userMessage;
        patchSubmittedTab((tab) => ({
          ...tab,
          isLoading: true,
          loadingStatus: "",
          session: {
            ...tab.session,
            messages: [...tab.session.messages, appendedUserMessage],
            updatedAt: Date.now(),
          },
        }));

        // Register the abort handle on the submitted slot NOW so Stop during
        // the probe window still works. Re-keyed to `resolvedRunWorkbookKey`
        // after the probe settles.
        const abortController = new AbortController();
        optimisticRunHandle = {
          abortController,
          runWorkbookKey: submittedWorkbookKey,
          pinnedWorkbookName: undefined,
          phase: "running",
        };
        inFlightRunsRef.current.set(submittedWorkbookKey, optimisticRunHandle);
        queryAbortRef.current = abortController;
        optimisticPhaseActive = true;
        optimisticSubmitSlotRef.current = submittedWorkbookKey;
        // Cancel during a slow/hung probe must clear the optimistic UI
        // immediately — waiting for the probe to settle would leave the
        // Thinking panel visible until WORKBOOK_KEY_PROBE_TIMEOUT_MS.
        abortController.signal.addEventListener(
          "abort",
          () => {
            if (optimisticPhaseActive) unwindOptimisticSubmit();
          },
          { once: true }
        );
      }

      // Resolve the workbook key BEFORE we capture `runWorkbookKey`.
      // This is not polling: it is one bounded
      // workbook-identity lookup per user-submitted prompt. It catches
      // workbook switches that happened while the pane was idle and also
      // avoids the disappearing-run bug from pinning a first run to the
      // `""` sentinel before workbook identity resolves.
      let resolvedWorkbookKey: string | null = currentWorkbookKey;
      try {
        resolvedWorkbookKey = (await resolveWorkbookKey()) ?? currentWorkbookKey;
      } catch {
        resolvedWorkbookKey = currentWorkbookKey;
      }
      if (resolvedWorkbookKey == null) {
        resolvedWorkbookKey = currentWorkbookKey;
      }

      // User cancelled during the probe window — drop the optimistic bubble
      // and abort handle before any run-scoped work begins.
      if (optimisticRunHandle?.abortController.signal.aborted) {
        unwindOptimisticSubmit();
        return;
      }
      // The probe is the submission's first suspension point. Signing in as
      // someone else during it would otherwise leave this request holding the
      // previous account's Skill selection. The optimistic bubble is already
      // on screen, so it has to be unwound with the refuse.
      if (!ownsAccount()) {
        unwindOptimisticSubmit();
        submission.refuse("account_changed_during_submit", { retryable: false });
        return;
      }

      const resolvedRunWorkbookKey = redirectedWorkbookHistoryKey(
        resolvedWorkbookKey ?? "",
        workbookHistoryKeyRedirectsRef.current,
        resolvedWorkbookKey
      );
      const submittedHistoryKey = redirectedWorkbookHistoryKey(
        submittedWorkbookKey,
        workbookHistoryKeyRedirectsRef.current,
        resolvedWorkbookKey
      );

      let runTabsState = materializeTabsStateForRun(
        resolvedRunWorkbookKey,
        submittedWorkbookKey === "" ? "" : undefined
      );

      // The empty key is a provisional slot for the workbook whose identity is
      // still resolving, not a different workbook. Keep loaded chat history but
      // carry the exact source/file scope attached to the submitted prompt.
      if (
        submittedResearchSelection &&
        submittedWorkbookKey === "" &&
        resolvedRunWorkbookKey !== ""
      ) {
        runTabsState = applyExcelResearchSelectionToActiveTab(
          runTabsState,
          submittedResearchSelection
        );
        writeWorkbookSlot(resolvedRunWorkbookKey, runTabsState);
      }

      if (
        userMessage &&
        submittedTab &&
        submittedWorkbookKey === "" &&
        resolvedRunWorkbookKey !== ""
      ) {
        const nextSlots = carryInFlightSubmitToResolvedSlot(
          tabsByWorkbookRef.current,
          submittedWorkbookKey,
          resolvedRunWorkbookKey,
          submittedTab.id,
          userMessage.id
        );
        tabsByWorkbookRef.current = nextSlots;
        setTabsByWorkbook((prev) => ({
          ...prev,
          [submittedWorkbookKey]: nextSlots[submittedWorkbookKey] ?? prev[submittedWorkbookKey],
          [resolvedRunWorkbookKey]:
            nextSlots[resolvedRunWorkbookKey] ?? prev[resolvedRunWorkbookKey],
        }));
        runTabsState = nextSlots[resolvedRunWorkbookKey] ?? runTabsState;
      }

      const runScopedTab = runTabsState.tabs.find((tab) => tab.id === runTabsState.activeTabId);
      if (!runScopedTab) {
        unwindOptimisticSubmit();
        submission.refuse("no_active_chat");
        return;
      }

      if (
        shouldDeferExcelRunAfterWorkbookSwitch(
          submittedHistoryKey,
          resolvedRunWorkbookKey,
          false
        )
      ) {
        // Drop the optimistic bubble + spinner from the submitted slot so the
        // resolved workbook's deferral messages are the single source of truth.
        unwindOptimisticSubmit();
        const submittedAt = Date.now();
        // The prompt itself is deliberately not echoed into this transcript.
        // Refusing leaves it in the composer, so logging it here would show it
        // twice the moment the user follows the instruction below.
        const scopeChangedMessage =
          "Excel switched workbooks while a prompt was being submitted, so " +
          "nothing was run. The prompt is still in the composer — review this " +
          "workbook's source selection and send it again.";
        setTabsStateFor(resolvedRunWorkbookKey, (slot) => {
          // The run's slot is materialized off `tabsByWorkbookRef`, which lags
          // a slot that hydration has only queued into state. When it does,
          // the run's tab ID is absent here and the notice would land nowhere,
          // so address the slot's own active tab — the one on screen.
          const noticeTabId = slot.tabs.some((tab) => tab.id === runTabsState.activeTabId)
            ? runTabsState.activeTabId
            : slot.activeTabId;
          return {
            ...slot,
            tabs: slot.tabs.map((tab) =>
              tab.id === noticeTabId
                ? {
                    ...tab,
                    session: {
                      ...tab.session,
                      messages: [
                        ...tab.session.messages,
                        {
                          id: generateId(),
                          role: "assistant",
                          content: scopeChangedMessage,
                          timestamp: submittedAt,
                        },
                      ],
                      updatedAt: submittedAt,
                    },
                  }
                : tab
            ),
          };
        });
        // Nothing started, so the composer keeps the prompt, its Skill chips
        // and its file scope for the resend the message above asks for.
        submission.refuse("workbook_changed_during_submit");
        return;
      }

      // Provisional "" → real key (or any non-deferral key change): move the
      // optimistic abort handle onto the resolved slot so Cancel keeps working
      // for the rest of the run.
      if (
        optimisticRunHandle &&
        submittedWorkbookKey !== resolvedRunWorkbookKey &&
        inFlightRunsRef.current.get(submittedWorkbookKey) === optimisticRunHandle
      ) {
        inFlightRunsRef.current.delete(submittedWorkbookKey);
        inFlightRunsRef.current.get(resolvedRunWorkbookKey)?.abortController.abort();
        optimisticRunHandle.runWorkbookKey = resolvedRunWorkbookKey;
        inFlightRunsRef.current.set(resolvedRunWorkbookKey, optimisticRunHandle);
      }
      // Past the probe / early-exit window: Cancel must tear down the run via
      // the normal abort path, not strip the committed user bubble.
      releaseOptimisticSubmitSlot();

      const activeTab = runScopedTab;
      const submittedRunResearchContext: ExcelSubmittedResearchSelection =
        submittedResearchSelection ??
        captureExcelSubmittedResearchSelection(availableDataSources, runScopedTab.session);
      const analyticsStartedAt = Date.now();
      trackQuerySubmitted({
        mode: "excel",
        hasContext: Boolean(resolvedWorkbookKey),
      });
      let queryRunOutcomeTracked = false;
      // Declared ahead of the outcome tracker below, which closes over them and
      // can fire on early-exit paths before Skills resolution is even started.
      let skillBootstrapResult: SkillTurnBootstrapResult | undefined;
      let skillRunContext: UserSkillRunContext | undefined;
      let skillResolveStartedAt: number | null = null;
      let skillResolveSettledAt: number | null = null;
      const buildRunSkillsAnalytics = () => {
        if (!skillBootstrapResult) return undefined;
        const diagnostics = skillBootstrapResult.resolution;
        const resolvedContext = skillRunContext ?? skillBootstrapResult.context;
        return buildQuerySkillsAnalytics({
          selectionPath:
            resolvedContext?.selectionSource ??
            (skillBootstrapResult.mode === "explicit" ? "explicit" : "none"),
          status: skillBootstrapResult.status,
          origins: resolvedContext?.skills.map((skill) => skill.origin),
          skillCount: resolvedContext?.skills.length ?? diagnostics?.skillCount ?? 0,
          policyVersion: resolvedContext?.skillPolicyVersion ?? diagnostics?.skillPolicyVersion,
          resolveLatencyMs:
            skillResolveStartedAt === null
              ? undefined
              : (skillResolveSettledAt ?? Date.now()) - skillResolveStartedAt,
          fallback: skillBootstrapResult.status === "fail-open" ? true : undefined,
          effectiveSourceCount:
            resolvedContext?.effectiveDataSources?.length ??
            diagnostics?.effectiveSourceCount ??
            undefined,
          effectiveFileCount:
            resolvedContext?.effectiveExplorerContext.userFileIds?.length ??
            diagnostics?.effectiveFileCount ??
            undefined,
          guidanceChars: resolvedContext?.guidance.length ?? diagnostics?.guidanceChars,
        });
      };
      let langfuseRunContext: ExcelLangfuseRunContext | undefined = createExcelLangfuseRunContext({
        mode: "excel",
        hasContext: Boolean(resolvedWorkbookKey),
        sessionId: activeTab.session.id,
      });
      const trackExcelQueryOutcome = (params: {
        status: "completed" | "failed" | "cancelled";
        errorType?: string;
        needsClarification?: boolean;
        workbookEditAttempted?: boolean;
        workbookEditSuccess?: boolean;
        operationCount?: number;
        affectedSheetCount?: number;
      }) => {
        if (queryRunOutcomeTracked) return;
        queryRunOutcomeTracked = true;
        const skills = buildRunSkillsAnalytics();
        if (langfuseRunContext) {
          langfuseRunContext.skills = toLangfuseRunSkills(
            (skillRunContext ?? skillBootstrapResult?.context)?.skills ?? []
          );
        }
        trackQueryRun({
          mode: "excel",
          hasContext: Boolean(resolvedWorkbookKey),
          durationMs: Date.now() - analyticsStartedAt,
          status: params.status,
          errorType: params.errorType,
          needsClarification: params.needsClarification,
          workbookEditAttempted: params.workbookEditAttempted,
          workbookEditSuccess: params.workbookEditSuccess,
          operationCount: params.operationCount,
          affectedSheetCount: params.affectedSheetCount,
        });
        if (skills) {
          postExcelLangfuseObservation(langfuseRunContext, {
            name: "excel.skills.outcome",
            metadata: { ...skills, status: params.status },
            ...(params.status === "failed" ? { level: "ERROR" as const } : undefined),
            endTime: new Date().toISOString(),
          });
        }
        postExcelLangfuseOutcome(langfuseRunContext, {
          status: params.status,
          durationMs: Date.now() - analyticsStartedAt,
          errorType: params.errorType,
          needsClarification: params.needsClarification,
          workbookEditAttempted: params.workbookEditAttempted,
          workbookEditSuccess: params.workbookEditSuccess,
          operationCount: params.operationCount,
          affectedSheetCount: params.affectedSheetCount,
        });
      };
      const trackExcelQuerySuccess = (
        params: {
          needsClarification?: boolean;
          workbookEditAttempted?: boolean;
          workbookEditSuccess?: boolean;
          operationCount?: number;
          affectedSheetCount?: number;
        } = {}
      ) => {
        trackExcelQueryOutcome({ status: "completed", ...params });
      };
      const trackExcelQueryFailure = (
        errorType: string,
        params: {
          needsClarification?: boolean;
          workbookEditAttempted?: boolean;
          workbookEditSuccess?: boolean;
          operationCount?: number;
          affectedSheetCount?: number;
        } = {}
      ) => {
        trackExcelQueryOutcome({
          status: errorType === "cancelled" ? "cancelled" : "failed",
          errorType,
          ...params,
        });
      };
      // Pin the run to the workbook it was started in. Every state
      // update inside this function — progress messages, status,
      // streaming thinking, partial plan result, final assistant
      // message — targets `runWorkbookKey` rather than whatever
      // workbook the user happens to be focused on at the moment the
      // update lands. This is what keeps a multi-step plan running
      // in workbook A streaming progress into A's chat even after
      // the user clicks into workbook B.
      //
      // Prompts fall back to `""` (the no-workbook slot) only
      // when the submit-time probe couldn't surface a key either:
      // Office.js mode without bridge identity, or a wedged probe that
      // hit the 1.5s timeout.
      let runWorkbookKey = resolvedRunWorkbookKey;
      const userQuestionOwner = generateId();
      // Shadow the outer focused-workbook setters with run-targeted
      // versions so the rest of this function (including all the
      // `setTabsState(prev => ...)` and `updateActiveTab(...)` calls
      // below) routes into the pinned workbook's slot without any
      // per-call-site changes.
      //
      // They are also the run's owner gate. Aborting a run on account change
      // stops its next request, not the settlement it is already in the middle
      // of: without this check a cancelled account-A run would still append its
      // cancellation notice or result toast to a pane now showing account B.
      const setTabsState = (mutator: (prev: ExcelTabsState) => ExcelTabsState) => {
        if (!ownsAccount()) return;
        setTabsStateFor(runWorkbookKey, mutator);
      };
      const updateActiveTab = (updates: Partial<ExcelTab>) => {
        if (!ownsAccount()) return;
        updateTab(runWorkbookKey, updates);
      };
      // Shadow the run-progress setters too — same trick as `updateActiveTab`
      // above. Without these, the component-scope wrappers would target the
      // focused workbook and a background plan would scribble its progress onto
      // whatever workbook the user is currently looking at.
      const setCurrentIteration = (next: number) => updateActiveTab({ currentIteration: next });
      const setCurrentPlan = (next: ExecutionPlan | null) => updateActiveTab({ currentPlan: next });
      const setResult = (next: { success: boolean; message: string } | null) =>
        updateActiveTab({ result: next });
      const runTabId = runTabsState?.activeTabId ?? activeTab.id;

      // ── OPERATION CONTINUITY ─────────────────────────────────────────────────
      // One request and the native clarification replies that answer it are a
      // single operation under one frozen context. Everything below keys off
      // the operation id so a reply reuses what the operation started with.
      const pendingOperation = activeTab.pendingOperation;
      const skillSubmissionSnapshot = snapshotSkillSubmissionOptions(options.skills);
      const runOwner: ExcelOperationOwner = {
        accountScopeKey: submissionAccountScopeKey,
        workbookKey: runWorkbookKey,
        sessionId: activeTab.session.id,
        tabId: runTabId,
      };
      // Touching the Skill picker — choosing Skills or explicitly clearing them
      // — is the only way to change policy mid-conversation. A reply that
      // re-sends the same selection is still the same operation; only a real
      // change replaces it, so two policies can never mix into one operation.
      const claimedOperationId = pendingOperation?.continuationId;
      const claimedRecord = readSkillOperation(claimedOperationId);
      const skillSelectionReplaced =
        skillSubmissionSnapshot.selectionProvided &&
        !sameSkillSelection(claimedRecord?.skillSelection, skillSubmissionSnapshot);
      // A native clarification reply carries the operation id and whether it
      // was Skill-bound, so it is validated before any provider work.
      const continuationClaim =
        pendingOperation && !skillSelectionReplaced
          ? {
              operationId: pendingOperation.continuationId,
              skillBound: pendingOperation.skillBound,
              accountScopeKey: pendingOperation.accountScopeKey,
            }
          : undefined;
      // An account, workbook, or chat change means this is no longer the
      // conversation the operation was frozen against, so the reply must be
      // refused rather than silently downgraded to the current context.
      const claimOutcome = continuationClaim
        ? validateExcelOperationClaim({
            claim: continuationClaim,
            owner: runOwner,
            record: claimedRecord,
          })
        : ({ kind: "accepted" } as const);

      if (claimOutcome.kind === "refused") {
        const refusal = claimOutcome.message;
        log(`⚠️ ${refusal}`);
        // Nothing was traced for this run, so it must not post an outcome either.
        langfuseRunContext = undefined;
        // A refusal the user can undo by going back to the workbook or chat the
        // operation belongs to keeps its binding; anything else can never be
        // continued, so nothing may keep claiming it.
        if (!claimOutcome.retryable) {
          releaseSkillContinuation(continuationClaim?.operationId);
        }
        // Drop the optimistic bubble so refusal does not echo it beside the warning.
        unwindOptimisticSubmit();
        const refusedAt = Date.now();
        setTabsState((prev) => ({
          ...prev,
          tabs: prev.tabs.map((tab) =>
            tab.id === runTabId
              ? {
                  ...tab,
                  isLoading: false,
                  // A retryable refusal keeps its binding in the registry, so
                  // the clarification it belongs to has to survive too — the
                  // message above tells the user to come back and answer it,
                  // and dropping this is what would make that impossible.
                  pendingOperation: claimOutcome.retryable
                    ? tab.pendingOperation
                    : undefined,
                  session: {
                    ...tab.session,
                    messages: [
                      ...tab.session.messages.filter(
                        (msg) => msg.id !== userMessage?.id
                      ),
                      {
                        id: generateId(),
                        role: "assistant",
                        content: `⚠️ ${refusal}`,
                        timestamp: refusedAt,
                      },
                    ],
                    updatedAt: refusedAt,
                  },
                }
              : tab
          ),
        }));
        setResult({ success: false, message: refusal });
        trackExcelQueryFailure(
          claimOutcome.reason === "skill_binding_lost" ? "skills_blocked" : claimOutcome.reason
        );
        submission.refuse(claimOutcome.reason, { retryable: claimOutcome.retryable });
        return;
      }
      const frozenOperation = claimOutcome.record;
      // A clarification reply runs under the operation's frozen research
      // allowlist. Re-reading it between turns would let a source the user
      // toggled change the scope of work already in progress.
      const runResearchContext: NonNullable<ToolCallContext["researchContext"]> =
        frozenOperation?.researchSelection ?? submittedRunResearchContext;
      // An explicit new selection replaces the operation, so the one it
      // replaced is abandoned here rather than left holding its policy.
      if (pendingOperation && !continuationClaim) {
        releaseSkillContinuation(pendingOperation.continuationId);
      }

      // When a clarification is pending, the stored query is the real request; the
      // user's follow-up reply ("yes", "sure", "do it", "the first one", …) is
      // only a routing hint and must not replace the original query.
      const pendingIntentQuery = pendingOperation?.originalQuery;
      const effectiveQuery = pendingIntentQuery ?? query;
      // Started here so the read overlaps the run's setup; the executor call awaits it.
      const savedContextPromise = fetchSavedContext(log);
      // For the agent: rejoin the user's clarification reply onto the
      // original request when a pending question is being answered. The
      // agent sees the resolved company ("DCF" → "DCF\n\n[User
      // clarification reply: \"Apple\"]") so the second plan can emit
      // `applyTemplate.companyIdentifier: "AAPL-US"` instead of asking
      // again. The naked `effectiveQuery` (still the original "DCF") is
      // intentionally preserved for chat-history labels and step-context
      // strings.
      const agentQuery =
        pendingIntentQuery && pendingIntentQuery !== query
          ? `${effectiveQuery}\n\n[User clarification reply: "${query}"]`
          : effectiveQuery;

      // ── OPERATION ADMISSION ──────────────────────────────────────────────────
      // A clarification reply keeps the original operation's id so it reuses
      // that record; a fresh prompt — including a reply that replaced the
      // Skill selection — starts a new one and freezes its own context.
      //
      // Admission comes before the run is registered, before anything is
      // traced, and before the transcript is touched, because a request that
      // cannot be frozen must not be half-started: the composer has to be left
      // exactly as the user left it.
      const skillContinuationId = continuationClaim?.operationId ?? createSkillContinuationId();
      const inheritedSkillRunContext = frozenOperation?.skillContext;
      if (
        !frozenOperation &&
        !openSkillOperation(
          {
            operationId: skillContinuationId,
            owner: runOwner,
            originalQuery: effectiveQuery,
            skillSelection: skillSubmissionSnapshot,
            researchSelection: submittedRunResearchContext,
          },
          // The registry is module-level, so the ref check above cannot speak
          // for it. Handing back the generation captured at submit is what
          // stops this call from re-populating a registry that was cleared
          // while the submission was still resolving its workbook.
          { expectedGeneration: submissionRegistryGeneration }
        )
      ) {
        // Without a record there is no frozen policy, and a run with no frozen
        // policy is the silent Skill loss this registry exists to prevent.
        // Either the account changed under this submission or the registry is
        // at its safety ceiling; both are recoverable by sending again.
        const refusal = EXCEL_OPERATION_ADMISSION_REFUSAL;
        log(`⚠️ ${refusal}`);
        // Nothing was traced for this run, so it must not post an outcome either.
        langfuseRunContext = undefined;
        setResult({ success: false, message: refusal });
        trackExcelQueryFailure("operation_admission_refused");
        unwindOptimisticSubmit();
        submission.refuse("start_failed", { retryable: true });
        return;
      }

      // Cancel any prior in-flight run on THIS workbook before starting
      // a new one. Background runs on OTHER workbooks are left alone —
      // they continue streaming progress into their own workbook's tab.
      // Fresh prompts already registered an optimistic handle before the
      // probe; reuse it so Cancel during that window stays authoritative.
      const priorHandle = inFlightRunsRef.current.get(runWorkbookKey);
      if (priorHandle && priorHandle !== optimisticRunHandle) {
        priorHandle.abortController.abort();
      }
      const abortController =
        optimisticRunHandle?.abortController ?? new AbortController();
      const runHandle: RunHandle = optimisticRunHandle ?? {
        abortController,
        runWorkbookKey,
        // `pinnedWorkbookName` is filled in after the probe resolves
        // it; until then a "Stop" click can still abort because the
        // abort controller is already wired up.
        pinnedWorkbookName: undefined,
        phase: "running",
      };
      runHandle.runWorkbookKey = runWorkbookKey;
      inFlightRunsRef.current.set(runWorkbookKey, runHandle);
      // Mirror the focused-workbook run into `queryAbortRef` so the
      // existing "is something running on the focused workbook?" UI
      // (loading toast, Stop button) keeps working without a separate
      // subscription mechanism. The unmount + handleCancelQuery
      // codepaths now both go through `inFlightRunsRef`, so this is
      // purely a back-compat surface for the visible-state checks.
      queryAbortRef.current = abortController;
      const signal = abortController.signal;

      // End-to-end run wall-clock for the success-criteria bars (comps / builds
      // p95 < 2-3 min). Recorded in the `finally` so every exit path (success,
      // chat, abort, error) is captured.
      const runStartedAt = Date.now();

      // ── SKILLS RESOLUTION (one per operation) ────────────────────────────────
      // Kicked off here, before the workbook reads, and awaited just before the
      // executor starts, so resolution latency hides behind work we already do.
      // Only the operation's first turn resolves. A clarification reply inherits
      // the policy the first turn sealed.
      const skillSubmissionId = createSkillSubmissionId();
      // Whether this turn leaves an unanswered native clarification. The run
      // tail acts on this in one place and defaults to releasing, so a new exit
      // path cannot leave a frozen Skill policy alive with nothing able to
      // reach it.
      let operationRemainsResumable = false;
      skillResolveStartedAt = Date.now();
      const skillBootstrapPromise = skillResolveSingleFlightRef.current.resolveSkillsOnce({
        submissionId: skillSubmissionId,
        userQuery: agentQuery,
        dataSources: runResearchContext.dataSources,
        userFileIds: runResearchContext.userFileIds,
        userFileTags: [],
        // A clarification reply inherits; only a submission that starts an
        // operation hands the picker's state to the resolver. Re-sending the
        // operation's own Skills is a reply, not a new selection.
        ...(continuationClaim ? {} : { selection: skillSubmissionSnapshot }),
        // A clarification reply reuses the original prompt's context, including
        // an inherited "no Skill" state, rather than re-resolving against a
        // catalog that may have changed since the operation started.
        ...(continuationClaim
          ? {
              hasInheritedContext: true,
              ...(inheritedSkillRunContext ? { inheritedContext: inheritedSkillRunContext } : {}),
            }
          : {}),
        signal,
      });
      const markSkillResolveSettled = () => {
        skillResolveSettledAt ??= Date.now();
      };
      noteSkillSubmissionRef.current(skillSubmissionId);
      void skillBootstrapPromise.then(markSkillResolveSettled, markSkillResolveSettled);

      // Trace the same resolved prompt the agent receives. Start concurrently so
      // the request cannot delay the synchronous loading affordance below.
      const langfuseRunStart = langfuseRunContext
        ? startExcelLangfuseRun(langfuseRunContext, { query: agentQuery })
        : Promise.resolve();
      setResult(null);
      setCurrentIteration(0);
      setCurrentPlan(null);

      log(`📝 User query: "${query}"`);
      // The request is owned from here: its operation is frozen, its run is
      // registered and abortable, and its loading state and user message are
      // committed. Everything after this point belongs to the run, so the
      // composer is released now rather than minutes from now when the plan
      // finishes.
      submission.accept();

      // Loading state and the submitted message are now committed. Waiting here
      // preserves trace parentage without delaying either user-visible update.
      await langfuseRunStart;
      const sessionTabId = runTabId;
      const snapshotHistory = () =>
        snapshotChatHistoryForRun(
          tabsByWorkbookRef.current[runWorkbookKey],
          sessionTabId,
          userMessage?.id
        );

      // Hoisted out of the try so the outer catch can include any partial
      // progress (completed steps + plan) in the user-visible error message
      // instead of showing just the raw "Load failed". A mid-run network
      // hiccup on step N should not amnesia steps 1…N-1.
      let partialPlan: ExecutionPlan | null = null;
      const partialStepResults: string[] = [];
      const partialStepDisplayResults: string[] = [];
      const partialStepStatuses: ExcelStepStatus[] = [];
      let workbookEditAttempted = false;
      let latestAgentTimeline = emptyExcelAgentTimeline();

      try {
        // Gather a lightweight workbook probe (active sheet, sheet names,
        // cursor) before the native run. The native harness reads workbook
        // detail through tools rather than an eager all-sheet extract.
        log(`👁️ Probing workbook…`);

        const probe = await timePhase("probe", () => gatherProbeContext());
        const workbookContent: WorkbookContent = workbookContentFromProbe(probe);
        if (langfuseRunContext) {
          langfuseRunContext.workbook = summarizeWorkbookForLangfuse(workbookContent);
        }
        // Stash the light probe-derived workbook content for follow-up runs.
        // It is refreshed on every submitted query as the user navigates between
        // workbooks.
        lastProbedWorkbookContentRef.current = workbookContent;

        // Pin every host read for this run at the workbook the user
        // started the plan on. `pinnedWorkbookName` is derived from
        // the probe (which itself reads the *currently active*
        // workbook); if the user switches workbooks after this point
        // every extract/apply still targets the original workbook.
        // `undefined` falls back to "active workbook" — happens only
        // in Office.js mode (no bridge) or when the probe couldn't
        // resolve a name, in which case workbook-routing isn't
        // available anyway.
        let pinnedWorkbookName = probe.workbookName;
        runHandle.pinnedWorkbookName = pinnedWorkbookName;
        if (pinnedWorkbookName) {
          log(`📌 Pinning run to workbook "${pinnedWorkbookName}"`);
          // Capture the pin's full path for identity/logging, and check
          // whether any OTHER open workbook shares this name. Both the
          // background route (`RangeReferenceAddress.workbookName` /
          // external-ref `destinationCell`) AND a foreground
          // `activateWorksheet` key on the workbook *name*, so two
          // same-named workbooks open at once are genuinely ambiguous at the
          // host level — there's no reliable programmatic disambiguation.
          // The honest mitigation is to warn the user once and let them
          // disambiguate. Metadata-only + best-effort: a failed probe never
          // blocks the run (we just lose the hint).
          try {
            const openWorkbooks = await getOpenWorkbooks();
            const sameName = openWorkbooks.filter((w) => w.name === pinnedWorkbookName);
            runHandle.pinnedWorkbookFullName =
              sameName.find((w) => w.isActive)?.fullName ?? sameName[0]?.fullName;
            if (sameName.length > 1) {
              runHandle.pinAmbiguous = true;
              log(
                `⚠️ ${sameName.length} open workbooks are named "${pinnedWorkbookName}". ` +
                  `Edits target a workbook by name and can't tell them apart, so this run ` +
                  `may write to the wrong "${pinnedWorkbookName}". To be safe, keep the ` +
                  `intended workbook focused, or rename/close the duplicate.`
              );
            }
          } catch {
            // Metadata probe failed — fall back to name-only routing.
          }
        }

        log(
          `📊 Probed ${probe.sheetNames.length} sheet(s); deferring per-sheet cell reads until needed.`
        );

        let templateToolContext: ToolCallContext = {};

        const buildTemplateToolContext = (
          catalog: typeof templateCatalogRef.current
        ): ToolCallContext =>
          catalog && catalog.length > 0
            ? {
                templateContext: {
                  catalog,
                  rankTemplateCandidates: (query, templates, topK, signal) =>
                    rankTemplateCandidates(query, templates, topK, signal, langfuseRunContext),
                  openTemplate: openTemplateInExcel,
                  canOpenTemplates: () => shouldUseExcelIntentBridge(),
                  workbookHasIdentifierName: async () => {
                    if (!shouldUseExcelIntentBridge()) return false;
                    try {
                      const ctx = await getActiveExcelContext();
                      const workbookName = ctx.workbookName?.trim();
                      if (!workbookName) return false;
                      const namedObjects = await getWorkbookNamedObjects({
                        workbookName,
                        skipHidden: false,
                      });
                      return namedObjectsIncludeIdentifier(namedObjects);
                    } catch {
                      return false;
                    }
                  },
                },
              }
            : {};

        // Lazy template catalog: defer the host RPC until the unified agent path
        // needs template context. The load is memoized by
        // `templateCatalogRef.current` and pre-warmed in the background on auth,
        // so the first real use is usually already a cache hit.
        let templateCatalog: TemplateCatalogRow[] | null = null;
        const ensureTemplateToolContext = async (): Promise<TemplateCatalogRow[]> => {
          if (templateCatalog === null) {
            templateCatalog = await timePhase("template_catalog", () => ensureTemplateCatalog(log));
            templateToolContext = buildTemplateToolContext(templateCatalog);
          }
          return templateCatalog;
        };

        await ensureTemplateToolContext();

        log("🧠 Running unified Excel agent without an external plan.");

        // Sheet-lock fallback for plans whose `PlanStep`s omit `targetSheet`
        // (legacy / hand-edited plans). The pre-apply re-activate inside the
        // loop uses `step.targetSheet || sessionLockSheet` to pin the live
        // active sheet right before each `applyWorkbookModifications` call —
        // see the "Lock multi-step plan execution to its target sheet"
        // change. `getActiveSheetName()` is the dual-mode dispatcher (bridge
        // first, Office.js fallback); on internal bridge failure it returns
        // `""` rather than throwing, so the try/catch here is defensive
        // (e.g. Excel context unavailable). A `""` lock plus a `PlanStep`
        // without `targetSheet` makes the activate inside the loop a no-op,
        // preserving today's behaviour.
        let sessionLockSheet = "";
        if (!sessionLockSheet) {
          try {
            sessionLockSheet = await getActiveSheetName();
          } catch (e) {
            console.warn("[Excel] Could not capture plan-start active sheet for lock fallback:", e);
          }
        }
        let runResult: ExecutorRunResult;
        let skipFinalVerificationReason: string | null = null;
        let completedAgentTimeline = emptyExcelAgentTimeline();

        // ── Native ReAct executor ───────────────────────────────────────────
        {
          log("🔄 Native ReAct executor enabled — using metadata-first workbook context.");
          updateActiveTab({
            agentTimeline: emptyExcelAgentTimeline(),
            retrievedSources: [],
            retrievedCitations: [],
            retrievedCitedValuesByUuid: {},
          });
          let liveRetrievedSources: NonNullable<ExcelTab["retrievedSources"]> = [];
          let liveRetrievedCitations: NonNullable<ExcelTab["retrievedCitations"]> = [];
          let liveRetrievedCitedValuesByUuid: NonNullable<ExcelTab["retrievedCitedValuesByUuid"]> =
            {};
          let agentTimelineFlushHandle: number | null = null;
          let agentTimelineFlushClosed = false;
          const flushAgentTimeline = () => {
            if (agentTimelineFlushHandle !== null) {
              window.cancelAnimationFrame(agentTimelineFlushHandle);
            }
            agentTimelineFlushHandle = null;
            if (agentTimelineFlushClosed) return;
            updateActiveTab({
              agentTimeline: capExcelAgentTimeline(latestAgentTimeline),
              retrievedSources: liveRetrievedSources,
              retrievedCitations: liveRetrievedCitations,
              retrievedCitedValuesByUuid: liveRetrievedCitedValuesByUuid,
            });
          };
          const scheduleAgentTimelineFlush = () => {
            if (agentTimelineFlushClosed) return;
            if (agentTimelineFlushHandle !== null) return;
            agentTimelineFlushHandle = window.requestAnimationFrame(flushAgentTimeline);
          };
          const closeAgentTimelineFlush = () => {
            agentTimelineFlushClosed = true;
            if (agentTimelineFlushHandle !== null) {
              window.cancelAnimationFrame(agentTimelineFlushHandle);
              agentTimelineFlushHandle = null;
            }
          };
          const reasoningNarrator = createExcelExecutorReasoningNarrator({
            userQuery: agentQuery,
            signal,
            onNarrationDelta: (text, iteration) => {
              if (agentTimelineFlushClosed) return;
              latestAgentTimeline = reduceExcelAgentEvent(latestAgentTimeline, {
                type: "reasoning_delta",
                iteration,
                text,
              });
              scheduleAgentTimelineFlush();
            },
            onError: (error) => {
              console.warn("[Excel] Reasoning narrator did not complete:", error);
            },
          });

          const baseRunToolCallContext: ToolCallContext = {
            ...templateToolContext,
            researchContext: {
              ...runResearchContext,
              manualUserFileIds: [...runResearchContext.userFileIds],
              skillTargetFileIds: [],
            },
            chatSessionId: activeTab.session.id,
            langfuseContext: langfuseRunContext,
          };

          // ── SKILLS: settle once, then freeze for the rest of the run ──────────
          if (skillResolveSettledAt === null) {
            updateActiveTab({ loadingStatus: skillLoadingStatus("resolving") });
          }
          const skillTurn = await settleExcelUserSkillTurn(skillBootstrapPromise);
          if (!ownsAccount()) {
            abortController.abort();
            throw new DOMException("Aborted", "AbortError");
          }
          skillBootstrapResult = skillTurn.result;
          const skillResolveAnalytics = buildRunSkillsAnalytics();
          if (skillResolveAnalytics) {
            postExcelLangfuseObservation(langfuseRunContext, {
              name: "excel.skills.resolve",
              metadata: { ...skillResolveAnalytics },
              ...(skillBootstrapResult.status === "blocked"
                ? { level: "ERROR" as const }
                : undefined),
              ...(skillResolveStartedAt === null
                ? undefined
                : { startTime: new Date(skillResolveStartedAt).toISOString() }),
              endTime: new Date(skillResolveSettledAt ?? Date.now()).toISOString(),
            });
          }
          if (signal.aborted || skillTurn.outcome.kind === "aborted") {
            // Settle through the run's normal cancel path. Returning here would
            // skip it and leave the tab spinning on a run nobody is driving.
            if (skillTurn.outcome.kind === "aborted") abortController.abort();
            throw new DOMException("Aborted", "AbortError");
          }
          if (skillTurn.outcome.kind === "blocked") {
            // Fail closed: the user asked for specific Skills and we cannot prove
            // which guidance applies, so nothing is sent upstream.
            throw new ExcelSkillsBlockedError(skillTurn.outcome.message);
          }
          setSkillDecisionRef.current(
            skillSubmissionId,
            skillBootstrapResult.skillDecision ?? null,
            query
          );
          if (skillBootstrapResult.status === "fail-open") {
            // Automatic inference is allowed to drop a Skill when the resolver is
            // unreachable, but a run that silently loses its guidance is
            // indistinguishable from one where nothing matched.
            log(
              `⚠️ Skill lookup unavailable (${skillBootstrapResult.failure?.kind ?? "unknown"}); continuing without a Skill.`
            );
          }
          skillRunContext = skillTurn.outcome.context;
          sealSkillOperationPolicy(skillContinuationId, skillRunContext);
          if (skillRunContext) {
            log(
              `🧠 Skill applied: ${skillRunContext.skills.map((skill) => skill.name).join(", ")}`
            );
            // References only. This lands in persisted message state, so it must
            // never carry guidance.
            latestAgentTimeline = withActiveUserSkills(
              latestAgentTimeline,
              skillRunContext.skills.map((skill) => ({
                id: skill.id,
                name: skill.name,
                slug: skill.slug,
                origin: skill.origin,
              }))
            );
            scheduleAgentTimelineFlush();
          }
          updateActiveTab({ loadingStatus: skillLoadingStatus("settled") });
          // One immutable context for the whole run. Every later rebuild of the
          // tool context spreads this base, so continuations inherit it too.
          let skillAwareToolCallContext = baseRunToolCallContext;
          let skillTargetFilesAttached = true;
          if (skillRunContext) {
            const skillScope = applyUserSkillRunContextToToolCallContext(
              skillRunContext,
              baseRunToolCallContext
            );
            if (skillScope.status === "blocked") {
              // Fail closed: the user chose this Skill, and a run that quietly
              // leaves some of its target files behind reads as a complete one.
              throw new ExcelSkillsBlockedError(skillScope.message);
            }
            skillAwareToolCallContext = skillScope.toolCallContext;
            if (skillScope.omittedTargetCount > 0) {
              skillTargetFilesAttached = false;
              log(
                `⚠️ Skill target files not attached: ${skillScope.omittedTargetCount} file(s) would exceed the per-request limit. Continuing without them.`
              );
            }
          }
          const skillPreludeMessages = skillRunContext
            ? buildUserSkillPreludeMessages(skillRunContext, {
                targetFilesAttached: skillTargetFilesAttached,
              })
            : undefined;
          // Composer selection strips the `/slug` the user typed, so the agent
          // is handed the request with those tokens back in place and resolves
          // the bundled modeling guidance from them itself.
          const skillAwareQuery = withSkillSlashTokens(agentQuery, skillRunContext);

          const savedContext = await savedContextPromise;
          const multiHopResult: ExecutorUnifiedRunResult = await timePhase("executor", () =>
            runNativeReactExecutor({
              brandId: runBrandId,
              userRequest: skillAwareQuery,
              workbookContext: JSON.stringify(workbookContent, null, 2),
              workbookSnapshot: workbookContent,
              chatHistory: snapshotHistory(),
              sessionLockSheet,
              pinnedWorkbookName,
              applyWorkbookModifications: async (args) => {
                await reactivateSheetBeforeApply(
                  args.modifications,
                  args.targetSheet || sessionLockSheet,
                  args.reason,
                  args.pinnedWorkbookName
                );
                return applyWorkbookModifications(
                  args.modifications,
                  (op) => {
                    log(`   ${op}`);
                  },
                  {
                    signal,
                    pinnedWorkbookName: args.pinnedWorkbookName,
                    skipFactSetFormatNormalization: args.skipFactSetFormatNormalization,
                    ...hostReadyWaitCallbacks((loadingStatus) =>
                      updateActiveTab({ loadingStatus })
                    ),
                  }
                );
              },
              beginWorkbookHandoff: () => {
                workbookHandoffHoldsRef.current += 1;
                let released = false;
                return () => {
                  if (released) return;
                  released = true;
                  workbookHandoffHoldsRef.current -= 1;
                };
              },
              onWorkbookHandoff: async (handoff) => {
                if (signal.aborted) {
                  throw new DOMException("Aborted", "AbortError");
                }
                let targetWorkbookKey = handoff.targetWorkbookName;
                const observedWorkbookKey = await readWorkbookKey();
                if (
                  observedWorkbookKey &&
                  workbookDisplayNameFromKey(observedWorkbookKey).toLowerCase() ===
                    workbookDisplayNameFromKey(handoff.targetWorkbookName).toLowerCase()
                ) {
                  targetWorkbookKey = observedWorkbookKey;
                }
                if (!targetWorkbookKey.trim()) {
                  throw new Error("The activated workbook did not provide a chat-history key.");
                }
                // The conversation is about to live under this key, so the workbook
                // poll has to resolve the new workbook to the same one when it
                // resumes. Left to its own probe it may spell it differently — a
                // path where this took a bare name, or the reverse — and move the
                // pane to an empty slot while this run reports into the old one.
                rememberWorkbookKey(workbookKeyAliasesRef.current, targetWorkbookKey);

                const sourceWorkbookKey = runWorkbookKey;
                // Relocation writes the chat map directly rather than through
                // the guarded run setters, so it needs the same owner check.
                if (!ownsAccount()) return;
                hydratedKeysRef.current.add(targetWorkbookKey);
                tabsByWorkbookRef.current = relocateExcelRunTab(
                  tabsByWorkbookRef.current,
                  sourceWorkbookKey,
                  targetWorkbookKey,
                  runTabId
                );
                setTabsByWorkbook((prev) => {
                  const next = relocateExcelRunTab(
                    prev,
                    sourceWorkbookKey,
                    targetWorkbookKey,
                    runTabId
                  );
                  tabsByWorkbookRef.current = next;
                  return next;
                });

                if (inFlightRunsRef.current.get(sourceWorkbookKey) === runHandle) {
                  inFlightRunsRef.current.delete(sourceWorkbookKey);
                }
                runWorkbookKey = targetWorkbookKey;
                runHandle.runWorkbookKey = targetWorkbookKey;
                // The operation moves with the run, so its own resume — which
                // will carry the target key — still matches its owner.
                rebindSkillOperationWorkbook(skillContinuationId, targetWorkbookKey);
                runHandle.pinnedWorkbookName = handoff.targetWorkbookName;
                inFlightRunsRef.current.set(targetWorkbookKey, runHandle);
                pinnedWorkbookName = handoff.targetWorkbookName;
                if (handoff.activeSheetName?.trim()) {
                  sessionLockSheet = handoff.activeSheetName.trim();
                }
                setCurrentWorkbookKey(targetWorkbookKey);
                log(`📌 Moved this conversation to workbook "${handoff.targetWorkbookName}".`);
              },
              onPlanStateChange: (planState) => {
                const livePlan =
                  planState.steps.length > 0
                    ? {
                        summary: planState.summary,
                        estimatedComplexity: planState.estimatedComplexity ?? "moderate",
                        steps: planState.steps.map((s) => s.planStep),
                      }
                    : null;
                setCurrentPlan(livePlan);
                partialPlan = livePlan;
                const activeStepIndex = planState.currentStepId
                  ? planState.steps.findIndex((s) => s.id === planState.currentStepId)
                  : -1;
                const terminalStepCount = planState.steps.filter(
                  (s) =>
                    s.status === "success" ||
                    s.status === "warning" ||
                    s.status === "failed" ||
                    s.status === "recovered" ||
                    s.status === "paused"
                ).length;
                updateActiveTab({
                  currentIteration:
                    activeStepIndex >= 0
                      ? activeStepIndex + 1
                      : Math.min(terminalStepCount + 1, planState.steps.length),
                });
              },
              onProgressSnapshot: (snapshot) => {
                partialPlan = snapshot.plan;
                partialStepResults.length = 0;
                partialStepResults.push(...snapshot.stepResults);
                partialStepDisplayResults.length = 0;
                partialStepDisplayResults.push(...snapshot.stepDisplayResults);
                partialStepStatuses.length = 0;
                partialStepStatuses.push(...snapshot.stepStatuses);
                workbookEditAttempted = snapshot.workbookEditAttempted;
              },
              onReasoningDelta: (text, iteration) => {
                reasoningNarrator.addReasoningDelta(text, iteration);
              },
              answerUserQuestion: (question) =>
                new Promise<UserQuestionAnswer>((resolve) => {
                  setUserQuestionsByWorkbook((current) => ({
                    ...current,
                    [runWorkbookKey]: {
                      owner: userQuestionOwner,
                      question,
                      resolve,
                    },
                  }));
                }),
              onEvent: (event: ExecutorRunEvent) => {
                if (agentTimelineFlushClosed) return;
                if (event.type === "tool_call_started") {
                  reasoningNarrator.recordToolEvent(event.name, "calling", event.iteration);
                } else if (event.type === "tool_call_completed") {
                  const eventIteration =
                    event.iteration ??
                    latestAgentTimeline.hops[latestAgentTimeline.hops.length - 1]?.hop ??
                    1;
                  reasoningNarrator.recordToolEvent(
                    event.name,
                    event.status === "error" ? "failed" : "completed",
                    eventIteration
                  );
                }
                latestAgentTimeline = reduceExcelAgentEvent(latestAgentTimeline, event);
                scheduleAgentTimelineFlush();
                if (event.type === "iteration_start") {
                  log(`⚡ Executor iteration ${event.iteration}`);
                } else if (event.type === "tool_call_started") {
                  log(`🔧 ${event.name}(${JSON.stringify(event.args)})`);
                  if (isDiagEnabled()) {
                    log(`   ↳ input ${event.name}: ${formatDiagnosticPayload(event.args)}`);
                    console.log("[Finster][tool input]", {
                      name: event.name,
                      callId: event.callId,
                      iteration: event.iteration,
                      input: event.args,
                    });
                  }
                } else if (event.type === "tool_call_completed") {
                  const marker = event.status === "error" ? "✗" : "✓";
                  log(`  ${marker} ${event.name} (${event.elapsedMs}ms): ${event.result}`);
                  if (isDiagEnabled()) {
                    log(`   ↳ output ${event.name}: ${formatDiagnosticPayload(event.result)}`);
                    console.log("[Finster][tool output]", {
                      name: event.name,
                      callId: event.callId,
                      elapsedMs: event.elapsedMs,
                      status: event.status,
                      output: parseDiagnosticPayload(event.result),
                      rawOutput: event.result,
                    });
                  }
                  if (event.name === "factset_ai_query" || event.name === "finster_query") {
                    const newSources = extractSourcesFromCitations(event.researchCitations);
                    if (newSources.length > 0) {
                      liveRetrievedSources = mergeExcelAgentSources(
                        liveRetrievedSources,
                        newSources
                      );
                    }
                    if (Array.isArray(event.researchCitations)) {
                      liveRetrievedCitations = [
                        ...liveRetrievedCitations,
                        ...event.researchCitations,
                      ];
                    }
                    if (event.researchCitedValuesByUuid) {
                      liveRetrievedCitedValuesByUuid = {
                        ...liveRetrievedCitedValuesByUuid,
                        ...event.researchCitedValuesByUuid,
                      };
                    }
                    if (
                      newSources.length > 0 ||
                      Array.isArray(event.researchCitations) ||
                      event.researchCitedValuesByUuid
                    ) {
                      scheduleAgentTimelineFlush();
                    }
                  }
                } else if (event.type === "status") {
                  log(`  ℹ️ ${event.message}`);
                }
              },
              signal,
              toolCallContext: skillAwareToolCallContext,
              ...(skillPreludeMessages ? { preludeUserMessages: skillPreludeMessages } : {}),
              savedContext,
              withSignal: (init: RequestInit) => {
                if (signal) {
                  return { ...init, signal };
                }
                return init;
              },
              probeActiveContext: async () => {
                const ctx = await getActiveExcelContext();
                return { workbookName: ctx?.workbookName };
              },
              bridgeAvailable: shouldUseExcelIntentBridge(),
              citationNoteCapability: shouldUseExcelIntentBridge()
                ? getFactSetSpreadsheetNoteCapability()
                : "unsupported",
              excelTabularQuery: authState.features?.excelTabularQuery
                ? {
                    enabled: authState.features.excelTabularQuery.enabled,
                    columnMaxInFlightBatches:
                      authState.features.excelTabularQuery.columnMaxInFlightBatches ?? 1,
                    cellMaxInFlightSingles:
                      authState.features.excelTabularQuery.cellMaxInFlightSingles ?? 1,
                  }
                : undefined,
              refreshFactSetData: async (opts) => {
                const start = performance.now();
                try {
                  let gate = await waitForHostReady(PATIENT_HOST_READY_SCHEDULE);
                  gate = await escalateToPatientWait(gate, signal, (loadingStatus) =>
                    updateActiveTab({ loadingStatus })
                  );
                  if (gate.reason === "stuck_edit" || gate.reason === "stuck_busy") {
                    return {
                      ok: false,
                      refreshed: false,
                      scope: "active",
                      refreshedSheetNames: [],
                      scopeConfirmed: false,
                      elapsedMs: Math.round(performance.now() - start),
                      message: `Host busy (${gate.reason}); refresh deferred.`,
                      error: gate.reason,
                    };
                  }
                  // Active-workbook-only refresh (see `finalizeRefreshAndRecalc`):
                  // route through the foreground coordinator so the pin is brought
                  // forward for the refresh and the user's focus is restored after
                  // — serialised across concurrent runs. No-pin paths run inline.
                  const requestedSheetNames =
                    opts.sheetNames && opts.sheetNames.length > 0
                      ? opts.sheetNames
                      : [opts.fallbackSheet ?? sessionLockSheet];
                  const refreshResult = await foregroundCoordinator.runInForeground(
                    {
                      workbookName: opts.pinnedWorkbookName,
                      sheet: requestedSheetNames[0],
                    },
                    async () => {
                      if (opts.pinnedWorkbookName) {
                        const active = await getActiveExcelContext();
                        const activeWorkbookName = active?.workbookName?.trim() ?? "";
                        const expectedWorkbookName = workbookDisplayNameFromKey(
                          opts.pinnedWorkbookName
                        ).trim();
                        const observedWorkbookName =
                          workbookDisplayNameFromKey(activeWorkbookName).trim();
                        if (
                          !activeWorkbookName ||
                          observedWorkbookName.toLowerCase() !== expectedWorkbookName.toLowerCase()
                        ) {
                          throw new Error(
                            `Refusing to refresh workbook "${activeWorkbookName || "(unknown)"}"; ` +
                              `the run is pinned to "${expectedWorkbookName}".`
                          );
                        }
                      }
                      if (opts.refreshAll) {
                        return harmonizedRefreshAllWorksheetsViaIntents(
                          opts.pinnedWorkbookName,
                          undefined,
                          FACTSET_WORKSHEET_REFRESH_TIMEOUT_MS,
                        );
                      }
                      return harmonizedRefreshWorksheetsSequentiallyViaIntents(
                        requestedSheetNames,
                        undefined,
                        FACTSET_WORKSHEET_REFRESH_TIMEOUT_MS,
                      );
                    },
                    { contextLabel: "mid-run FactSet refresh" }
                  );
                  const scope = opts.refreshAll
                    ? ("all" as const)
                    : opts.sheetNames && opts.sheetNames.length > 0
                      ? ("sheets" as const)
                      : ("active" as const);
                  const failedSheetNames = refreshResult.failedSheetNames ?? [];
                  const refreshComplete =
                    refreshResult.scopeConfirmed && failedSheetNames.length === 0;
                  return {
                    ok: refreshComplete,
                    refreshed: refreshResult.refreshedSheetNames.length > 0,
                    scope,
                    sheetNames: refreshResult.refreshedSheetNames,
                    refreshedSheetNames: refreshResult.refreshedSheetNames,
                    failedSheetNames,
                    worksheetResults: refreshResult.worksheetResults,
                    scopeConfirmed: refreshResult.scopeConfirmed,
                    elapsedMs: Math.round(performance.now() - start),
                    message: refreshComplete
                      ? `FactSet data refreshed sequentially (${scope}).`
                      : refreshResult.message ||
                        `FactSet data refresh was incomplete (${scope}).`,
                    ...(refreshComplete
                      ? {}
                      : {
                          error:
                            failedSheetNames.length > 0
                              ? `Failed worksheets: ${failedSheetNames.join(", ")}`
                              : "Explicit worksheet scope was not confirmed.",
                        }),
                  };
                } catch (err: unknown) {
                  if (err instanceof DOMException && err.name === "AbortError") {
                    throw err;
                  }
                  return {
                    ok: false,
                    refreshed: false,
                    scope: "active" as const,
                    refreshedSheetNames: [],
                    scopeConfirmed: false,
                    elapsedMs: Math.round(performance.now() - start),
                    message: "FactSet refresh failed.",
                    error: String(err),
                  };
                }
              },
              recalculateWorkbook: async (opts) => {
                const start = performance.now();
                const mode = opts.mode ?? "full";
                try {
                  let gate = await waitForHostReady(PATIENT_HOST_READY_SCHEDULE);
                  gate = await escalateToPatientWait(gate, signal, (loadingStatus) =>
                    updateActiveTab({ loadingStatus })
                  );
                  if (gate.reason === "stuck_edit" || gate.reason === "stuck_busy") {
                    return {
                      ok: false,
                      recalculated: false,
                      mode,
                      elapsedMs: Math.round(performance.now() - start),
                      message: `Host busy (${gate.reason}); recalc deferred.`,
                      error: gate.reason,
                    };
                  }
                  // `ApplicationCalculate` is application-level (f2_docs: "not
                  // workbook specific") — it recalculates every open workbook
                  // regardless of which is active. No pin activation or focus
                  // preservation needed, so this stays fully in the background and
                  // never disturbs the user's view.
                  await applicationCalculate(mode);
                  return {
                    ok: true,
                    recalculated: true,
                    mode,
                    elapsedMs: Math.round(performance.now() - start),
                    message: `Workbook recalculated (${mode}).`,
                  };
                } catch (err: unknown) {
                  if (err instanceof DOMException && err.name === "AbortError") {
                    throw err;
                  }
                  return {
                    ok: false,
                    recalculated: false,
                    mode,
                    elapsedMs: Math.round(performance.now() - start),
                    message: "Workbook recalculation failed.",
                    error: String(err),
                  };
                }
              },
              langfuseContext: langfuseRunContext,
            })
          ).finally(async () => {
            await reasoningNarrator.finalize();
            flushAgentTimeline();
            closeAgentTimelineFlush();
          });

          const liveCompletedAgentTimeline = capExcelAgentTimeline(latestAgentTimeline);
          const persistedAgentTimeline =
            typeof multiHopResult.reply === "string"
              ? capExcelAgentTimeline(
                  withoutDuplicateTerminalAssistantText(
                    latestAgentTimeline,
                    multiHopResult.reply,
                  ),
                )
              : liveCompletedAgentTimeline;
          updateActiveTab({
            agentTimeline: liveCompletedAgentTimeline,
            retrievedSources: liveRetrievedSources,
            retrievedCitations: liveRetrievedCitations,
            retrievedCitedValuesByUuid: liveRetrievedCitedValuesByUuid,
          });
          completedAgentTimeline = persistedAgentTimeline;
          if (!("plan" in multiHopResult)) {
            const assistantMessage: ExcelChatMessage = {
              id: generateId(),
              role: "assistant",
              content: multiHopResult.reply,
              timestamp: Date.now(),
              thinking: persistedAgentTimeline.hops.length ? undefined : multiHopResult.thinking,
              agentTimeline: persistedAgentTimeline,
              retrievedSources: multiHopResult.retrievedSources,
              retrievedCitations: multiHopResult.retrievedCitations,
              retrievedCitedValuesByUuid: multiHopResult.retrievedCitedValuesByUuid,
            };
            // An unanswered question keeps the operation open: the reply that
            // arrives next must run under this operation's frozen policy, not a
            // freshly inferred one.
            const clarificationPending = multiHopResult.mode === "clarification";
            operationRemainsResumable = clarificationPending;
            setTabsState((prev) => ({
              ...prev,
              tabs: prev.tabs.map((t) =>
                t.id === runTabId
                  ? {
                      ...t,
                      isLoading: false,
                      pendingOperation: clarificationPending
                        ? {
                            continuationId: skillContinuationId,
                            originalQuery: pendingIntentQuery ?? effectiveQuery,
                            skillBound: Boolean(skillRunContext),
                            accountScopeKey: runOwner.accountScopeKey,
                          }
                        : undefined,
                      agentTimeline: undefined,
                      retrievedSources: undefined,
                      retrievedCitations: undefined,
                      retrievedCitedValuesByUuid: undefined,
                      session: {
                        ...t.session,
                        messages: [...t.session.messages, assistantMessage],
                        updatedAt: Date.now(),
                      },
                    }
                  : t
              ),
            }));
            setResult({
              success: multiHopResult.mode !== "failed",
              message:
                multiHopResult.mode === "clarification"
                  ? "Needs clarification"
                  : "Answered in chat",
            });
            setCurrentIteration(0);
            setCurrentPlan(null);
            trackExcelQueryOutcome({
              status: multiHopResult.mode === "failed" ? "failed" : "completed",
              errorType: multiHopResult.mode === "failed" ? "agent_failed" : undefined,
              workbookEditAttempted: false,
            });
            return;
          }

          runResult = multiHopResult;
          skipFinalVerificationReason = multiHopResult.skipFinalVerificationReason ?? null;
          if (multiHopResult.finalPinnedWorkbookName) {
            pinnedWorkbookName = multiHopResult.finalPinnedWorkbookName;
          }
          workbookEditAttempted = multiHopResult.workbookEditAttempted;

          // Sync partial-progress arrays so the abort-safety path has data
          partialStepResults.length = 0;
          partialStepResults.push(...multiHopResult.stepResults);
          partialStepDisplayResults.length = 0;
          partialStepDisplayResults.push(...multiHopResult.stepDisplayResults);
          partialStepStatuses.length = 0;
          partialStepStatuses.push(...multiHopResult.stepStatuses);
        }

        // ── Executor run result ─────────────────────────────────────────
        // The native executor produces the normalized `ExecutorRunResult`
        // directly, so the finalize + chat-message tail
        // below is executor-agnostic.

        // Single end-of-run refresh + recalc pair. Routes both the
        // FDS-write auto-detection (`fdsRefreshSheets`) and deferred
        // deferred `refreshFactSetData` / `recalculate` workbook ops
        // through one patient-gated call — see `finalizeRefreshAndRecalc`
        // for the topology and gate posture.
        //
        // **Refresh path: HOST, not cloud.** `refreshWorksheets` →
        // `factset.Spreadsheet.HarmonizedRefreshWorksheets` runs in the
        // in-process FactSet runtime and does NOT consume any cloud
        // refresh quota. Same for `applicationCalculate` — pure Excel
        // recalc, no network. The only quota-capped refresh path on
        // this surface is `factset.RefreshCalculate`, reserved for
        // template instantiation (`openTemplate.ts`) and explicit
        // explicit `refreshCalculate` operation.
        // Entering the foreground-only tail (`HarmonizedRefreshWorksheets`
        // must own Excel focus). Flip the banner phase so a user watching
        // from another workbook sees this run is finishing up, not stalled.
        runHandle.phase = "finalizing";
        const finalize: FinalizeResult = skipFinalVerificationReason
          ? { refreshStatus: "skipped", recalcStatus: "skipped" }
          : await timePhase("finalize_refresh_recalc", () =>
              finalizeRefreshAndRecalc({
                fdsRefreshSheets: runResult.fdsRefreshSheets,
                deferredRefreshAllRequested: runResult.deferredRefreshAllRequested,
                deferredRecalcMode: runResult.deferredRecalcMode,
                pinnedWorkbookName,
                fallbackSheet:
                  Array.from(runResult.fdsRefreshSheets)[0] ?? runResult.sessionLockSheet,
                signal,
                log,
                setLoadingStatus: (status) => updateActiveTab({ loadingStatus: status }),
                contextLabel: "post-plan FactSet refresh",
              })
            );
        const trailingRefreshCompleted =
          finalize.refreshStatus === "refreshed" && finalize.refreshScopeConfirmed === true;
        const trailingRefreshAllCompleted =
          trailingRefreshCompleted && runResult.deferredRefreshAllRequested;
        if (trailingRefreshCompleted) {
          if (trailingRefreshAllCompleted) {
            runResult.fdsRefreshSheets.clear();
            runResult.deferredRefreshAllRequested = false;
          } else {
            const refreshed = new Set(
              (finalize.refreshedSheetNames ?? []).map((name) => name.trim().toLowerCase())
            );
            for (const pendingName of Array.from(runResult.fdsRefreshSheets)) {
              if (refreshed.has(pendingName.trim().toLowerCase())) {
                runResult.fdsRefreshSheets.delete(pendingName);
              }
            }
          }
        }
        const trailingRecalcCompleted = finalize.recalcStatus === "recalculated";
        if (trailingRecalcCompleted) {
          runResult.deferredRecalcMode = null;
        }
        const runPostFinalizeFormulaScan =
          !skipFinalVerificationReason && shouldRunPostFinalizeFormulaScan(runResult);
        const postFinalizeHostBusy =
          finalize.refreshStatus === "deferred-host-busy" ||
          finalize.recalcStatus === "deferred-host-busy";
        const postFinalizeScan =
          runPostFinalizeFormulaScan && !postFinalizeHostBusy
            ? await scanPostFinalizeHardErrors({
                targets: collectPostFinalizeScanTargets(runResult),
                pinnedWorkbookName,
                signal,
                log,
              })
            : { errors: [], truncatedTargets: [], failedTargets: [] };
        const postFinalizeWarningReasons: string[] = [];
        if (postFinalizeHostBusy) {
          postFinalizeWarningReasons.push(
            "Post-refresh formula verification was skipped because Excel remained busy."
          );
        } else {
          if (finalize.refreshStatus === "failed") {
            postFinalizeWarningReasons.push(
              "FactSet formula refresh failed; delivered values may be stale."
            );
          }
          if (finalize.recalcStatus === "failed") {
            postFinalizeWarningReasons.push(
              "Workbook recalculation failed; delivered formula values may be stale."
            );
          }
        }
        if (postFinalizeScan.errors.length > 0) {
          const hardErrorSummary = summarizeHardErrors(postFinalizeScan.errors);
          postFinalizeWarningReasons.push(
            `Post-refresh formula errors remain: ${hardErrorSummary}`
          );
        }
        if (postFinalizeScan.truncatedTargets.length > 0) {
          const truncatedSummary = summarizePostFinalizeScanTargets(
            postFinalizeScan.truncatedTargets
          );
          postFinalizeWarningReasons.push(
            `Post-refresh verification could not fully scan large formula range${postFinalizeScan.truncatedTargets.length === 1 ? "" : "s"}: ${truncatedSummary}`
          );
        }
        if (postFinalizeScan.failedTargets.length > 0) {
          const failedSummary = summarizePostFinalizeScanTargets(postFinalizeScan.failedTargets);
          postFinalizeWarningReasons.push(
            `Post-refresh verification could not scan formula range${postFinalizeScan.failedTargets.length === 1 ? "" : "s"}: ${failedSummary}`
          );
        }
        // Surface the reasons discovered so far in the debug log. The step
        // status/result promotion is applied once, after the autofit pass below,
        // so the (possibly larger) reason set is written into the step outcome a
        // single time — see `replaceStepResultWithPostFinalizeWarning`.
        for (const reason of postFinalizeWarningReasons) {
          log(`⚠️ ${reason}`);
        }

        if (postFinalizeWarningReasons.length > 0) {
          const lastStepIndex = runResult.plan.steps.length - 1;
          if (lastStepIndex >= 0) {
            const step = runResult.plan.steps[lastStepIndex];
            const label = step.step || lastStepIndex + 1;
            const currentStatus = runResult.stepStatuses[lastStepIndex];
            if (currentStatus !== "failed" && currentStatus !== "paused") {
              runResult.stepStatuses[lastStepIndex] = "warning";
              runResult.stepDisplayResults[lastStepIndex] =
                `${step.description} (warning: ${postFinalizeWarningReasons.join("; ")})`;
              replaceStepResultWithPostFinalizeWarning(
                runResult.stepResults,
                label,
                postFinalizeWarningReasons
              );
            }
          }
        }
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");

        // Run had at least one step that couldn't be completed — the title
        // prefix flips to `⚠️` so the user isn't shown a green checkmark on
        // a partially-failed run. Successful runs keep the `✅`.
        const needsAttention =
          runResult.mode === "partial" ||
          runResult.mode === "failed" ||
          postFinalizeWarningReasons.length > 0 ||
          runResult.stepResults.some((r) => {
            const normalizedResult = r.toLowerCase();
            return (
              normalizedResult.includes("needs attention") ||
              normalizedResult.includes("unresolved gap")
            );
          }) ||
          runResult.stepStatuses.some((s) => s === "failed" || s === "warning" || s === "paused");
        const titlePrefix = needsAttention ? "⚠️" : "✅";
        const completedStepCount = runResult.stepStatuses.filter(
          (status) => status === "success" || status === "recovered" || status === "warning"
        ).length;
        const hasDisplayPlan = runResult.plan.steps.length > 0;
        const deterministicSummary =
          !hasDisplayPlan
            ? ""
            : runResult.plan.steps.length > 1 || needsAttention
            ? `${titlePrefix} ${runResult.plan.summary}\n\n${
                needsAttention
                  ? `Completed ${completedStepCount}/${runResult.plan.steps.length} step${runResult.plan.steps.length === 1 ? "" : "s"}`
                  : `Completed ${runResult.plan.steps.length} step${runResult.plan.steps.length === 1 ? "" : "s"}`
              }:\n${runResult.stepResults.join("\n")}`
            : runResult.lastModifications?.message || "Done!";
        let finalMessage =
          !deterministicSummary
            ? runResult.reply || runResult.lastModifications?.message || "Done!"
            : runResult.reply && runResult.reply.trim() !== deterministicSummary.trim()
            ? `${runResult.reply}\n\n${deterministicSummary}`
            : deterministicSummary;
        let executionSummary = runResult.stepResults.join("\n");

        if (runResult.editedCells.length > 0) {
          const uniqueCells = Array.from(new Set(runResult.editedCells));
          const cellsSummary =
            uniqueCells.length <= 5
              ? uniqueCells.join(", ")
              : `${uniqueCells.slice(0, 5).join(", ")} +${uniqueCells.length - 5} more`;
          finalMessage += `\n\n**Edited cells:** ${cellsSummary}`;
          if (runResult.reply) {
            executionSummary += `\n\nEdited cells: ${cellsSummary}`;
          }
        }

        const finalizeFooter = buildFinalizeFooter(finalize);
        finalMessage += finalizeFooter;
        if (runResult.reply) {
          const trimmedFinalizeFooter = finalizeFooter.trim();
          if (trimmedFinalizeFooter) {
            executionSummary += `\n\n${trimmedFinalizeFooter}`;
          }
        }
        const assistantMessage: ExcelChatMessage = {
          id: generateId(),
          role: "assistant",
          content: finalMessage,
          timestamp: Date.now(),
          modifications: runResult.lastModifications || undefined,
          plan: hasDisplayPlan ? runResult.plan : undefined,
          stepResults: hasDisplayPlan ? runResult.stepDisplayResults : undefined,
          stepStatuses: hasDisplayPlan ? runResult.stepStatuses : undefined,
          thinking:
            runResult.reply && !completedAgentTimeline?.hops.length ? executionSummary : undefined,
          agentTimeline: completedAgentTimeline,
          retrievedSources: runResult.retrievedSources,
          retrievedCitations: runResult.retrievedCitations,
          retrievedCitedValuesByUuid: runResult.retrievedCitedValuesByUuid,
        };

        setTabsState((prev) => ({
          ...prev,
          // Append to the run's originating tab, not whatever tab the user is
          // currently viewing. Matches the run-scoped streaming updaters above
          // so the final summary lands on the same tab as its reasoning trace.
          tabs: prev.tabs.map((t) =>
            t.id === runTabId
              ? {
                  ...t,
                  isLoading: false,
                  pendingOperation: undefined,
                  agentTimeline: undefined,
                  retrievedSources: undefined,
                  retrievedCitations: undefined,
                  retrievedCitedValuesByUuid: undefined,
                  session: {
                    ...t.session,
                    messages: [...t.session.messages, assistantMessage],
                    updatedAt: Date.now(),
                  },
                }
              : t
          ),
        }));

        setResult({
          success: !needsAttention,
          message: !needsAttention
            ? "All steps completed"
            : "Completed with items needing attention",
        });
        trackExcelQueryOutcome({
          status: !needsAttention ? "completed" : "failed",
          errorType: needsAttention ? "needs_attention" : undefined,
          workbookEditAttempted: runResult.workbookEditAttempted,
          workbookEditSuccess: !needsAttention,
          operationCount: runResult.stepResults.length,
          affectedSheetCount: Math.max(
            runResult.autofitSheetNames?.size ?? 0,
            runResult.fdsRefreshSheets.size,
            runResult.lastModifications?.worksheets?.length ?? 0
          ),
        });
      } catch (error) {
        // Aborts reach here in two shapes: the native `DOMException` from a
        // pre-headers `fetch` cancellation, and a plain `Error` named
        // "AbortError" synthesized when the response stream is torn down
        // mid-read. Both are user cancellations, so classify on the broad
        // helper plus the run's own signal rather than the `DOMException`
        // shape alone.
        const isAbort = signal.aborted || isAbortError(error);
        // A replacement run, or any newer run that already owns this workbook,
        // keeps its thinking transcript. Stop still owns the slot, so it still
        // writes "Run cancelled".
        const suppressTerminalUi =
          isAbort &&
          (skillReplacementAbortsRef.current.has(abortController) ||
            inFlightRunsRef.current.get(runWorkbookKey) !== runHandle);
        const isWorkbookClosed = error instanceof WorkbookUnavailableError;
        const isSkillsBlocked = error instanceof ExcelSkillsBlockedError;
        const rawErr = error instanceof Error ? error.message : String(error);
        log(
          isAbort
            ? `🛑 Cancelled by user`
            : isWorkbookClosed
              ? `🛑 Stopped — workbook "${(error as WorkbookUnavailableError).workbookName}" is no longer open.`
              : isSkillsBlocked
                ? `⚠️ ${rawErr}`
                : `❌ Error: ${rawErr}`
        );

        // Build a user-visible message that preserves any partial progress
        // (completed plan + step results) so a mid-run abort doesn't wipe
        // the chat. The UI renders `plan` + `stepResults` as a per-step
        // checklist, so threading them through here keeps the visual story
        // intact: completed steps stay marked done and the failing step shows
        // the abort/error reason.
        //
        // `WorkbookUnavailableError` gets a friendly dedicated headline
        // (instead of the raw "WorkbookUnavailableError: …" string) so
        // the user knows exactly why the plan stopped — the workbook
        // they were running it on is no longer open.
        const headline = isAbort
          ? `🛑 Run cancelled`
          : isWorkbookClosed
            ? `⚠️ Stopped — the workbook this plan was running on ("${(error as WorkbookUnavailableError).workbookName}") is no longer open. Reopen it and send a new request.`
            : isSkillsBlocked
              ? `⚠️ ${rawErr}`
              : `❌ Error: ${rawErr}`;

        let content = headline;
        if (partialPlan && partialStepResults.length > 0) {
          const failedAt = partialStepResults.length;
          const total = partialPlan.steps.length;
          content =
            `${headline}\n\n` +
            `Stopped after ${failedAt}/${total} step${total === 1 ? "" : "s"}. ` +
            `Completed workbook changes have been preserved.\n\n` +
            partialStepResults.join("\n");
        }

        const errorMessage: ExcelChatMessage = {
          id: generateId(),
          role: "assistant",
          content,
          timestamp: Date.now(),
          plan: partialPlan ?? undefined,
          stepResults: partialStepDisplayResults.length > 0 ? partialStepDisplayResults : undefined,
          stepStatuses: partialStepStatuses.length > 0 ? partialStepStatuses : undefined,
          agentTimeline: capExcelAgentTimeline(
            finalizeFailedExcelAgentTimeline(latestAgentTimeline),
          ),
        };

        if (!suppressTerminalUi) {
          setTabsState((prev) => ({
            ...prev,
            // Append to the run's originating tab (see success path above) so a
            // mid-run error/abort lands its partial summary on the right tab.
            tabs: prev.tabs.map((t) =>
              t.id === runTabId
                ? {
                    ...t,
                    isLoading: false,
                    pendingOperation: undefined,
                    agentTimeline: undefined,
                    retrievedSources: undefined,
                    retrievedCitations: undefined,
                    retrievedCitedValuesByUuid: undefined,
                    session: { ...t.session, messages: [...t.session.messages, errorMessage] },
                  }
                : t
            ),
          }));

          setResult({ success: false, message: isAbort ? "Cancelled" : rawErr });
        }
        trackExcelQueryFailure(
          isAbort
            ? "cancelled"
            : isWorkbookClosed
              ? "workbook_unavailable"
              : isSkillsBlocked
                ? "skills_blocked"
                : "query_error",
          {
            workbookEditAttempted,
            workbookEditSuccess: workbookEditAttempted ? false : undefined,
            operationCount: workbookEditAttempted
              ? Math.max(partialStepResults.length, 1)
              : undefined,
            affectedSheetCount: workbookEditAttempted ? 0 : undefined,
          }
        );
      } finally {
        setUserQuestionsByWorkbook((current) =>
          Object.fromEntries(
            Object.entries(current).filter(
              ([, pending]) => pending.owner !== userQuestionOwner,
            ),
          ),
        );
        // End-to-end wall-clock for the success-criteria bar. Aborts/errors are
        // intentionally included so the p95 reflects what users actually
        // experience.
        const runElapsedMs = Date.now() - runStartedAt;
        recordPhase("run:total", runElapsedMs, {
          aborted: signal.aborted,
        });
        // The run either consumed the resolution or ended without needing it,
        // so the dedupe entry has served its purpose.
        skillResolveSingleFlightRef.current.release(skillSubmissionId);
        // The one place the operation's Skill binding is settled. Native
        // clarification keeps it; every terminal outcome releases it.
        if (operationRemainsResumable) {
          // A record that is already gone means the account changed under this
          // run. Its transcript writes were suppressed, so log the mismatch.
          if (!markSkillContinuationRetained(skillContinuationId, true)) {
            log("⚠️ Skill operation was cleared before its clarification could be retained.");
          }
        } else {
          releaseSkillContinuation(skillContinuationId);
        }
        // Clear the focused-workbook back-compat ref only if it's
        // still ours — a newer runQuery() call (on the focused
        // workbook) may have already replaced it.
        if (queryAbortRef.current === abortController) {
          queryAbortRef.current = null;
        }
        // Deregister from the per-workbook runs map — but only if
        // our handle is still the live one. A newer runQuery() on
        // the same workbook would already have evicted+replaced us,
        // and we must NOT remove the newer entry.
        if (inFlightRunsRef.current.get(runWorkbookKey) === runHandle) {
          inFlightRunsRef.current.delete(runWorkbookKey);
        }
      }

      setCurrentIteration(0);
      setCurrentPlan(null);
    },
    [
      ensureTemplateCatalog,
      log,
      // `updateActiveTab` is shadowed at the top of this callback (routes
      // via `updateTab(runWorkbookKey, …)`); the component-scope wrapper
      // targets the focused workbook and must not be a dependency here.
      updateTab,
      setTabsStateFor,
      materializeTabsStateForRun,
      currentWorkbookKey,
      resolveWorkbookKey,
      readWorkbookKey,
      availableDataSources,
      authState.features?.excelTabularQuery,
      visibleWorkbookKey,
      activeBrandId,
      brandStyle,
    ]
  );

  /**
   * Offers a query to the run pipeline and resolves as soon as the request is
   * either owned or refused — not when the answer arrives. Callers that reset
   * an affordance on submit (the composer's Skill chips, a Continue button)
   * need that answer within the interaction, and the run behind an accepted
   * request can take minutes.
   *
   * `completed` is there for callers that really do want the whole run, which
   * in practice means tests.
   */
  const submitQuery = useCallback(
    (query: string, options: HandleQueryOptions = {}): Promise<ExcelSubmissionOutcome> => {
      const { handoff, result, settle } = createExcelSubmissionHandoff();
      const completed = runQuery(query, options, handoff)
        .catch((error: unknown) => {
          log(`❌ Query run failed: ${error instanceof Error ? error.message : String(error)}`);
        })
        .finally(() => {
          // A throw before acceptance would otherwise strand the caller on a
          // promise the run can no longer settle.
          settle();
        });
      return result.then((outcome) => ({ ...outcome, completed }));
    },
    [runQuery, log]
  );

  const handleReplaceSkillRun = useCallback(
    async (input: {
      prompt: string;
      selectedSkills: readonly SelectedSkillReference[];
    }): Promise<boolean> => {
      return replaceSkillRun({
        stop: () => {
          const focused = inFlightRunsRef.current.get(visibleWorkbookKey);
          if (!focused) return;
          skillReplacementAbortsRef.current.add(focused.abortController);
          focused.abortController.abort();
        },
        removeLastUserMessage: () => {
          setTabsStateFor(visibleWorkbookKey, (state) => {
            const activeId = state.activeTabId;
            return {
              ...state,
              tabs: state.tabs.map((tab) => {
                if (tab.id !== activeId) return tab;
                const messages = messagesBeforeLastUser(tab.session.messages);
                if (!messages) {
                  return { ...tab, isLoading: false, loadingStatus: "" };
                }
                return {
                  ...tab,
                  isLoading: false,
                  loadingStatus: "",
                  session: {
                    ...tab.session,
                    messages,
                    updatedAt: Date.now(),
                  },
                };
              }),
            };
          });
        },
        resubmit: async () => {
          const outcome = await submitQuery(input.prompt, {
            skills: {
              selectedSkills: [...input.selectedSkills],
              selectionProvided: true,
            },
          });
          return outcome.accepted;
        },
      });
    },
    [setTabsStateFor, submitQuery, visibleWorkbookKey]
  );

  const handleMessageAction = useCallback(
    async (action: ExcelChatMessageAction) => {
      if (!action.retryPayloads || action.retryPayloads.length === 0) {
        if (action.reply) {
          await submitQuery(action.reply);
        }
        return;
      }

      const retryPayloads = action.retryPayloads;
      const retryWorkbookKey = visibleWorkbookKey;
      inFlightRunsRef.current.get(retryWorkbookKey)?.abortController.abort();
      const abortController = new AbortController();
      const runHandle: RunHandle = {
        abortController,
        runWorkbookKey: retryWorkbookKey,
        pinnedWorkbookName: retryPayloads.find((payload) => payload.workbookName)?.workbookName,
      };
      inFlightRunsRef.current.set(retryWorkbookKey, runHandle);
      queryAbortRef.current = abortController;
      const signal = abortController.signal;
      // Shadow the focused-workbook setters with retry-pinned
      // versions so a workbook switch during the retry doesn't
      // bleed loading state / step counter / completion toast /
      // assistant messages onto the newly-visible workbook. Same
      // pattern as the shadows inside `runQuery`, including its
      // account gate: a retry that outlives the account it started
      // under must not append its results to the next tenant's chat.
      const retryAccountGeneration = accountGenerationRef.current;
      const ownsAccount = () => accountGenerationRef.current === retryAccountGeneration;
      const updateActiveTab = (updates: Partial<ExcelTab>) => {
        if (!ownsAccount()) return;
        updateTab(retryWorkbookKey, updates);
      };
      const setTabsState = (mutator: (prev: ExcelTabsState) => ExcelTabsState) => {
        if (!ownsAccount()) return;
        setTabsStateFor(retryWorkbookKey, mutator);
      };
      const setCurrentIteration = (next: number) => updateActiveTab({ currentIteration: next });
      const setCurrentPlan = (next: ExecutionPlan | null) => updateActiveTab({ currentPlan: next });
      const setResult = (next: { success: boolean; message: string } | null) =>
        updateActiveTab({ result: next });
      updateActiveTab({
        isLoading: true,
        loadingStatus: `Retrying ${retryPayloads.length} skipped step${retryPayloads.length === 1 ? "" : "s"}...`,
      });
      setResult(null);
      setCurrentIteration(0);
      setCurrentPlan(null);

      const editedCells: string[] = [];
      const fdsRefreshSheets = new Set<string>();
      // Run-level aggregators for deferred workbook ops (mirrors
      // the main `runQuery` orchestrator). Threaded through
      // `mergeDeferredWorkbookOps` after each apply result lands.
      let deferredRefreshAllRequested = false;
      let deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null = null;
      const mergeDeferredWorkbookOps = (applyResult: {
        deferredRefreshAll?: boolean;
        deferredRecalcMode?: "recalculate" | "full" | "fullRebuild";
      }): void => {
        if (applyResult.deferredRefreshAll === true) {
          deferredRefreshAllRequested = true;
        }
        if (applyResult.deferredRecalcMode) {
          deferredRecalcMode = applyResult.deferredRecalcMode;
        }
      };
      const retryLines: string[] = [];
      const failedPayloads: ExcelScopedRetryPayload[] = [];
      // Mirror of `retryPayloads` minus each payload the moment its
      // apply lands on the host. Lets the catch-path Retry button
      // offer ONLY the unapplied work — without this, an abort or
      // host throw mid-loop would push the user toward re-applying
      // every previously-successful modification and duplicating
      // those writes in the workbook. On the normal success path
      // this ends up identical to `failedPayloads`, so we keep both
      // for clarity (success path reads `failedPayloads`, catch
      // path reads `remainingPayloads`).
      const remainingPayloads: ExcelScopedRetryPayload[] = retryPayloads.slice();
      let lastSuccessfulMod: WorkbookModification | undefined;

      try {
        for (let i = 0; i < retryPayloads.length; i++) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          const payload = retryPayloads[i];
          const mod = payload.modifications;
          log(
            `🔁 Scoped retry ${i + 1}/${retryPayloads.length}: ${mod.message || "workbook modification"}`
          );
          await reactivateSheetBeforeApply(
            mod,
            nonEmptySheetName(payload.targetSheet),
            `scoped retry ${i + 1}`,
            payload.workbookName
          );
          const result = await applyWorkbookModifications(
            mod,
            (op) => {
              log(`   ${op}`);
            },
            {
              signal,
              pinnedWorkbookName: payload.workbookName,
              ...hostReadyWaitCallbacks((loadingStatus) => updateActiveTab({ loadingStatus })),
            }
          );
          if (result.success) {
            // Only mark "done" AFTER the host write actually lands.
            // An apply that throws above never reaches here, so its
            // payload stays in `remainingPayloads` for the catch
            // path's retry button.
            const remainingIdx = remainingPayloads.indexOf(payload);
            if (remainingIdx !== -1) remainingPayloads.splice(remainingIdx, 1);
            lastSuccessfulMod = mod;
            if (result.editedCells) editedCells.push(...result.editedCells);
            for (const s of collectFdsBoundFormulaSheets(mod)) fdsRefreshSheets.add(s);
            mergeDeferredWorkbookOps(result);
            retryLines.push(`${i + 1}. ${result.message || mod.message || "Retry applied"}`);
          } else {
            // Apply returned `success: false` without throwing —
            // payload stays in `remainingPayloads` (we never spliced
            // it out) AND in `failedPayloads` for the success-path
            // Retry button.
            failedPayloads.push(payload);
            retryLines.push(`${i + 1}. ⚠️ needs attention: ${result.message || "Retry failed"}`);
          }
        }

        const refreshWorkbookName = retryPayloads.find(
          (payload) => payload.workbookName
        )?.workbookName;
        const refreshFallbackSheet =
          Array.from(fdsRefreshSheets)[0] ??
          retryPayloads.find((payload) => payload.targetSheet)?.targetSheet ??
          "";
        const finalize = await finalizeRefreshAndRecalc({
          fdsRefreshSheets,
          deferredRefreshAllRequested,
          deferredRecalcMode,
          pinnedWorkbookName: refreshWorkbookName,
          fallbackSheet: refreshFallbackSheet,
          signal,
          log,
          setLoadingStatus: (status) => updateActiveTab({ loadingStatus: status }),
          contextLabel: "scoped retry FactSet refresh",
        });
        const refreshFooter = buildFinalizeFooter(finalize);

        const uniqueCells = Array.from(new Set(editedCells));
        const cellsFooter =
          uniqueCells.length === 0
            ? ""
            : `\n\n**Edited cells:** ${
                uniqueCells.length <= 5
                  ? uniqueCells.join(", ")
                  : `${uniqueCells.slice(0, 5).join(", ")} +${uniqueCells.length - 5} more`
              }`;
        const allSucceeded = failedPayloads.length === 0;
        const content =
          `${allSucceeded ? "✅" : "⚠️"} Retried skipped step${retryPayloads.length === 1 ? "" : "s"}.\n\n` +
          retryLines.join("\n") +
          cellsFooter +
          refreshFooter;

        const assistantMessage: ExcelChatMessage = {
          id: generateId(),
          role: "assistant",
          content,
          timestamp: Date.now(),
          modifications: lastSuccessfulMod,
          actions:
            failedPayloads.length > 0
              ? [
                  {
                    id: "retry-scoped-modifications",
                    label: `Retry skipped step${failedPayloads.length === 1 ? "" : "s"}`,
                    retryPayloads: failedPayloads,
                    variant: "default",
                  },
                ]
              : undefined,
        };

        setTabsState((prev) => ({
          ...prev,
          tabs: prev.tabs.map((t) =>
            t.id === prev.activeTabId
              ? {
                  ...t,
                  isLoading: false,
                  session: {
                    ...t.session,
                    messages: [...t.session.messages, assistantMessage],
                    updatedAt: Date.now(),
                  },
                }
              : t
          ),
        }));
        setResult({
          success: allSucceeded,
          message: allSucceeded
            ? "Skipped steps retried"
            : "Some skipped steps still could not be applied",
        });
      } catch (error) {
        const isAbort = signal.aborted || isAbortError(error);
        const msg = isAbort ? "Cancelled" : error instanceof Error ? error.message : String(error);
        log(`❌ Scoped retry failed: ${msg}`);
        // CRITICAL: do NOT hand the original `retryPayloads` back as
        // the Retry button's payload. If we got here after applying
        // steps `0..k-1` and then throwing at step `k`, the
        // already-applied steps must NOT be re-applied (duplicate
        // writes in the workbook). `remainingPayloads` is what's
        // actually outstanding — the loop splices each payload out
        // only AFTER its host write reports success. If everything
        // was already applied before the throw (e.g. abort fired
        // after the loop body completed but before the success
        // branch finished), the button is suppressed entirely.
        const assistantMessage: ExcelChatMessage = {
          id: generateId(),
          role: "assistant",
          content: isAbort ? "🛑 Retry cancelled" : `❌ Retry failed: ${msg}`,
          timestamp: Date.now(),
          actions:
            remainingPayloads.length > 0
              ? [
                  {
                    id: "retry-scoped-modifications",
                    label: `Retry skipped step${remainingPayloads.length === 1 ? "" : "s"}`,
                    retryPayloads: remainingPayloads,
                    variant: "default",
                  },
                ]
              : undefined,
        };
        setTabsState((prev) => ({
          ...prev,
          tabs: prev.tabs.map((t) =>
            t.id === prev.activeTabId
              ? {
                  ...t,
                  isLoading: false,
                  session: {
                    ...t.session,
                    messages: [...t.session.messages, assistantMessage],
                    updatedAt: Date.now(),
                  },
                }
              : t
          ),
        }));
        setResult({ success: false, message: msg });
      } finally {
        if (queryAbortRef.current === abortController) {
          queryAbortRef.current = null;
        }
        if (inFlightRunsRef.current.get(retryWorkbookKey) === runHandle) {
          inFlightRunsRef.current.delete(retryWorkbookKey);
        }
        updateActiveTab({ isLoading: false, loadingStatus: "" });
        setCurrentIteration(0);
        setCurrentPlan(null);
      }
    },
    [submitQuery, log, setResult, setTabsStateFor, updateTab, visibleWorkbookKey]
  );

  if (!authChecked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background font-sans">
        <Spinner size="sm" />
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return (
      <Login
        onLogin={handleLogin}
        enableStandaloneSsoLogin={shouldAttemptFactSetAutoLogin()}
        headlineTemplate="{brand} AI, right in your workbook"
        subheadline="Chat with your sheets, apply edits, and verify in real time."
      />
    );
  }

  if (shouldShowOnboarding) {
    return <ExcelOnboarding onComplete={handleCompleteOnboarding} />;
  }

  return (
    // `h-screen` (not `min-h-screen`) is load-bearing: with `min-h-*`
    // the outer div is allowed to grow past 100vh once the chat
    // history is long enough, which makes the document body scroll
    // and drags the sticky header off-screen with it. Locking the
    // outer to exactly 100vh forces the inner `overflow-y-auto`
    // scroll container below to absorb all chat-history overflow,
    // so the header (a sibling of that scroll container) really
    // does stay glued to the top regardless of how many messages
    // accumulate.
    <div className="h-screen flex flex-col overflow-hidden bg-background font-sans">
      <SupportFeedbackDialog
        open={supportDialogOpen}
        onOpenChange={setSupportDialogOpen}
        userEmail={authState.user?.email}
        appEnv={authState.features?.auth?.appEnv}
        appName="Excel Add-in"
        sessionId={activeTab?.session.id}
        lastQuery={lastUserMessage?.content}
        lastResponse={lastAssistantMessage?.content}
      />

      <ExcelHeader
        workbookName={workbookDisplayNameFromKey(currentWorkbookKey)}
        onOpenSupport={() => setSupportDialogOpen(true)}
        view={chatView}
        onViewChange={handleChatViewChange}
        showViewToggle={showChatCitationsToggle}
      />

      {/* Content */}
      <main className="flex min-h-0 flex-1 flex-col px-4 pt-2 pb-4 overflow-hidden">
        {citationPopupError && (
          <div
            role="alert"
            className="mb-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
          >
            <span>{citationPopupError.message}</span>
            {citationPopupError.directHref && (
              <>
                {" "}
                <a
                  href={citationPopupError.directHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline underline-offset-2"
                >
                  Open source directly
                </a>
              </>
            )}
          </div>
        )}
        {/*
          The scroll container itself owns the vertical "breathing"
          rhythm:
            - `pt-2` keeps the first message from kissing the
              underside of the sticky header.
            - `pb-3` puts a soft floor under the last bubble so a
              long answer doesn't slam into the composer.
            - `pr-1` reserves space for the scrollbar gutter so
              bubbles don't visually shift when the scrollbar
              appears.
        */}
        <div
          ref={chatScrollRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-2 pb-3 pr-1"
        >
          {chatView === "citations" && showChatCitationsToggle && activeTab ? (
            <ExcelCitationsView
              sources={activeCitationData.sources}
              citations={activeCitationData.citations}
              citedValuesByUuid={activeCitationData.citedValuesByUuid}
            />
          ) : activeTab && activeTab.session.messages.length === 0 && !activeTab.isLoading ? (
            <ExcelEmptyState
              manageSkillsUrl={manageSkillsUrl}
              {...suggestedSkillsState}
              onSelectSuggestedSkill={(path) =>
                queryComposerRef.current?.selectSuggestedSkill(path)
              }
            />
          ) : (
            <>
              {activeTab?.session.messages.map((message) => (
                <div key={message.id} className="mb-5 flex flex-col gap-2.5">
                  {message.role === "user" ? (
                    <div className="ml-6 self-end rounded-xl border border-primary-muted bg-primary/10 px-3.5 py-3 text-[13px] leading-relaxed text-foreground">
                      {message.content}
                    </div>
                  ) : (
                    <>
                      <ExcelRetrievedSources
                        sources={message.retrievedSources}
                        citations={message.retrievedCitations}
                        citedValuesByUuid={getMessageCitedValues(message)}
                        defaultOpen={false}
                      />
                      <ExcelThoughtProcess
                        timeline={message.agentTimeline}
                        thinking={message.thinking}
                        defaultOpen={false}
                      />
                      <div className="mr-6 self-start rounded-xl border border-border bg-card px-3.5 py-3 text-[13px] leading-relaxed text-foreground shadow-sm">
                        <SimpleMarkdown
                          text={message.content}
                          onCitationClick={(id) =>
                            openInlineCitation(
                              message.retrievedCitations,
                              message.retrievedSources,
                              getMessageCitedValues(message),
                              id
                            )
                          }
                        />
                        {message.actions?.some(
                          (action) => Boolean(action.reply) || Boolean(action.retryPayloads?.length)
                        ) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {message.actions
                              .filter(
                                (action) =>
                                  Boolean(action.reply) || Boolean(action.retryPayloads?.length)
                              )
                              .map((action) => (
                                <Button
                                  key={action.id}
                                  type="button"
                                  size="sm"
                                  variant={action.variant ?? "default"}
                                  className="cursor-pointer! disabled:cursor-not-allowed!"
                                  onClick={() => handleMessageAction(action)}
                                  disabled={activeTab?.isLoading}
                                >
                                  {action.label}
                                </Button>
                              ))}
                          </div>
                        )}
                      </div>
                      {message.content.trim().length > 0 && (
                        <OutputFeedback
                          area="Excel"
                          className="mr-6"
                          target={{
                            outputId: message.id,
                            outputType: "chat_response",
                            surface: "chat",
                            sessionId: activeTab.session.id,
                            queryId: message.id,
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}

              {activeTab?.isLoading && (
                <div className="mb-5 flex flex-col gap-2.5">
                  <ExcelThoughtProcess
                    timeline={activeTab.agentTimeline}
                    streaming
                    live
                    loadingStatus={activeTab.loadingStatus}
                  />
                  {((activeTab.retrievedSources?.length ?? 0) > 0 ||
                    (activeTab.retrievedCitations?.length ?? 0) > 0) && (
                    <ExcelRetrievedSources
                      sources={activeTab.retrievedSources}
                      citations={activeTab.retrievedCitations}
                      citedValuesByUuid={activeTab.retrievedCitedValuesByUuid}
                    />
                  )}
                </div>
              )}
            </>
          )}

          {/* An empty chat has no latest turn to jump to. Without the message
              check the button appeared on a brand-new chat in a short pane,
              where the getting-started copy alone made the container
              scrollable. */}
          {chatView === "chat" && (activeTab?.session.messages.length ?? 0) > 0 && (
            <JumpToLatestButton
              visible={showChatReturnToBottom}
              onClick={() => scrollChatToBottom({ behavior: "smooth" })}
            />
          )}
        </div>

        {backgroundRuns.length > 0 && (
          <div className="px-3 pb-1">
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-900 flex items-center gap-2">
              <Spinner size="sm" />
              <span className="truncate">
                {backgroundRuns.length === 1
                  ? (() => {
                      const run = backgroundRuns[0];
                      const where = run.workbookName ? `"${run.workbookName}"` : "another workbook";
                      const verb =
                        run.phase === "finalizing" ? "is finishing up in" : "is running in";
                      const ambiguityNote = run.ambiguous
                        ? " (⚠️ another open workbook shares this name — edits may target the wrong one)"
                        : "";
                      return `A plan ${verb} ${where}${ambiguityNote}. Progress streams into that workbook's chat — switch back to view it.`;
                    })()
                  : `${backgroundRuns.length} plans are running in other workbooks. Switch to each workbook to view their progress.`}
              </span>
            </div>
          </div>
        )}

        {activeBrand && (
          <ExcelBrandDefaultsToggle
            activeBrand={activeBrand}
            enabled={brandStyle === activeBrand.id}
            onToggle={() =>
              setBrandStyle((current) => (current === activeBrand.id ? null : activeBrand.id))
            }
          />
        )}

        {!accountScopeTransition && userQuestionsByWorkbook[currentWorkbookKey ?? ""] && (
          <ExcelUserQuestionCard
            key={
              `${currentWorkbookKey ?? ""}:` +
              userQuestionsByWorkbook[currentWorkbookKey ?? ""].owner
            }
            question={userQuestionsByWorkbook[currentWorkbookKey ?? ""].question}
            onAnswer={(answer) => {
              const workbookKey = currentWorkbookKey ?? "";
              const pending = userQuestionsByWorkbook[workbookKey];
              if (!pending) return;
              setUserQuestionsByWorkbook((current) => {
                if (current[workbookKey]?.owner !== pending.owner) return current;
                const next = { ...current };
                delete next[workbookKey];
                return next;
              });
              pending.resolve(answer);
            }}
          />
        )}

        {clientUpdatePending && (
          <div className="px-3 pb-1" role="status">
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-900">
              Finster will update as soon as the current Excel work is safe to pause.
            </div>
          </div>
        )}

        <ExcelQueryComposer
          ref={queryComposerRef}
          isLoading={accountScopeTransition || activeTab?.isLoading || false}
          onSubmit={(query, skills, userFileIds) =>
            submitQuery(query, { skills, userFileIds }).then(
              (outcome) => outcome.accepted,
              (error: unknown) => {
                log(
                  `⚠️ Query submission failed: ${error instanceof Error ? error.message : String(error)}`
                );
                // The request never started, so the prompt's Skill selection
                // has to survive for the retry.
                return false;
              }
            )
          }
          onCancel={handleCancelQuery}
          matchingSkillBanner={matchingSkillBanner}
          onReplaceSkillRun={handleReplaceSkillRun}
          selectedDataSources={selectedDataSources}
          onDataSourcesChange={handleDataSourcesChange}
          selectedUserFileIds={activeTab?.session.selectedUserFileIds ?? []}
          onUserFileIdsChange={handleUserFileIdsChange}
          manageSkillsUrl={manageSkillsUrl}
          skillsResetKey={skillsResetKey}
          onSuggestedSkillsStateChange={setSuggestedSkillsState}
          featureFlags={authState.features?.featureFlags}
          featureFlagsLoading={!authChecked}
        />

        {isLocalEnv && (
          <ExcelConsolePanel
            logs={logs}
            isOpen={showLogs}
            onToggle={() => setShowLogs(!showLogs)}
          />
        )}
      </main>
    </div>
  );
};

function createInitialTabsState(): ExcelTabsState {
  const initialTab = createNewTab("Sheet1");
  return {
    tabs: [initialTab],
    activeTabId: initialTab.id,
  };
}

function createNewTab(sheetName: string): ExcelTab {
  const id = generateId();
  return {
    id,
    session: {
      id: createChatSessionId(),
      name: `Chat ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      messages: [],
      selectedUserFileIds: [],
      sheetName,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    },
    isLoading: false,
    loadingStatus: "",
    currentIteration: 0,
    currentPlan: null,
    result: null,
  };
}

export default ExcelApp;
